// §WS14B — platform i18n. A dependency-free locale layer: detect the system
// locale once at boot, set <html lang dir> (RTL-aware), and resolve UI strings
// through t(). English is the source of truth AND the fallback, so an
// untranslated key shows clean English — never a broken/missing string. Hebrew
// (he, RTL) is a first-class launch locale; the catalog grows over time.
//
// Detection precedence: ?lang= override → saved choice → en (default).
// §launch — Pomela defaults to English; OS language is NOT auto-inferred.

export type Locale = 'en' | 'he';
const SUPPORTED: readonly Locale[] = ['en', 'he'];
const RTL = new Set<Locale>(['he']);
const STORAGE_KEY = 'pomela:locale';

// ── Catalog ──────────────────────────────────────────────────────────────────
// Dot-namespaced keys. `en` is complete (the fallback); `he` is natural Hebrew
// for the highest-traffic first-run + chrome surfaces. Add keys as surfaces are
// localized — a missing `he` key transparently falls back to `en`.
const EN: Record<string, string> = {
  // ─── §V2 exact-port platform namespace (spike copy is the source of truth;
  //     tt() falls back to the same strings — these entries make t() canonical
  //     and give every key its HE twin below) ───
  'v2.nav.home': 'Home',
  'v2.nav.connectors': 'Integrations',
  'v2.nav.workspace': 'Workspace',
  'v2.nav.learn': 'Learn',
  'v2.support.topicApp': 'My app isn\'t working',
  'v2.support.topicBilling': 'Billing question',
  'v2.support.topicIdea': 'I have an idea',
  'v2.support.topicOther': 'Something else',
  'v2.learn.step1s': 'Say what you want in a sentence.',
  'v2.learn.step2s': 'Pomela builds real screens + data.',
  'v2.learn.step3s': 'Chat or tap to refine anything.',
  'v2.learn.step4s': 'Install & share in a tap.',
  'v2.set.light': 'Light',
  'v2.set.dark': 'Dark',
  'v2.set.system': 'System',
  'v2.account.dangerSub': 'Sign out of this device, or permanently remove the data Pomela keeps here.',
  'v2.account.errB': 'Something went wrong reaching your billing and usage. Your plan and credits are safe, this is just a display hiccup.',
  'v2.account.errT': 'We couldn\'t load your account',
  'v2.account.installs': 'Installs',
  'v2.account.pro': 'Pro',
  'v2.account.statApps': 'Apps built (lifetime)',
  'v2.account.statJoined': 'Joined {y}',
  'v2.account.usageRow': '{n} AI uses · {c} credits',
  'v2.account.usageSub': '{n} apps · {m} AI uses',
  'v2.account.usageT': 'Per-app usage',
  'v2.account.views': 'Views',
  'v2.app.ariaCopy': 'Copy link',
  'v2.app.ariaShareWa': 'Share on WhatsApp',
  'v2.app.ariaShareX': 'Share on X',
  'v2.app.chWa': 'WhatsApp',
  'v2.app.chX': 'X',
  'v2.app.customDomain': 'Custom domain',
  'v2.app.editApp': 'Edit app',
  'v2.app.errB': 'Something went wrong reaching your app\'s live data. Your customers are unaffected, this is only your private view.',
  'v2.app.errT': 'We couldn\'t load your data',
  'v2.app.exportCode': 'Export code',
  'v2.app.keepBuilding': 'Keep building, one thing at a time',
  'v2.app.linkK': 'LINK',
  'v2.app.liveSub': 'Send it to your first 5 users, that\'s how apps find their people.',
  'v2.app.liveT': 'Your app is live',
  'dlv.stTry': 'Built to try',
  'dlv.title': 'Built to try: not ready yet',
  'dlv.sub': 'This app does not do everything you asked yet, so it is not offered as ready. Try it, and tell the editor what to add.',
  'dlv.unmet': 'not built',
  'dlv.unverified': 'not confirmed',
  'dlv.try': 'Try the app',
  'v2.app.manageLabel': 'Management · private to you',
  'v2.app.openLiveApp': 'Open live app',
  'v2.app.previewT': 'Your app is ready to share',
  'v2.app.privacyBooking': 'Bookings, revenue and approvals',
  'v2.app.privacyGeneric': 'Your records and customer details',
  'v2.app.privacyLead': 'Your customers only ever see the customer side.',
  'v2.app.privacyT': 'This management view lives inside Pomela, never in the app.',
  'v2.app.privacyTail': 'stay private to you here.',
  'v2.app.private': 'Private, link only',
  'v2.app.public': 'Public share link',
  'v2.app.retry': 'Try again',
  'v2.app.viewLive': 'View live',
  'v2.home.attachAria': 'Attach',
  'v2.home.attachTitle': 'Attach a photo or file',
  'v2.home.buildIt': 'Build it',
  'v2.home.buildMode': 'Build mode',
  'v2.home.chipBooking': 'a booking page',
  'v2.home.chipFlash': 'a flashcard deck',
  'v2.home.chipRsvp': 'an event RSVP',
  'v2.home.chipTodo': 'a to-do list',
  'v2.home.chipSurprise': 'surprise me',
  'v2.home.chipWater': 'a water tracker',
  // §Launch-honesty — supported-only spark/mini strings (replace trip/budget/crm/recipe/game).
  'v2.home.sparkClassN': 'Class booking',
  'v2.home.sparkClassTag': 'Share one link — people pick a time and reserve a spot.',
  'v2.home.sparkClassP': 'a class booking page where people pick an available time and reserve a spot',
  'v2.home.miniClassName': 'Class Booking',
  'v2.home.miniClassH': 'Reserve a spot',
  'v2.home.sparkWorkoutN': 'Workout log',
  'v2.home.sparkWorkoutTag': 'Log sets and reps, watch your weekly volume climb.',
  'v2.home.sparkWorkoutP': 'a workout log with exercises, sets and reps, rest timers and a weekly volume chart',
  'v2.home.miniWorkoutName': 'Workout Log',
  'v2.home.miniWorkoutH': 'This week',
  'v2.home.miniWorkoutSets': 'Sets',
  'v2.home.miniWorkoutVol': 'Volume',
  'v2.home.sparkInvN': 'Inventory tracker',
  'v2.home.sparkInvTag': 'Every item, its count, and what is running low.',
  'v2.home.sparkInvP': 'an inventory tracker for my shop with item counts, stock adjustments and low-stock alerts',
  'v2.home.miniInvName': 'Inventory',
  'v2.home.miniInvH': 'Items',
  'v2.home.miniInvI1': 'Oat milk',
  'v2.home.miniInvSub1': '12 in stock',
  'v2.home.miniInvI2': 'Espresso beans',
  'v2.home.miniInvSub2': 'Low · 3 left',
  'v2.home.sparkFlashN': 'Flashcard deck',
  'v2.home.sparkFlashTag': 'Study with spaced repetition, one card at a time.',
  'v2.home.sparkFlashP': 'a flashcard deck with spaced-repetition study, decks by topic and a daily review',
  'v2.home.miniFlashName': 'Flashcards',
  'v2.home.miniFlashH': 'Spanish 101',
  'v2.home.miniFlashCards': 'Cards',
  'v2.home.miniFlashDue': 'Due today',
  'v2.home.sparkTodoN': 'To-do list',
  'v2.home.sparkTodoTag': 'Projects, checklists and due dates that actually stick.',
  'v2.home.sparkTodoP': 'a to-do app with projects, checklists, due dates and quick notes',
  'v2.home.miniTodoName': 'To-dos',
  'v2.home.miniTodoH': 'Today',
  'v2.home.miniTodoDone': 'Done',
  'v2.home.miniTodoOpen': 'To-do',
  'v2.home.continueT': 'Pick up where you left off',
  'v2.home.fAll': 'All',
  'v2.home.fBusiness': 'Business',
  'v2.home.fHobby': 'Hobby',
  'v2.home.fPersonal': 'Personal',
  'v2.home.fSocial': 'Social',
  'v2.home.ghBody': 'No code, no waiting, no app store review. Just describe it.',
  'v2.home.ghCta': 'Start building',
  'v2.home.ghEyebrow': 'Ready when you are',
  'v2.home.ghTitle': 'Your idea is one sentence away.',
  'v2.home.heroW1': 'Type.',
  'v2.home.heroW2': 'Tap.',
  'v2.home.heroW3': 'Install.',
  'v2.home.lede': 'Describe it in a sentence. Install it on your phone, no code, no app store.',
  'v2.home.miniBookH': 'Book a time',
  'v2.home.miniBookName': 'Studio Bookings',
  'v2.home.miniBudgetEnv': 'Envelopes',
  'v2.home.miniBudgetH': 'This month',
  'v2.home.miniBudgetName': 'Budget Jar',
  'v2.home.miniCrmC1': 'Noa Peretz',
  'v2.home.miniCrmC2': 'Dana Cohen',
  'v2.home.miniCrmH': 'Clients',
  'v2.home.miniCrmName': 'Client CRM',
  'v2.home.miniCrmSub1': 'Follow up · Thu',
  'v2.home.miniCrmSub2': 'Birthday · in 3 days',
  'v2.home.miniGameH': 'Who\'s in Friday?',
  'v2.home.miniGameName': 'Game Night',
  'v2.home.miniLangDays': '14 day',
  'v2.home.miniLangH': 'Daily practice',
  'v2.home.miniLangName': 'Language Streak',
  'v2.home.miniLangStreak': 'streak',
  'v2.home.miniRecipeCooked': 'Cooked',
  'v2.home.miniRecipeH': 'Your recipes',
  'v2.home.miniRecipeName': 'Recipe Box',
  'v2.home.miniSaved': 'Saved',
  'v2.home.miniTripCount': '4 going · 1 maybe',
  'v2.home.miniTripCta': 'View plan',
  'v2.home.miniTripH': 'Weekend in Eilat',
  'v2.home.miniTripInv': 'You\'re invited',
  'v2.home.miniTripName': 'Trip Planner',
  'v2.home.miniWaterGlasses': 'glasses today',
  'v2.home.miniWaterH': 'Stay hydrated',
  'v2.home.miniWaterName': 'Water Tracker',
  'v2.home.openWorkspace': 'Open Workspace',
  'v2.home.removeAsset': 'Remove',
  'v2.home.sparkBookN': 'Studio bookings',
  'v2.home.sparkBookP': 'a booking page for my studio with deposits and appointment reminders',
  'v2.home.sparkBookTag': 'Take appointments and deposits, no phone tag.',
  'v2.home.sparkBudgetN': 'Budget jar',
  'v2.home.sparkBudgetP': 'a budget app with spending envelopes instead of a spreadsheet',
  'v2.home.sparkBudgetTag': 'Envelopes for spending, without the spreadsheet.',
  'v2.home.sparkCrmN': 'Client CRM',
  'v2.home.sparkCrmP': 'a simple CRM to keep notes on every client in one list',
  'v2.home.sparkCrmTag': 'Every client, every note, in one list.',
  'v2.home.sparkGameN': 'Game night RSVP',
  'v2.home.sparkGameP': 'a game night RSVP page where I send one link and see who\'s actually coming',
  'v2.home.sparkGameTag': 'Send one link, know who\'s actually coming.',
  'v2.home.sparkLangN': 'Language streak',
  'v2.home.sparkLangP': 'a daily streak tracker for 10 minutes of language practice',
  'v2.home.sparkLangTag': '10 minutes a day, tracked like a habit.',
  'v2.home.sparkRecipeN': 'Recipe box',
  'v2.home.sparkRecipeP': 'a recipe box where I can save and search my own recipes',
  'v2.home.sparkRecipeTag': 'Your recipes, searchable, never lost again.',
  'v2.home.sparkSub': 'Tap any idea to drop it straight into the composer above, edit it, or build it as-is.',
  'v2.home.sparkT': 'Not sure what to build? Start from a spark.',
  'v2.home.sparkTripN': 'Trip planner',
  'v2.home.sparkTripP': 'a trip planner where friends can see the dates, shared docs and who\'s coming',
  'v2.home.sparkTripTag': 'One place for dates, docs and who\'s coming.',
  'v2.home.sparkWaterN': 'Water tracker',
  'v2.home.sparkWaterP': 'a water tracker that gently reminds me to drink more water through the day',
  'v2.home.sparkWaterTag': 'A glass a day, gently reminded.',
  'v2.home.stat1L': 'like a real app',
  'v2.home.stat1N': 'Installs',
  'v2.home.stat2L': 'with Pomy, your AI copilot',
  'v2.home.stat2N': 'Edit anytime',
  'v2.home.stat3L': 'server-backed, yours to keep',
  'v2.home.stat3N': 'Real data',
  'v2.home.stat4L': 'describe it in one sentence',
  'v2.home.stat4N': 'No code',
  'v2.home.surprisePrompt': 'a workout log with exercises, sets and reps, rest timers and a weekly volume chart',
  'v2.home.uploading': 'Uploading…',
  'v2.home.useIdea': 'Use this idea',
  'v2.learn.curatedT': 'Start with these',
  'v2.learn.heroSub': 'Pomela turns one sentence into a complete, installable mobile app: real screens, real interactions, real data. This is the field guide, what to build, how to phrase it, and the tricks that make an app go from good to genuinely yours.',
  'v2.learn.noResB': 'We couldn\'t find a guide for that search. Try a broader word, or ask Pomy, it knows the whole product inside out.',
  'v2.learn.noResT': 'No guides match that yet',
  'v2.nav.askPomy': 'Ask Pomy',
  'v2.nav.collapse': 'Collapse sidebar',
  'v2.nav.support': 'Support',
  // §sign-out — honest failure copy for the shell + account sign-out controls.
  'v2.signout.error': 'Couldn’t sign you out — check your connection and try again.',
  'v2.pomy.aria': 'Ask Pomy — ideas, advice, anything about Pomela',
  'v2.pomy.chip0': 'Give me an app idea for my business',
  'v2.pomy.chip1': 'What can I build with Pomela?',
  'v2.pomy.chip2': 'How do credits work?',
  'v2.pomy.chip3': 'What can I actually build here?',
  'v2.pomy.err': 'I couldn’t reach Pomy just now — try again in a moment.',
  'v2.pomy.hello': 'Hey, I’m Pomy. Ask me for app ideas, how something in Pomela works, or advice on what to build next.',
  'v2.pomy.ph': 'Ask Pomy anything…',
  'v2.pomy.sub': 'Ideas, advice, anything about Pomela',
  'v2.send': 'Send',
  'v2.set.account': 'Account',
  'v2.set.appearance': 'Appearance',
  'v2.set.billing': 'Billing & credits',
  'v2.set.billingProSub': 'Pro plan · manage your credits',
  'v2.set.copiedToast': 'Link copied ✓',
  'v2.set.copy': 'Copy',
  'v2.set.danger': 'Danger zone',
  'v2.set.dangerSub': 'Sign out or delete your data',
  'v2.set.dirLtr': 'English · left-to-right',
  'v2.set.dirRtl': 'עברית · right-to-left',
  'v2.set.inviteSub': 'Pomela gets better as more people build with it. Send your link, your friend lands straight in the builder, ready to make their first app.',
  'v2.set.language': 'Language & layout',
  'v2.set.notifications': 'Notifications',
  'v2.set.prefs': 'Preferences',
  'v2.set.privacy': 'Privacy',
  'v2.set.privacySub': 'Data, tracking and exports',
  'v2.set.sectionsAria': 'Settings sections',
  'v2.set.security': 'Security',
  'v2.set.securitySub': 'How Pomela protects your account and data',
  'v2.set.sub': 'Your account, preferences and system controls, all in one place.',
  'v2.set.systemSection': 'System',
  'v2.set.tapFlip': 'Tap to flip',
  'v2.set.themeToast': 'Appearance set to {mode}',
  'v2.set.title': 'Settings',
  'v2.set.yourLink': 'YOUR LINK',
  'v2.support.badge': 'Average response time: under 24 hours',
  'v2.support.copied': 'Address copied ✓',
  'v2.support.copy': 'Copy',
  'v2.support.emailLabel': 'Your email',
  'v2.support.emailSub': 'Prefer email? Copy our address',
  'v2.support.errBody': 'Something went wrong on our side. Please check your connection and try again.',
  'v2.support.errTitle': 'That didn\'t send',
  'v2.support.eyebrow': 'Concierge',
  'v2.support.foot': 'Built with care in 2026',
  'v2.support.hero1': 'We\'re here.',
  'v2.support.hero2': 'Always.',
  'v2.support.heroSub': 'Real humans behind every reply. The founder reads every message, and most get a thoughtful response within a day.',
  'v2.support.linkIdea': 'Share an idea',
  'v2.support.linkIdeaSub': 'Tell us what Pomela should build next.',
  'v2.support.linkKb': 'Knowledge base',
  'v2.support.linkKbSub': 'Walkthroughs, tips, and prompt patterns.',
  'v2.support.linkPress': 'Press & partnerships',
  'v2.support.linkPressSub': 'We\'d love to hear from journalists and creators.',
  'v2.support.more': 'More from Pomela',
  'v2.support.msgLabel': 'How can we help?',
  'v2.support.msgPh': 'Tell us what\'s going on with your app…',
  'v2.support.send': 'Send message',
  'v2.support.sendAnother': 'Send another',
  'v2.support.sendTitle': 'Send us a message',
  'v2.support.sending': 'Sending…',
  'v2.support.successBody': 'We\'ll reply within a day. Keep an eye on the inbox at the email you gave us.',
  'v2.support.successTitle': 'Message sent',
  'v2.support.tryAgain': 'Try again',
  'v2.try': 'Try',
  'v2.workspace.emptyBody': 'Describe your first app in a sentence and it will live here, ready to edit, publish and share.',
  'v2.workspace.installsTotal': 'Total across your apps',
  'v2.workspace.offlineBody': 'We can\'t reach your synced apps right now. Check your connection and try again.',
  'v2.workspace.retry': 'Try again',
  'v2.workspace.searchPh': 'Search apps…',
  'v2.workspace.statApps': 'Apps in this workspace',
  'v2.workspace.statDrafts': 'Drafts',
  'v2.workspace.sub': 'Everything you\'ve made, in one place.',
  // primary nav / chrome (labels match the live NavRail)
  'nav.home': 'Home',
  'nav.copilot': 'Pomy',
  'nav.workspace': 'Workspace',
  'nav.connectors': 'Integrations',
  'cmd.connectorsHint': 'Browse live data sources you can connect',
  'nav.guide': 'Knowledge Base',
  'nav.settings': 'Settings',
  'nav.account': 'Account',
  'nav.admin': 'Admin',
  'nav.credits': 'credits',
  'nav.signIn': 'Sign in',
  'nav.ownerGodMode': 'Owner · God Mode',
  'nav.search': 'Search',
  'nav.streak': '{n} day streak',
  'nav.streakNone': 'Build today',
  'nav.creditsLeft': 'credits left',
  'common.home': 'Home',
  'common.skip': 'Skip',
  'common.back': 'Back',
  'common.goBack': 'Go back',
  'common.openMenu': 'Open menu',
  'common.menu': 'Menu',
  'common.next': 'Next',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.retry': 'Retry',
  'common.close': 'Close',
  'common.signIn': 'Sign in',
  // generation home
  'home.promptPlaceholder': 'Describe an app you want to build…',
  'home.build': 'Build it',
  'home.quick': 'Quick',
  'home.deep': 'Deep',
  // auth wall
  'auth.title': 'You’re shipping fast.',
  'auth.sub': 'You’ve used up your free builds. Sign in, it stays free, to keep building and sync your apps across every device.',
  // common states
  'state.loading': 'Loading…',
  'state.offline': 'You’re offline',
  'state.retry': 'Try again',
  'state.error': 'That didn’t go through. Please try again in a moment.',
  // home canvas
  'home.sub': 'Describe an app and install it on your phone. Real, native-feeling apps, no app store needed.',
  'home.ex1': 'a water tracker',
  'home.ex2': 'a booking page',
  'home.ex3': 'a flashcard deck',
  'home.ex4': 'an event RSVP',
  // secondary-screen chrome (TopBar title + eyebrow + intro)
  'sb.workspace.title': 'Workspace',
  'sb.workspace.eyebrow': 'Your apps',
  'sb.workspace.intro': 'Every app you generate collects here. Reopen one to keep editing, duplicate it as the start of something new, or clear it from your workspace.',
  'sb.workspace.empty': 'No apps yet',
  'sb.workspace.emptyText': 'The apps you generate land here automatically. Reopen any one with a tap.',
  'sb.workspace.emptyBtn': 'Build your first app',
  'sb.workspace.search': 'Search apps',
  'sb.workspace.fAll': 'All',
  'sb.workspace.fLive': 'Live',
  'sb.workspace.fPreview': 'Preview',
  'sb.workspace.sortRecent': 'Recent',
  'sb.workspace.activity': 'Recent activity',
  'sb.workspace.installs': 'Installs',
  'sb.help.title': 'Support',
  'sb.help.eyebrow': 'Concierge',
  'sb.guide.title': 'Knowledge base',
  'sb.guide.eyebrow': 'Learn',
  'sb.copilot.title': 'Pomy',
  'sb.copilot.eyebrow': 'Build partner',
  'sb.account.title': 'Account',
  'sb.account.eyebrow': 'Profile',
  'sb.upgrade.title': 'Upgrade',
  'sb.upgrade.eyebrow': 'Pro',
  // cookie / consent banner (first-touch surface)
  'cookie.msg': 'We use essential cookies to run Pomela. With your okay, we’ll also use a few to understand what to build next.',
  'cookie.privacy': 'Privacy Policy',
  'cookie.essential': 'Essential only',
  'cookie.all': 'Accept all',
  // install coach (Add-to-Home-Screen bottom sheet)
  'install.iosTitle': 'Add to Home Screen',
  'install.iosSub': 'Keeps this app fullscreen — no browser bars.',
  'install.iosStep1': 'Tap the Share button below',
  'install.iosStep2': 'Scroll to “Add to Home Screen”',
  'install.iosStep3': 'Tap it',
  'install.iosStep4': 'Open it from your home screen',
  'install.androidCta': 'Install app',
  // invite card (Settings hero + sidebar row)
  'invite.eyebrow': 'Spread the joy',
  'invite.title': 'Invite a friend to build with Pomela.',
  'invite.sub': 'Pomela gets better as more people build with it. Send your link — your friend lands straight in the builder, ready to make their first app.',
  'invite.yourLink': 'Your link',
  'invite.share': 'Share invite',
  'invite.copy': 'Copy link',
  'invite.copied': 'Copied',
  'invite.compactTitle': 'Invite a friend',
  'invite.compactSub': 'Bring a friend into Pomela',
  'invite.counter': 'You’ve shared {n}. Keep it going.',
  'invite.shareText': 'I’ve been building little apps on my phone in about 90 seconds with Pomela. Try it:',
  'invite.shareTitle': 'Build an app with Pomela',
  'invite.compactAria': 'Invite a friend',
  'invite.copyFail': 'Copy failed. Long-press the link instead.',
  // settings sections + rows
  'set.appearance': 'Appearance',
  'set.light': 'Light',
  'set.dark': 'Dark',
  'set.system': 'System',
  'set.notifications': 'Notifications',
  'set.dataPrivacy': 'Data & Privacy',
  'set.export': 'Export my data',
  'set.exportSub': 'Download everything stored on this device as JSON',
  'set.delete': 'Delete my data',
  'set.deleteSub': 'Permanently remove your apps and history',
  'set.about': 'About',
  'set.version': 'Version',
  'set.privacy': 'Privacy policy',
  'set.security': 'Security',
  'set.title': 'Settings',
  'set.saved': 'All changes saved',
  'set.eyebrow': 'Preferences',
  'set.profile': 'Profile',
  'set.signedIn': 'Signed in',
  'set.notSignedIn': 'Not signed in',
  'set.manageAccount': 'Manage your account',
  'set.signInSync': 'Sign in to sync across devices',
  'set.pushLabel': 'Push notifications',
  'set.pushOn': 'On: apps can show notifications while open on this device',
  'set.pushOffMuted': 'Off: muted on this device. Reset fully in site settings.',
  'set.pushDefault': 'Let your apps show notifications on this device',
  'set.pushUnsupported': 'Not supported on this browser',
  'set.pushBlocked': 'Blocked in your browser settings',
  // Copilot (Pomy)
  'cop.greet': 'Hey, I’m Pomy.',
  'cop.intro': 'I’m your build partner. Tell me about the app you’ve been thinking about: the friction, the dream, the moment you wished something existed. We’ll shape it into a prompt together.',
  // Guide / Upgrade / Help heroes
  'guide.heroTitle': 'Type a sentence. Get a real app on your phone.',
  'upg.heroTitle': 'Build without limits',
  'upg.heroSub': 'A much bigger building pool, AI video generation, custom domains, and your code to take anywhere.',
  // Sign-in (magic-link flow)
  'si.welcome': 'Welcome',
  'si.title': 'Sign in to Pomela',
  'si.sub': 'One tap with email, no password to remember. We’ll send a 6-digit code.',
  'si.errEmail': 'That doesn’t look like an email address.',
  'si.continue': 'Continue',
  'si.consentPre': 'I agree to the ',
  'si.agreeAria': 'Agree to the Terms and Privacy Policy',
  // §A11Y-013 — accessible names for the two auth inputs. A placeholder is not a name: it is
  // unreliably announced and disappears the moment the field has content.
  'si.emailAria': 'Email address',
  'si.codeAria': 'Six-digit sign-in code from your email',
  'si.consentMid': ' and ',
  'si.terms': 'Terms',
  'si.privacy': 'Privacy Policy',
  'si.checkEmail': 'Check your email',
  'si.enterCode': 'Enter your code',
  'si.codeSent': 'We sent a 6-digit code to {email}. It expires in 10 minutes.',
  'si.errSend': 'Failed to send sign-in email. Please try again in a moment.',
  'si.errCode': 'Enter the 6-digit code from your email.',
  'si.verify': 'Verify & sign in',
  'si.differentEmail': 'Use a different email',
  'si.errCodeBad': 'That code didn’t work, it may have expired. Send a new one.',
  'si.youreIn': 'You’re in',
  'si.signedIn': 'Signed in',
  'si.doneSub': 'Your apps now sync across every device signed in as {email}.',
  'si.startBuilding': 'Start building',
  'si.oneMoment': 'One moment…',
  // Prompt canvas (home dock)
  'pi.placeholder': 'Describe an app you want to build…',
  'pi.whatBuild': 'What do you want to build?',
  'pi.addToPrompt': 'Add to your prompt',
  'pi.quick': 'Quick',
  'pi.quickHint': '~1 min',
  'pi.deep': 'Deep',
  'pi.deepHint': '~3 min · multi-screen',
  // §launch — Deep build is disabled for launch (Quick and Deep share the same
  // engine; Deep is not ready as its own tier). The tab shows a "Coming soon"
  // state and cannot be selected; every build runs as Quick.
  'pi.deepSoon': 'Coming soon',
  'pi.deepSoonAria': 'Deep build is coming soon — not available yet. Builds run in Quick mode.',
  'pi.generate': 'Generate',
  // Home canvas
  'home.tap1': 'Type. Tap.',
  'home.tap2': 'Install.',
  'home.tapAria': 'Type. Tap. Install.',
  'home.canvasAria': 'Pomela home: describe an app to build',
  'home.dockPlaceholder': 'Describe an app… a habit tracker, a menu, a booking page',
  'help.heroTitle': 'We’re here. Always.',
  'help.heroSub': 'Real humans behind every reply. The founder reads every message, and most get a thoughtful response within a day.',
  'help.sendMsg': 'Send us a message',
  'help.moreFrom': 'More from Pomela',
  // Account
  'acc.signInSync': 'Sign in to sync the apps you build across every device: phone, tablet, laptop.',
  'acc.signIn': 'Sign in',
  'acc.appsBuilt': 'Apps built',
  'acc.devices': 'Devices',
  'acc.joined': 'Joined',
  'acc.memberSince': 'Member since',
  'acc.signOut': 'Sign out',
  'acc.signOutConfirm': 'Sign out of Pomela?',
  'acc.managePlan': 'Manage plan',
  // Upgrade plan features
  'upg.free1': 'Plenty of room to build and tinker',
  'upg.free2': 'The full generation engine, same as Pro',
  'upg.free3': 'Install any app to your home screen',
  'upg.free4': 'Works offline, no account needed',
  'upg.pro1': 'A generous monthly building pool',
  'upg.pro2': 'Custom domains for your apps',
  'upg.pro3': 'One-click code export — a standard Vite project, yours to keep',
  'upg.pro4': 'AI video generation',
  'upg.pro5': 'Everything in Free',
  'acc.signOutBody': 'Your apps stay on this device. You’ll just stop syncing until you sign back in.',
  // Copilot starter prompts (title / desc / prompt)
  'cop.s1t': 'A habit tracker with streaks',
  'cop.s1d': 'Single-habit, streak-driven, one-tap log. Apple Health vibe.',
  'cop.s1p': 'A habit tracker for a single habit with daily streaks and a one-tap log',
  'cop.s2t': 'A flashcard deck for studying',
  'cop.s2d': 'Decks by topic, spaced-repetition review, one card at a time.',
  'cop.s2p': 'A flashcard deck app with decks by topic, spaced-repetition review, and a daily study session',
  'cop.s3t': 'A workout log with sets & reps',
  'cop.s3d': 'Log exercises, track sets and reps, watch weekly volume grow.',
  'cop.s3p': 'A workout log where I record exercises, sets and reps, with rest timers and a weekly volume chart',
  'cop.s4t': 'Help me pick an idea',
  'cop.s4d': 'Not sure yet? Describe how you want it to feel, not what it does.',
  'cop.s4p': 'I want to build a beautiful, small mobile app but I’m not sure what. Help me pick something that fits my taste',
  'cop.s5t': 'I’m stuck on my prompt',
  'cop.s5d': 'Tell me what you’ve got, and I’ll help sharpen the brief.',
  'cop.s5p': 'I’ve been trying to describe my app idea but it’s not coming out right. Can you ask me a few questions to pull the brief out of me?',
  'cop.s6t': 'How do I make it more elegant?',
  'cop.s6d': 'Bring me your generated app and I’ll suggest small upgrades.',
  'cop.s6p': 'I just built an app with Pomela and it works but feels generic. What kinds of small refinements usually make a generated app feel premium?',
  'cop.placeholder': 'Ask for an app idea…',
  // Help support form
  'help.copyEmail': 'Prefer email? Copy our address',
  'help.responseTime': 'Average response time: under 24 hours.',
  'help.topicAppIssue': 'My app isn’t working',
  'help.topicBilling': 'Billing question',
  'help.topicIdea': 'I have an idea',
  'help.topicOther': 'Something else',
  'help.emailLabel': 'Your email',
  'help.messageLabel': 'How can we help?',
  'help.messagePlaceholder': 'Tell us what’s going on…',
  'help.send': 'Send message',
  'help.sending': 'Sending…',
  'help.sent': 'Message sent',
  'help.sendAnother': 'Send another',
  'help.copy': 'Copy',
  'help.copied': 'Copied',
  'help.foot': 'Built with care in 2026',
  'help.errEmail': 'Enter a valid email so we can reply.',
  'help.errMsg': 'Tell us a little more so we can help.',
  'help.errRate': 'You’ve sent a few already. Give it a moment.',
  'help.errGeneric': 'Something went wrong. Please try again.',
  'help.errNetwork': 'Network hiccup. Check your connection and try again.',
  'help.attachLabel': 'Attachments (optional)',
  'help.attachAdd': 'Attach a screenshot or video',
  'help.attachHint': 'Drag & drop or tap to choose — up to 5 files · images ≤10 MB · video ≤50 MB',
  'help.attachRemove': 'Remove attachment',
  'help.attachErrType': 'Attach images (PNG, JPG, GIF, WebP) or video (MP4, MOV, WebM) only.',
  'help.attachErrSize': 'That file’s too large — images up to 10 MB, video up to 50 MB.',
  'help.attachErrCount': 'You can attach up to 5 files.',
  'help.uploading': 'Uploading… {pct}%',
  'upg.recommended': 'RECOMMENDED',
  'upg.proBadge': 'Pomela Pro',
  'upg.perMonth': '/ month',
  'upg.free': 'Free',
  'upg.forever': 'forever',
  'upg.cancelTitle': 'Cancel any time. No traps.',
  'upg.onPro': 'You’re on Pro',
  'upg.starting': 'Starting checkout…',
  'upg.goPro': 'Go Pro',
  'upg.signInPro': 'Sign in to go Pro',
  'upg.yourPlan': 'Your plan',
  'upg.checkoutErr': 'Couldn’t start checkout. Please try again in a moment.',
  // Guide knowledge base — atlas chrome (search, hero, how-it-works, browse, ask)
  'g.searchPh': 'Search the guide — “publish”, “data”, “credits”…',
  'g.askPomy': 'Ask Pomy',
  'g.heroBadge': 'Getting started',
  'g.heroCta': 'Start building',
  'g.howTitle': 'How Pomela works',
  'g.step1t': 'Describe', 'g.step1s': 'Say what you want in a sentence',
  'g.step2t': 'Generate', 'g.step2s': 'Pomela builds real screens + data',
  'g.step3t': 'Edit', 'g.step3s': 'Chat to refine anything',
  'g.step4t': 'Publish', 'g.step4s': 'Install + share in a tap',
  'g.browseTitle': 'Browse the guide',
  'g.countSuffix': 'guides',
  'g.askTitle': 'Still stuck? Ask Pomy',
  'g.askSub': 'Get an instant answer from your AI copilot',
  'g.noResultsT': 'Nothing in the guide matches',
  'g.noResultsS': 'Try another word, or ask Pomy for an instant answer.',
  'g.askResults': 'Ask Pomy instead',
  'g.clearSearch': 'Clear search',
  // Guide knowledge base — hero, TOC, section heads
  'g.heroSub': 'Pomela turns one sentence into a complete, installable mobile app: real screens, real interactions, real data. No code, no app store, no “we’ll get back to you in six weeks.” This is the field guide to getting the most out of it: what to build, how to phrase it, and the tricks that make an app go from good to genuinely yours.',
  'g.toc1': 'What you can build',
  'g.toc2': 'Get the best results',
  'g.toc3': 'Power features',
  'g.toc4': 'Tips & pitfalls',
  'g.s1e': 'The range', 'g.s1t': 'What you can build',
  'g.s1l': 'Pomela is happiest building real, customer-facing mobile apps, the kind a small business or creator actually ships. Here’s where it shines, with prompts you can steal.',
  'g.s2e': 'Prompting', 'g.s2t': 'How to get the best results',
  'g.s2l': 'The trick to a great app is a great sentence. You don’t need to be technical, you need to be specific. Here’s the difference.',
  'g.s3e': 'Go further', 'g.s3t': 'Using Pomela to the max',
  'g.s3l': 'The good stuff most people miss. These are what turn a one-off generation into an app you actually run your life or business on.',
  'g.s4e': 'Hard-won', 'g.s4t': 'Tips, patterns & pitfalls',
  'g.s4l': 'The little things that consistently produce better apps. Steal them all.',
  'g.archFoot': 'One rule of thumb: if it’s a screen-based app a person would tap through on a phone, Pomela can probably build it. The strongest results come from the archetypes above, where the engine is most opinionated and most beautiful.',
  'g.a1t': 'Booking & service apps',
  'g.a1b': 'Salons, clinics, trainers, tutors, studios: anyone whose customers need to book a time. Your clients pick a service and a slot in two taps; you keep 100% of every booking.',
  'g.a1e1': '“A hair salon with services, stylists, and an appointment booking flow.”',
  'g.a1e2': '“A personal-trainer app where clients book 1-on-1 sessions and see their plan.”',
  'g.a1e3': '“A tutoring app with subjects, available slots, and booking confirmation.”',
  'g.a2t': 'Storefronts & shops',
  'g.a2b': 'Sell anything: products, bundles, digital goods. Browse, cart, checkout, done. Connect your own payment processor and the money lands in your account, not ours.',
  'g.a2e1': '“A plant shop with a catalog, plant detail pages, a cart, and checkout.”',
  'g.a2e2': '“A bakery with a menu, custom-order options, and pickup scheduling.”',
  'g.a2e3': '“A boutique storefront with collections, product variants, and a cart.”',
  'g.a3t': 'Trackers',
  'g.a3b': 'Habits, workouts, money, plants, moods, reading. Trackers are mobile-native heaven: they live on the home screen, work offline, and nudge you with reminders.',
  'g.a3e1': '“A habit tracker with daily streaks and a weekly progress chart.”',
  'g.a3e2': '“A workout logger with routines, a live rest timer, and personal records.”',
  'g.a3e3': '“A budget tracker with categories, a spending chart, and monthly limits.”',
  'g.a4t': 'Content & feed apps',
  'g.a4b': 'Recipes, guides, newsletters, portfolios, a class of resources. A feed to browse, a detail view to dive in, a “saved” list to come back to.',
  'g.a4e1': '“A recipe app with categories, recipe detail pages, and a saved list.”',
  'g.a4e2': '“A travel city-guide with neighbourhoods, spots, and a map-style list.”',
  'g.a4e3': '“A reading list with articles, tags, and a clean reader view.”',
  'g.a5t': 'Directories',
  'g.a5b': 'Local spots, a team roster, a resource library, an event line-up. Searchable, filterable, tappable: the humble directory is secretly one of the most useful apps you can own.',
  'g.a5e1': '“A directory of local coffee shops with filters and detail pages.”',
  'g.a5e2': '“A conference app with a schedule, speakers, and session details.”',
  'g.a5e3': '“A staff directory with roles, contacts, and a search.”',
  'g.a6t': 'Tools & calculators',
  'g.a6b': 'A tip splitter, a unit converter, a mortgage calculator, a little internal tool for your team. Small, sharp, and weirdly satisfying to install on your home screen.',
  'g.a6e1': '“A tip calculator that splits the bill across people.”',
  'g.a6e2': '“A freelance rate calculator with hours, expenses, and a margin.”',
  'g.a6e3': '“A unit converter for cooking measurements.”',
  // Guide cards (title / paragraph / bullets / compare)
  'g.c1t': 'Name the screens, the data, and the flow',
  'g.c1p': 'The engine is brilliant at filling in detail, but it can’t read your mind. Tell it the screens you want, what’s on them, and how someone moves between them.',
  'g.c1bad': '“Make me a salon app.”',
  'g.c1good': '“A hair salon app with a home screen, a services menu grouped by category, a stylists page, and a booking flow that goes service → stylist → date → time → confirm.”',
  'g.c2t': 'Describe the mobile experience you want',
  'g.c2p': 'Pomela builds mobile-native apps, so talk about it like one. Mention a bottom tab bar, cards, a pull-to-refresh, a bottom sheet, haptics: the textures of a real phone app.',
  'g.c2b1': 'Say the vibe: “elegant and minimal”, “bold and playful”, “dark and technical”. A clear aesthetic gives the whole app a personality.',
  'g.c2b2': 'Name your colours and mood if you have them: “warm ivory with a rose-gold accent”, “forest green and terracotta”.',
  'g.c3t': 'Avoid the three things that trip people up',
  'g.c3p': 'Most underwhelming apps come from the same few prompts. Sidestep them and you’re golden:',
  'g.c3b1': 'Too vague. “An app for my business” gives the engine nothing to hold onto. Two extra sentences about screens and content change everything.',
  'g.c3b2': 'Describing the plumbing, not the experience. Pomela apps can sign people in, store data live, take real payments, and accept file uploads. You get there by describing what the user does (“a checkout screen with an order summary,” “let people save their favourites”), not the backend wiring. Ask for the experience; Pomela handles the rest.',
  'g.c3b3': 'Stuffing one screen. Ten features on one page reads as clutter. Spread them across a few screens with a tab bar. It’s more native and far prettier.',
  'g.c4t': 'Then iterate, without losing a thing',
  'g.c4p': 'Your first generation is a starting point, not a final answer. The real magic is the back and forth: change one thing at a time and watch the app evolve, with all your data and the rest of the app left perfectly intact.',
  'g.c4b1': '“Make the home hero bigger and add a tagline.” “Use a warmer accent colour.” “Add a loyalty section to the profile.” Small, specific edits land best.',
  'g.c4b2': 'Every example app on Pomela, including the ones on our homepage, was built exactly this way: one prompt, then refined by describing changes. No exceptions, no mockups.',
  'g.c5t': 'Quick edits: change anything, lose nothing',
  'g.c5p': 'Refine any part of a finished app without rebuilding it. In an app’s preview, tap the Magic Wand, point at the exact element you mean (a heading, a button, a card), and describe the change. Pomela patches just that piece and leaves the rest, and all your data, untouched.',
  'g.c5b1': 'It’s the difference between “redo the whole thing and pray” and “nudge this one button”. Edit fearlessly.',
  'g.c6t': 'Install, offline, and push: the native trifecta',
  'g.c6p': 'Every app you build is a real installable app, not a bookmark. This is the whole point of Pomela: it feels native because it basically is.',
  'g.c6b1': 'Install: add it to your home screen, with its own icon, full-screen, no browser chrome. (iPhone: Share → “Add to Home Screen”. Android: ⋮ → “Install app”.)',
  'g.c6b2': 'Offline: it keeps working on the subway. Your data lives on the device.',
  'g.c6b3': 'Push: the right apps can nudge people, a booking reminder, a “time to water your plant”, a streak about to break.',
  'g.c7t': 'Connect your own payments, keep 100%',
  'g.c7p': 'Building something that sells? Plug in your own payment processor and the money goes straight to you. Pomela never sits between you and your customers’ cash. It’s your business, your account, your 100%.',
  'g.c8t': 'Add real AI features inside your app',
  'g.c8p': 'Your apps can be smart, too: a recommender, a little assistant, a “summarise this” button. Describe the smart feature you want and Pomela wires it up, powered by the same engine that built the app.',
  'g.c9t': 'How credits work (the plain-English version)',
  'g.c9p': 'One simple balance powers everything: building apps and the AI features inside them both draw from the same pool. No maths, no per-action pricing to decode.',
  'g.c9b1': 'Running low? Top up in a tap, or bring your own AI key for unlimited use billed straight to your provider.',
  'g.c9b2': 'Free gives you plenty of room to build and explore. Pro unlocks a much bigger building pool, AI video generation, custom domains, code export, and badge removal.',
  'g.c10t': 'Your code, genuinely yours',
  'g.c10p': 'No lock-in, ever. From any finished app, open Export:',
  'g.c10b1': 'Push to GitHub (Pro): the full project lands in a brand-new repo, ready to clone and keep building.',
  'g.c10b2': 'Download the project: a standard Vite + Preact + Tailwind project, free for everyone. Walk away with it whenever you like.',
  'g.c11t': 'Share your app',
  'g.c11p': 'Pomela apps are made to travel. Share a link and it opens instantly with a beautiful preview.',
  'g.c11b1': 'Turn on the share link in the editor to get a short address you can hand out.',
  'g.c11b2': 'Send the link on WhatsApp or X, or show its QR code, straight from the share card.',
  'g.c11b3': 'Every shared app carries a tasteful “Built with Pomela”, your work quietly doing your marketing for you.',
  'g.c12t': 'Patterns that just work',
  'g.c12b1': 'Lead with a hero. One confident headline and a clear action up top sets the tone for the whole app.',
  'g.c12b2': 'Three to five tabs, max. A focused bottom tab bar beats a sprawling menu every time.',
  'g.c12b3': 'Ask for real demo content. “Seed it with a dozen realistic products” makes an app feel alive instantly. Empty apps look unfinished.',
  'g.c12b4': 'Name one accent colour. A single confident accent does more for “premium” than any amount of gradients.',
  'g.c13t': 'Pitfalls to dodge',
  'g.c13b1': 'Don’t ask for logins or real accounts. Describe a profile screen instead: same feel, no broken plumbing.',
  'g.c13b2': 'Don’t cram a “dashboard” of metric tiles. A wall of stat cards reads as generic. Put the richness into a feed, a list, or a detail view.',
  'g.c13b3': 'Don’t rewrite when you can edit. If 90% is right, surgically edit the 10%. Regenerating from scratch throws away the good parts (and your data).',
  'g.c14t': 'A 60-second recipe for a great first app',
  'g.c14p': 'Copy this shape, swap in your idea, and you’ll get something lovely on the first try:',
  'g.c14good': '“A [vibe] [type] app called [name]. Screens: [screen 1 with what’s on it], [screen 2], [screen 3]. A bottom tab bar across them. [One signature flow, step by step]. Seed it with realistic demo content. [Colour / mood].”',
  'g.c14foot': 'Then install it, live with it for a day, and edit the bits that bug you. That loop (build, use, nudge) is how every great Pomela app gets made.',
  'g.compareBad': 'Vague', 'g.compareGood': 'Specific',
  'g.foot': 'That’s the field guide. Close this and go build something you’ll actually use. 🍊',
  'help.linkKb': 'Knowledge base', 'help.linkKbSub': 'Walkthroughs, tips, and prompt patterns.',
  'help.linkPress': 'Press & partnerships', 'help.linkPressSub': 'We’d love to hear from journalists and creators.',
  'acc.proFeat': 'Bigger pool · AI video · custom domains · code export · $19/mo',
  'acc.active': 'Active',
  'acc.proAll': 'A much bigger building pool, AI video generation, custom domains, and code export. All yours.',
  'acc.upgraded': 'Your account is upgraded. Your bigger building pool and AI video are unlocked.',
  'acc.signInVia': '{p} sign-in',
  'acc.email': 'Email',
  'upg.refund1': 'Cancel from Settings → Account in one tap. You keep Pro until the end of the current billing cycle.',
  'upg.refund2': 'No price hikes mid-cycle. If we ever raise the price, your existing subscription stays locked at the rate you signed up at.',
  'upg.refund3': 'Your apps are yours. They keep working on your home screen even after you cancel.',
  'upg.refundFoot': 'Prices in USD. Taxes calculated at checkout where applicable.',
  // Credit panel
  'cr.title': 'Credits',
  'cr.sub': 'Powers your builds and your apps’ AI.',
  'cr.signIn': 'Sign in to see your credits.',
  'cr.loadErr': 'Could not load your credits.',
  'cr.left': 'Credits left',
  'cr.exhausted': 'You’re out of credits',
  'cr.topupHint': 'Top up to keep building and keep your apps’ AI running.',
  'cr.upgradeHint': 'Upgrade to Pro for a much bigger monthly pool, or add your own API key.',
  'cr.buy': 'Buy credits',
  'cr.upgradeCta': 'Upgrade to Pro',
  'cr.useKey': 'Use your key',
  'cr.buyMore': 'Buy more credits',
  'cr.upgradeMore': 'Upgrade for more',
  'cr.ownKey': 'Use your own key',
  'cr.unlimited': 'unlimited',
  'cr.keyBilled': 'Calls run on your provider, billed to you.',
  'cr.remove': 'Remove',
  'cr.yourKey': 'Your key:',
  'cr.used': '{pct}% used.',
  'cr.alertTopup': 'Top up so your builds and your apps’ AI keep running.',
  'cr.alertUpgrade': 'Upgrade to Pro for a much bigger monthly pool.',
  'cr.keyLabel': 'Your Anthropic API key',
  'cr.save': 'Save',
  'cr.keyStored': 'Stored encrypted in your vault. Never shown again, never sent to the browser.',
  'cr.errKeyRejected': 'That key was rejected.',
  'cr.errSaveKey': 'Could not save key.',
  'cr.errRemoveKey': 'Could not remove key.',
  'cr.errTopup': 'Could not start the top-up.',
  // Admin dashboard (in-creator founder view). BI acronyms (MRR/ARPU/DAU/WAU/MAU)
  // are intentionally kept untranslated — they are universal even in Hebrew BI.
  'a.admin': 'Admin',
  'a.godMode': 'Founder god mode',
  'a.welcome': 'Welcome back, {email}',
  'a.founder': 'founder',
  'a.openAnalytics': 'Open full analytics',
  'a.cap1': 'Generation ceilings bypassed',
  'a.cap2': 'Founder OTP shortcut active',
  'a.pulse': 'Platform pulse',
  'a.pulseSub': 'Live numbers from the live tables, cached for five minutes.',
  'a.totalUsers': 'Total users',
  'a.paidUsers': 'Paid users',
  'a.proSuffix': '{n}% pro',
  'a.arpuSuffix': '{v} ARPU',
  'a.ofMau': '{n}% of MAU',
  'a.appsGenerated': 'Apps generated',
  'a.last30': 'Last 30 days',
  'a.dailyActive': 'Daily active users',
  'a.today': 'Today',
  'a.todaySub': 'Counts since the UTC day rollover.',
  'a.recent': 'Recent activity',
  'a.recentSub': 'Latest 50 events across every device and account.',
  'a.noEvents': 'No events yet. Generations, signups, and sign-ins will appear here as the platform sees activity.',
  'a.tSignups': 'Signups',
  'a.tSignins': 'Sign-ins',
  'a.tSessions': 'Sessions started',
  'a.tApps': 'Apps generated',
  'a.tFree': 'Free tier generations',
  'a.tPro': 'Pro tier generations',
  'a.tCheckouts': 'Checkouts started',
  'a.tSubs': 'New subscriptions',
  'a.anon': 'anonymous',
  'a.sessionExpired': 'Session expired',
  'a.sessionExpiredBody': 'Sign in again to load the dashboard data.',
  'a.forbidden': 'Forbidden',
  'a.forbiddenBody': 'This account is signed in but is not authorized for admin endpoints.',
  'a.loadErr': 'Could not load metrics',
  'a.loadErrBody': 'The /v1/admin/overview request failed ({msg}). Sparklines and KPIs will appear once the gateway is reachable.',
  'a.founderOnly': 'Founder access only',
  'a.founderOnlyBody': 'Sign in as the founder account to open this dashboard.',
  'a.now': 'now',
  'a.secAgo': '{n}s ago',
  'a.minAgo': '{n}m ago',
  'a.hrAgo': '{n}h ago',
  'a.dayAgo': '{n}d ago',
  'a.wkAgo': '{n}w ago',
  'a.evSignup': 'New account',
  'a.evSignin': 'Sign-in',
  'a.evSignout': 'Sign-out',
  'a.evSession': 'Session started',
  'a.evAppGen': 'App generated',
  'a.evAppRev': 'App revised',
  'a.evAppExp': 'App exported',
  'a.evAppClone': 'App cloned',
  'a.evAppInstall': 'App installed',
  'a.evCheckout': 'Checkout started',
  'a.evSubStart': 'Subscription started',
  'a.evSubUp': 'Subscription upgraded',
  'a.evSubCancel': 'Subscription cancelled',
  'a.evCredits': 'Credits consumed',
  'a.tickets': 'Support tickets',
  'a.ticketsSub': '{open} open · {total} total',
  'a.loading': 'Loading…',
  'a.ticketsErr': 'Could not load tickets ({err}).',
  'a.noTickets': 'No support tickets yet.',
  'a.stReviewed': 'reviewed',
  'a.stResolved': 'resolved',
  'a.stDismissed': 'dismissed',
  'a.stOpen': 'open',
  // Generation stream (shown during every build)
  'gen.building': 'Building',
  'gen.fallbackName': 'Your new app',
  'gen.working': 'Working on it',
  'gen.stage.blueprint': 'Drafting the blueprint',
  'gen.stage.hydrate': 'Building the screens',
  'gen.stage.aesthetic': 'Choosing the aesthetic',
  'gen.stage.polish': 'Polishing the details',
  'gen.stage.ready': 'Landing on your home screen',
  'gen.status.0': 'Sketching the layout…',
  'gen.status.1': 'Picking the right palette…',
  'gen.status.2': 'Drafting the screens…',
  'gen.status.3': 'Choosing typography that feels right…',
  'gen.status.4': 'Composing the navigation…',
  'gen.status.5': 'Wiring the interactions…',
  'gen.status.6': 'Refining the spacing…',
  'gen.status.7': 'Adding the finishing touches…',
  'gen.status.8': 'Preparing the install bundle…',
  'gen.status.9': 'Almost ready to land on your home screen…',
  'gen.ariaProgress': 'Generation progress',
  'gen.ariaMilestones': 'Generation milestones',
  'gen.queueLine': 'You’re in line',
  'gen.queueEtaSec': 'about {n}s',
  'gen.queueEtaMin': 'about {n} min',
  'gen.queuePos': '#{n} in the queue',
  'gen.queueDemand': 'Pomela’s in high demand right now, your app starts the moment a slot opens.',
  'gen.slow': 'Still here, complex apps can take the full three minutes.',
  'gen.cancel': 'Cancel',
  'gen.timeout': 'That took longer than expected. Your prompt is saved, try again whenever.',
  'gen.retry': 'Retry',
  'gen.echo': 'Building from:',
  'gen.attachHeading': 'Working from what you attached',
  'gen.attachAria': 'Attachments included in this build',
  'gen.attachKind.image': 'Image',
  'gen.attachKind.rows': 'Spreadsheet',
  'gen.attachKind.pdf': 'PDF',
  'gen.attachRows': '{n} rows',
  'gen.hold': 'Almost yours — waiting for it to finish going live. This is the real handoff, not a countdown.',
  'gen.previewNote': 'Layout preview',
  'gen.reassure': 'This usually takes a minute or two. Keep this tab open — your app appears the moment it’s really live.',
  'gen.tipK': 'While you wait',
  'gen.tip.0': 'Your app installs to your home screen in one tap — no app store.',
  'gen.tip.1': 'After the build, tap any element with the Magic Wand to edit just that piece.',
  'gen.tip.2': 'Ask Pomy to reshape the whole app in plain words — no code.',
  'gen.tip.3': 'Publishing is separate from editing: your changes stay drafts until you say go live.',
  // §V2 Home below-the-fold
  'hm.recents.title': 'Pick up where you left off',
  'hm.recents.open': 'Open Workspace',
  'hm.sparks.title': 'Not sure what to build? Start from a spark.',
  'hm.sparks.sub': 'Tap any idea to drop it into the composer above — edit it, or build it as-is.',
  'hm.f.all': 'All',
  'hm.f.personal': 'Personal',
  'hm.f.business': 'Business',
  'hm.f.hobby': 'Hobby',
  'hm.f.social': 'Social',
  'hm.useIdea': 'Use this idea',
  'hm.spark.0.name': 'Water tracker',
  'hm.spark.0.tag': 'A glass a day, gently reminded.',
  'hm.spark.0.prompt': 'a water tracker that gently reminds me to drink more water through the day',
  'hm.spark.1.name': 'Studio bookings',
  'hm.spark.1.tag': 'Take appointments and deposits, no phone tag.',
  'hm.spark.1.prompt': 'a booking page for my studio with deposits and appointment reminders',
  'hm.spark.2.name': 'Language streak',
  'hm.spark.2.tag': '10 minutes a day, tracked like a habit.',
  'hm.spark.2.prompt': 'a daily streak tracker for 10 minutes of language practice',
  'hm.spark.3.name': 'Trip planner',
  'hm.spark.3.tag': "One place for dates, docs and who's coming.",
  'hm.spark.3.prompt': "a trip planner where friends can see the dates, shared docs and who's coming",
  'hm.spark.4.name': 'Budget jar',
  'hm.spark.4.tag': 'Envelopes for spending, without the spreadsheet.',
  'hm.spark.4.prompt': 'a budget app with spending envelopes instead of a spreadsheet',
  'hm.spark.5.name': 'Client CRM',
  'hm.spark.5.tag': 'Every client, every note, in one list.',
  'hm.spark.5.prompt': 'a simple CRM to keep notes on every client in one list',
  'hm.spark.6.name': 'Recipe box',
  'hm.spark.6.tag': 'Your recipes, searchable, never lost again.',
  'hm.spark.6.prompt': 'a recipe box where I can save and search my own recipes',
  'hm.spark.7.name': 'Game night RSVP',
  'hm.spark.7.tag': "Send one link, know who's actually coming.",
  'hm.spark.7.prompt': "a game night RSVP page where I send one link and see who's actually coming",
  'hm.cta.ey': 'Ready when you are',
  'hm.cta.title': 'Your idea is one sentence away.',
  'hm.cta.sub': 'No code, no waiting, no app store review. Just describe it.',
  'hm.cta.btn': 'Start building',
  // Paywall modal (monetization gates)
  'pw.removeBadge.headline': 'Make it yours.',
  'pw.removeBadge.body': 'Remove the “Built with Pomela” badge so your app reads as your own brand from the first impression.',
  'pw.removeBadge.b1': 'Badge disappears across every install',
  'pw.removeBadge.b2': 'Custom theme-color shows on the browser chrome',
  'pw.removeBadge.b3': 'A much bigger monthly building pool',
  'pw.removeBadge.b4': 'Everything in Pro: unlimited apps, custom domains, code export',
  'pw.removeBadge.cta': 'Remove badge & upgrade',
  'pw.exportCode.headline': 'Export the full project.',
  'pw.exportCode.body': 'Get the full Vite + Preact + Tailwind project and push it straight to your own GitHub repo with one click.',
  'pw.exportCode.b1': 'One-click GitHub repository creation',
  'pw.exportCode.b2': 'Includes all generated source: JSX, CSS, manifest',
  'pw.exportCode.b3': 'Own your source — it builds and runs anywhere; saved data and sign-in stay on Pomela',
  'pw.exportCode.b4': 'Everything in Pro: unlimited apps, custom domains, badge removal',
  'pw.exportCode.cta': 'Export code & upgrade',
  'pw.customDomain.headline': 'Ship on your own domain.',
  'pw.customDomain.body': 'Connect a custom domain like app.yourbrand.com. Automatic SSL, instant DNS provisioning, no setup.',
  'pw.customDomain.b1': 'Connect any apex or subdomain',
  'pw.customDomain.b2': 'Automatic Let’s Encrypt SSL, no certs to manage',
  'pw.customDomain.b3': 'DNS guidance built in: copy and paste the record',
  'pw.customDomain.b4': 'Everything in Pro: unlimited apps, code export, badge removal',
  'pw.customDomain.cta': 'Connect domain & upgrade',
  'pw.for': 'for',
  'pw.cadence': '/ month · cancel anytime',
  'pw.maybeLater': 'Maybe later',
  'pw.fineprintPitch': 'Secure checkout. Your subscription powers the engine and every Pomela feature you’re already using.',
  'pw.back': 'Back',
  // §LG2 — Pomela Checkout (real, embedded, PayPal-processed; no simulated payment UI)
  'pc.proTitle': 'Upgrade to Pomela Pro',
  'pc.proLine': 'Pomela Pro · ${v} / month · cancel anytime',
  'pc.topupTitle': 'Buy credits',
  'pc.topupSub': 'Add credits to keep building.',
  'pc.pkgLine': '{n} credits · ${v} one-time',
  'pc.choosePack': 'Choose a credit pack',
  'pc.loading': 'Loading secure checkout…',
  'pc.unavailable': 'Checkout is temporarily unavailable. Please try again shortly.',
  // Deliberately switched off — NOT transient. Never tell someone to retry something that cannot
  // succeed; Pro is unavailable to buy, and saying so plainly is the only honest option.
  'pc.disabled': 'Paid plans are not available yet. Nothing has been charged, and Pomela is free to use in the meantime.',
  'pc.sdkError': 'The payment window hit a problem. You were not charged — try again.',
  'pc.fallbackNote': 'The embedded checkout couldn’t load here. You can finish securely on PayPal instead.',
  'pc.fallbackCta': 'Continue on PayPal',
  'pc.verifying': 'Verifying your payment…',
  'pc.credited': '{n} credits added!',
  'pc.balanceNow': 'Your balance is now {n} credits.',
  'pc.already': 'This payment was already processed — your credits are in your account.',
  'pc.proActive': 'Welcome to Pomela Pro',
  'pc.doneCta': 'Done',
  'pc.failed': 'Payment not completed',
  'pc.failedLine': 'The payment didn’t complete, so nothing was charged and no credits were added. You can try again.',
  'pc.webhookPending': 'Payment approved — finishing activation…',
  'pc.webhookPendingLine': 'PayPal approved your payment. Activation can take a minute and completes automatically — if your plan doesn’t update, contact support and we’ll make it right.',
  'pc.supportTitle': 'Payment received — credits not visible yet',
  'pc.supportLine': 'Your payment may have gone through, but we couldn’t confirm your new balance. Don’t pay again — contact support and we’ll sort it out.',
  'pc.supportCta': 'Contact support',
  'pc.cancelled': 'Checkout cancelled — you were not charged.',
  'pc.creditedToast': '+{n} credits added to your account',
  'pc.captureFailed': 'We couldn’t confirm that payment. If you approved it at PayPal, contact support — don’t pay again.',
  'pc.payNote': 'Payments are processed securely by PayPal. Pomela never sees your card details.',
  // §LG2 — Bug bounty (real, admin-approved, exactly-once 100-credit award)
  'bb.title': 'Bug bounty',
  'bb.sub': 'A real, reproducible bug earns {n} credits.',
  'bb.cta': 'Report a bug',
  'bb.category': 'Category',
  'bb.cat.generation': 'App generation',
  'bb.cat.editing': 'Editing',
  'bb.cat.ui': 'Interface',
  'bb.cat.payment': 'Payments & credits',
  'bb.cat.data-loss': 'Data loss',
  'bb.cat.security': 'Security & privacy',
  'bb.cat.other': 'Other',
  'bb.desc': 'What happened?',
  'bb.descPh': 'Describe the bug — what you expected vs. what actually happened (20+ characters).',
  'bb.steps': 'Steps to reproduce',
  'bb.stepsPh': '1. Open… 2. Tap… 3. See the bug.',
  'bb.urlPh': 'App or page link (optional)',
  'bb.submit': 'Submit report',
  'bb.cancel': 'Cancel',
  'bb.fine': 'Reports are reviewed by a human. A confirmed, unique, reproducible bug earns {n} credits — duplicates and non-bugs don’t.',
  'bb.mine': 'My reports',
  'bb.submitted': 'Report submitted — we’ll review it soon.',
  'bb.tooShort': 'Add a fuller description and the steps to reproduce.',
  'bb.tooMany': 'Daily report limit reached — your existing reports are in review.',
  'bb.submitErr': 'Couldn’t submit the report. Try again.',
  'bb.st.submitted': 'In review',
  'bb.st.needs_info': 'Needs info',
  'bb.st.accepted': 'Accepted',
  'bb.st.duplicate': 'Duplicate',
  'bb.st.rejected': 'Not a bounty bug',
  'bb.st.awarded': 'Awarded',
  'pw.welcome': 'Welcome to Pomela Pro',
  'pw.upgradedReload': 'Your account is upgraded. Reloading so the rest of the app catches up…',
  'pw.toastSignInFirst': 'Sign in first, then we’ll take you to checkout.',
  'pw.toastSignInUpgrade': 'Sign in to upgrade.',
  'pw.toastBillingOffline': 'Billing is temporarily offline. Try again shortly.',
  'pw.toastConnLost': 'Connection lost. Check your internet and try again.',
  // Auth wall (guest free-build wall)
  'auth.cta': 'Sign in, it’s free',
  'auth.freeForever': 'Free forever',
  'auth.noCard': 'No card',
  'auth.magicLink': 'Magic-link sign-in',
  'auth.maybeLater': 'Maybe later',
  'auth.ariaSheet': 'Sign in to keep building',
  'auth.ariaDismiss': 'Drag down to dismiss',
  'auth.ariaGuarantees': 'Free-tier guarantees',
  // Share card (post-generation viral surface)
  'share.text': 'I built {name} in one sentence with Pomela ✨',
  'share.headline0': 'Your app is live. Send it to your first 5 users.',
  'share.headlineDone': 'You hit 5 shares. Keep the momentum going.',
  'share.toGoOne': '1 more share to go, keep the momentum going.',
  'share.toGoMany': '{n} more shares to go, keep the momentum going.',
  'share.share': 'Share',
  'share.shareLink': 'Share link',
  'share.ariaShare': 'Share this app',
  'share.ariaCopy': 'Copy share URL',
  'share.ariaQr': 'Show QR code',
  // Command palette (Cmd+K)
  'cmd.new': 'Start a new project',
  'cmd.newHint': 'Open the home canvas with a fresh prompt',
  'cmd.ai': 'Go to Pomy',
  'cmd.aiHint': 'Brainstorm an app idea with the copilot',
  'cmd.workspaceHint': 'Reopen apps you’ve already built',
  'cmd.kbHint': 'Tips, prompts and patterns for building with Pomela',
  'cmd.placeholder': 'Where do you want to go?',
  'cmd.aria': 'Command palette',
  'cmd.ariaSearch': 'Search commands',
  'cmd.ariaCommands': 'Commands',
  'cmd.close': 'close',
  // Copilot (Pomy) error states
  'cop.errBusyWait': 'The copilot is busy. Wait {n}s before your next idea.',
  'cop.errBusyMoment': 'The copilot is busy. Give it a moment before the next idea.',
  'cop.errAuth': 'Sign in to continue brainstorming with the copilot.',
  'cop.errServer': 'The copilot is taking a breath. Try again in a moment.',
  'cop.errGeneric': 'The copilot hit a snag. Try again in a moment.',
  'cop.errNetwork': 'Connection lost. Check your internet and try again.',
  // Attach-file (prompt input) error states
  'pi.attachErr': 'Couldn’t attach that file. Try again.',
  'pi.attachType': 'That file type isn’t supported. Attach an image, PDF, CSV, or Excel file.',
  'pi.attachSize': 'That file is a little too big. Images up to 8 MB, documents up to 15 MB.',
  'pi.attachRate': 'You’ve attached a lot today. Try again in a bit.',
  // Surgical edit rejected states
  'se.rejectTitle': 'Change rejected, nothing was lost',
  'se.rejectBody': 'Your app kept serving the previous version.',
  'se.rejectNetwork': 'We couldn’t reach the studio. Check your connection and try again.',
  // Public landing (waitlist) — the front door
  'lp.navCta': 'Get early access',
  'lp.heroEyebrow': 'Type. Tap. Install.',
  'lp.heroTitle': 'Apps that feel <span class="lp-title-accent">native</span>.<br /> Built from a sentence.',
  'lp.heroSub': 'Pomela builds a real mobile app, not just a website. It installs to your home screen and works offline. Describe it, tap once, and it’s on your phone in about a minute.',
  'lp.heroTrust': 'One email at launch. No spam, no marketing. Promise.',
  'lp.formSending': 'Sending…',
  'lp.formSuccess': 'You’re on the list ✓',
  'lp.formAriaEmail': 'Email address',
  'lp.s1Title': 'A sentence becomes a real app in about a minute.',
  'lp.s1Sub': 'No canvas to learn, no templates to fight. You describe it in plain language, Pomela builds the whole thing, and it lands on your home screen: installable, offline-ready, yours.',
  'lp.step1Kind': 'Type',
  'lp.step1Title': 'Describe it in a sentence',
  'lp.step1Body': '“A booking app for my studio with a calendar and opening hours.” That’s the whole brief. Plain words, no design tools, no code.',
  'lp.step2Kind': 'Tap',
  'lp.step2Title': 'Pomela builds the real thing',
  'lp.step2Body': 'Real screens, real navigation, real data that persists. Not a wireframe or a mockup, a complete working app. Refine it by describing changes.',
  'lp.step3Kind': 'Install',
  'lp.step3Title': 'Add to home screen',
  'lp.step3Body': 'One tap and it’s an icon on your phone. It launches full-screen and works offline. It feels native because, on a phone, it basically is.',
  'lp.s2Eyebrow': 'The beachhead',
  'lp.s2Title': 'For people who’d never call themselves developers.',
  'lp.s2Sub': 'Customer-facing apps for creators and small businesses, the kind you’d normally pay an agency five figures and wait three months for.',
  'lp.uc1Kind': 'Booking & service',
  'lp.uc1Title': 'Your clients book in two taps',
  'lp.uc1Body': 'Clinics, trainers, tutors, salons. Show your services and take appointments, with every booking in one place. Plug in your own payment processor and keep 100%.',
  'lp.uc2Kind': 'Storefronts · coming soon',
  'lp.uc2Title': 'Sell anything. Your money.',
  'lp.uc2Body': 'Browse, cart, checkout: a proper shop on the home screen. The payments run through your account, not ours. We never sit between you and your customers’ cash.',
  'lp.uc3Kind': 'Trackers',
  'lp.uc3Title': 'Lives on the home screen',
  'lp.uc3Body': 'Habits, workouts, inventory, flashcards. Trackers are mobile-native heaven: install once, works offline, always a tap away on your home screen.',
  'lp.s3Eyebrow': 'Why it feels different',
  'lp.s3Title': 'The details other builders skip.',
  'lp.s3Sub': 'A website in a browser tab is not an app. These are the things that make Pomela apps feel like the real thing.',
  'lp.d1Title': 'The Native Illusion',
  'lp.d1Body': 'Install to the home screen with its own icon, launch full-screen, work offline. It feels native because, on a phone, it basically is.',
  'lp.d2Title': 'Change one thing, keep everything else',
  'lp.d2Body': 'Point at any part of your app and say what to change. Pomela updates just that and leaves the rest, and all your data, exactly as it was. Edit without fear.',
  'lp.d3Title': 'Sentence to app in about a minute',
  'lp.d3Body': 'Not a wireframe. Not a prototype. A complete, working, installable app: real screens, real interactions, real data, while your coffee’s still hot.',
  'lp.d4Title': 'Genuinely yours',
  'lp.d4Body': 'Export the whole project to GitHub or download it: a standard Vite + Preact + Tailwind app. No lock-in, ever. Walk away with your code whenever you like.',
  'lp.bipEyebrow': 'Built in the open',
  'lp.bipTitle': 'A small team, slightly obsessed with making mobile creation feel like magic.',
  'lp.bipBody': 'Pomela is built in public, by people who think turning a sentence into a real app on your phone should feel like magic, not like work. We’re opening the doors soon, and the waitlist walks in first.',
  'lp.bipStat1Num': '~1 min',
  'lp.bipStat1Lbl': 'from a sentence<br />to an installable app',
  'lp.bipStat2Num': '100%',
  'lp.bipStat2Lbl': 'of every payment stays<br />yours (your processor)',
  'lp.bipStat3Num': '0',
  'lp.bipStat3Lbl': 'lock-in, export<br />your code anytime',
  'lp.bipNote': 'We could fill this section with five-star quotes from people who don’t exist. We’d rather earn the real ones. Your story could be the first: <a href="#waitlist">join the waitlist</a>.',
  'lp.finalTitle': 'Be first through the door.',
  'lp.finalSub': 'Drop your email and you’ll be among the first to turn a sentence into an app on your home screen.',
  'lp.finalTrust': 'One email at launch. No spam, no marketing.',
  'lp.footPrivacy': 'Privacy',
  'lp.footTerms': 'Terms',
  'lp.footSecurity': 'Security',
  'lp.footTag': 'Type. Tap. Install.',
  'lp.ariaRoot': 'Pomela, join the waitlist',
  'lp.ariaPrimary': 'Primary',
  'lp.ariaLegal': 'Legal',
  'lp.errEmail': 'That doesn’t look like an email. Try again?',
  'lp.errNetwork': 'Couldn’t reach the list just now. Check your connection and try again.',
  // Payments explainer (PaymentConnect)
  'pc.aria': 'Payments',
  'pc.title': 'Payments',
  'pc.sub': 'Sell from your apps, paid straight to you.',
  'pc.errSignIn': 'Sign in to connect payments.',
  'pc.errLoad': 'Could not load payment settings.',
  'pc.errRejected': 'That key was rejected.',
  'pc.errConnect': 'Could not connect.',
  'pc.errDisconnect': 'Could not disconnect.',
  'pc.connected': '{p} connected',
  'pc.connectedSub': 'Payments go straight to your {p} account.',
  'pc.disconnect': 'Disconnect',
  'pc.connectCta': 'Connect a processor',
  'pc.secretAria': 'Processor secret key',
  'pc.connect': 'Connect',
  'pc.cancel': 'Cancel',
  'pc.encrypted': 'Stored encrypted in your vault. Pomela never holds your money and takes no cut.',
  'pc.how': 'How payments work',
  'pc.how1': '<span class="font-semibold text-[color:var(--fg)]">1 · Connect your processor.</span> Paste a secret key from your own Stripe or PayPal account. It’s encrypted at rest and never shown again.',
  'pc.how2': '<span class="font-semibold text-[color:var(--fg)]">2 · Your apps start charging.</span> Any app you’ve built with a cart gets a working checkout: customers tap Checkout and finish payment on your processor’s secure page. The money lands in your account; Pomela never touches it and takes no cut.',
  'pc.how3': '<span class="font-semibold text-[color:var(--fg)]">3 · Until then, checkout is honest.</span> Without a connection, carts still work but checkout tells customers the shop isn’t taking payments yet. Nothing fake, no dead ends.',
  'pc.howReq': 'Requirements: an account with one of the four processors and its secret key (e.g. Stripe → Developers → API keys). Refunds, receipts and disputes are handled in your processor’s dashboard.',
  // Account — plan management + data deletion
  'acct.delErrConn': 'Connection lost, your account was not deleted. Check your network and try again.',
  'acct.delErrService': 'Couldn’t reach the deletion service. Your account is unchanged. Please try again in a moment, or email support@pomela.app.',
  'acct.delErrExpired': 'Sign in expired. Refresh and try again.',
  'acct.delErrStart': 'Couldn’t start the deletion. Please try again.',
  'acct.delErrResponse': 'The deletion service returned an unexpected response. Your account is unchanged. Please try again.',
  'acct.delErrFinish': 'We confirmed your deletion request but couldn’t finish removing the account. Your data is unchanged. Please try again.',
  'acct.delErrConn2': 'Connection lost. Your account is unchanged. Check your internet and try again.',
  'acct.cancelInfo': 'You’ll keep Pro until the end of your current billing cycle, then your account moves to Free. Questions? <strong style="color:#fff">support@pomela.app</strong> (or use Support in the menu). We read every note.',
  'acct.cancelling': 'Cancelling…',
  'acct.cancelPro': 'Cancel Pro',
  'acct.keepPro': 'Keep Pro',
  'acct.cancelFailed': "Couldn't cancel your plan — please try again in a moment.",
  // §S680 honesty — the deletion endpoint is DEVICE-scoped (deviceId), so the
  // copy promises exactly that: this device's data, not "your account everywhere".
  'acct.delTitle': 'Delete your Pomela data on this device?',
  'acct.delBody': 'This permanently wipes the apps, build history, and synced state created on this device. Apps built on your other devices aren’t touched. You can export everything first from <strong>Settings → Data &amp; Privacy</strong>.',
  'acct.delList1': 'Your account and its stored data are deleted right away. Published app links can take up to 24 hours to fully stop serving.',
  'acct.delList2': 'Backup logs are kept up to 30 days for fraud prevention, then purged.',
  'acct.delList3': 'You can create a fresh account later with the same email.',
  'acct.delTypeLabel': 'Type <strong>delete</strong> to confirm',
  'acct.deleting': 'Deleting your data…',
  'acct.delConfirm': 'Permanently delete',
  'acct.keepAccount': 'Keep my data',
  // GitHub / download export errors
  'ex.errToken': 'That token didn’t work. Check it’s valid and hasn’t expired.',
  'ex.errPerms': 'That token can’t create repositories. It needs repository creation and contents write access.',
  'ex.errRepoName': 'You already have a repo with that name. Try a different one below.',
  'ex.errNoToken': 'Please paste your GitHub token.',
  'ex.errNoCode': 'This app’s code isn’t available on this device.',
  'ex.errPro': 'GitHub export is a Pro feature.',
  'ex.errGeneric': 'Export failed. Please try again in a moment.',
  'ex.errReach': 'Couldn’t reach GitHub. Please try again in a moment.',
  'ex.errDl': 'Download failed. Please try again in a moment.',
  // Custom-domain errors + controls
  'dom.errInvalid': 'That doesn’t look like a domain. Try something like app.yoursite.com',
  'dom.errTaken': 'That domain is already connected to another app.',
  'dom.errNotConfigured': 'Custom domains aren’t enabled on this deployment yet.',
  'dom.errNotFound': 'We couldn’t find this app on your device.',
  'dom.errProvision': 'We couldn’t set up that domain right now. Please try again shortly.',
  'dom.errPro': 'Custom domains are a Pro feature.',
  'dom.errGeneric': 'Something went wrong. Please try again in a moment.',
  'dom.errReach': 'Couldn’t reach the server. Please try again in a moment.',
  'dom.ariaConnect': 'Connect a custom domain',
  'dom.connecting': 'Connecting…',
  'dom.connect': 'Connect domain',
  // Preview / publish
  'pv.errReach': 'Couldn’t reach your app. The preview host may have restarted. Try again.',
  'pv.errLoad': 'Couldn’t load your app. The preview host may have restarted. Try again.',
  'pv.versionHistory': 'Version history',
  'pv.viewingOriginal': 'Viewing the original',
  'pv.unavailable': 'Preview unavailable',
  'pv.preparing': 'Preparing your preview…',
  'pv.wandOn': 'Magic Wand on, click an element',
  'pv.wand': 'Magic Wand',
  'pv.publishErr': 'Couldn’t publish. Please try again in a moment.',
  // App analytics
  'an.errSignIn': 'Sign in to see your analytics.',
  'an.errLoad': 'Could not load analytics.',
  // App card (workspace)
  'ac.share': 'Share',
  'ac.linkCopied': 'Link copied',
  // Streak chip
  'streak.chip': '{current}-day building streak, best {best}',
  // Next-capability suggestions (post-generation "keep building" chips)
  'nc.header': 'Keep building, one thing at a time',
  'nc.or': 'or',
  'cap.food_ordering': 'Add online ordering', 'cap.food_ordering.why': 'Let customers order and pay from the menu',
  'cap.food_photos': 'Add item photos', 'cap.food_photos.why': 'Dishes with photos sell far better',
  'cap.fit_chart': 'Add a progress chart', 'cap.fit_chart.why': 'See trends and stay motivated',
  'cap.fit_reminders': 'Add reminders', 'cap.fit_reminders.why': 'A nudge brings people back daily',
  'cap.shop_checkout': 'Add checkout', 'cap.shop_checkout.why': 'Turn browsing into sales',
  'cap.shop_payments': 'Accept payments', 'cap.shop_payments.why': 'Get paid right in the app',
  'cap.event_rsvp': 'Add RSVP', 'cap.event_rsvp.why': 'Know who is coming',
  'cap.event_share': 'Add invites', 'cap.event_share.why': 'Let people invite their friends',
  'cap.course_progress': 'Add progress tracking', 'cap.course_progress.why': 'Show how far along they are',
  'cap.course_accounts': 'Add sign-in', 'cap.course_accounts.why': 'So progress saves to their account',
  'cap.u_accounts': 'Add sign-in', 'cap.u_accounts.why': 'So each person’s data is their own',
  'cap.u_chart': 'Add insights', 'cap.u_chart.why': 'Visualize the data at a glance',
  'cap.u_reminders': 'Add reminders', 'cap.u_reminders.why': 'Bring people back',
  'cap.u_share': 'Add sharing', 'cap.u_share.why': 'Let people share or collaborate',
  'cap.u_search': 'Add search', 'cap.u_search.why': 'Find anything fast',
  // Smart suggestions ("build another app?" chips)
  'ss.0.t': 'Meal planner', 'ss.0.b': 'Plan your meals around your workouts',
  'ss.1.t': 'Recovery log', 'ss.1.b': 'Track sleep, soreness, and rest days',
  'ss.2.t': 'Body measurements', 'ss.2.b': 'Photo + measurements over time',
  'ss.3.t': 'Pantry tracker', 'ss.3.b': 'Know what you already have',
  'ss.4.t': 'Recipe collection', 'ss.4.b': 'Save favorites + auto-build lists',
  'ss.5.t': 'Budget tracker', 'ss.5.b': 'See where your grocery spend goes',
  'ss.6.t': 'Mood journal', 'ss.6.b': 'Pair your habits with mood data',
  'ss.7.t': 'Meditation timer', 'ss.7.b': 'Quiet morning anchor',
  'ss.8.t': 'Reading log', 'ss.8.b': 'Stack a reading habit',
  'ss.9.t': 'Shopping list', 'ss.9.b': 'Auto-build from recipes',
  'ss.10.t': 'Pantry tracker', 'ss.10.b': 'Don’t buy what you have',
  'ss.11.t': 'Meal planner', 'ss.11.b': 'Drag a week into shape',
  'ss.12.t': 'Bill tracker', 'ss.12.b': 'Never miss a due date',
  'ss.13.t': 'Savings goal', 'ss.13.b': 'Watch progress toward a target',
  'ss.14.t': 'Subscription audit', 'ss.14.b': 'See what you’re paying for',
  'ss.15.t': 'Highlights vault', 'ss.15.b': 'Save quotes you love',
  'ss.16.t': 'Habit tracker', 'ss.16.b': 'Make reading a daily habit',
  'ss.17.t': 'Mood journal', 'ss.17.b': 'Pair books with feelings',
  'ss.18.t': 'Gratitude log', 'ss.18.b': 'Three things, every night',
  'ss.19.t': 'Meditation timer', 'ss.19.b': 'Anchor your evening',
  'ss.20.t': 'Sleep tracker', 'ss.20.b': 'Connect mood to rest',
  'ss.21.t': 'Sleep tracker', 'ss.21.b': 'Wind down with data',
  'ss.22.t': 'Gratitude log', 'ss.22.b': 'Close the day softly',
  'ss.23.t': 'Mood journal', 'ss.23.b': 'One tap a day',
  'ss.24.t': 'Reading log', 'ss.24.b': 'Stack with study time',
  'ss.25.t': 'Habit tracker', 'ss.25.b': 'Study a little every day',
  'ss.26.t': 'Pomodoro timer', 'ss.26.b': 'Focus sprints with breaks',
  'ss.27.t': 'Shopping list', 'ss.27.b': 'The simplest thing that works',
  'ss.28.t': 'Habit tracker', 'ss.28.b': 'One check-in a day',
  'ss.29.t': 'Pomodoro timer', 'ss.29.b': 'Focus sprints + breaks',
  'ps.habit': 'Habit tracker', 'ps.workout': 'Workout log', 'ps.flash': 'Flashcard deck',
  'ps.todo': 'To-do list', 'ps.booking': 'Booking page', 'ps.inventory': 'Inventory tracker',
  // Settings toasts (push + data deletion)
  'set.pushOnToast': 'Push notifications on.',
  'set.pushOffToast': 'Push notifications off.',
  'set.pushBlockedToast': 'Your browser is blocking notifications. Enable them in site settings.',
  'set.delSignIn': 'Sign in required before deleting your data.',
  'set.delRate': 'Too many requests. Try again in a minute.',
  'set.delStart': 'Couldn’t start the deletion. Please try again.',
  'set.delDone': 'Your Pomela data has been deleted from this device.',
  'set.delExpired': 'That confirmation has expired. Try the delete button again.',
  'set.delSnag': 'We hit a snag deleting your data. Please try again.',
  'set.delNet': 'Connection lost. Check your internet and try again.',
  // Assorted aria-labels + tooltips
  'cop.ariaMessage': 'Message the copilot',
  'cop.ariaSend': 'Send message',
  'cop.ariaThinking': 'Pomy is thinking',
  'cop.newChat': 'New chat',
  // Pomy two-pane build workspace (real handoff into the existing builder)
  'cop.buildThis': 'Build this',
  'cop.latestBuild': 'Your latest build',
  'cop.livePreview': 'Live preview',
  'cop.openBuilder': 'Open in builder',
  'cop.openWorkspace': 'Open in Workspace',
  'cop.timelineTitle': 'Version timeline',
  'cop.versionsN': 'versions',
  'cop.versionN': 'version',
  'cop.localOnly': 'Saved on this device',
  'cop.restoreHint': 'Open in builder to roll back a version',
  'cop.buildEmptyT': 'Your builds show up here',
  'cop.buildEmptyS': 'Describe an app and Pomy hands the brief to the builder — your live app and its versions land here.',
  'cop.original': 'First build',
  'guide.ariaSections': 'Guide sections',
  'cookie.ariaPrefs': 'Cookie preferences',
  'common.dismiss': 'Dismiss',
  'pi.attachTitle': 'Attach photos, a spreadsheet, or a PDF to guide your app’s content',
  'pi.attachAria': 'Attach files',
  'ex.ariaExport': 'Export to GitHub',
  'pv.wandPlaceholder': 'Make it bigger, change the color, reword it…',
  'app.openMenu': 'Open menu',
  'app.tapAria': 'Type. Tap. Install.',
  // My Apps + sheets + misc controls
  'my.confirmRemove': 'Remove from My Apps?',
  'my.duplicate': 'Duplicate as a new app',
  'my.remove': 'Remove from My Apps',
  'ex.title': 'Export to GitHub',
  'ex.pushed': 'Pushed to GitHub',
  'ex.repoName': 'Repository name',
  'ex.makePublic': 'Make the repository public',
  'dom.title': 'Connect a custom domain',
  'dom.registered': 'Domain registered',
  'dom.yourDomain': 'Your domain',
  'rp.placeholder': 'Make it dark mode · Add a timer · Use a green accent…',
  'nav.ariaHome': 'Go to the Pomela home screen',
  'cr.ariaKey': 'Anthropic API key',
  'cel.aria': 'Your first Pomela app is live',
  'se.ariaQuickEdit': 'Quick edit',
  'se.placeholder': 'Type the new text…',
  // Final tail — quick actions, onboarding starters, element labels, empties
  'cop.qa1': 'Help me ideate', 'cop.qa2': 'I’m stuck', 'cop.qa3': 'Sharpen my prompt',
  'se.lblHeadline': 'Headline', 'se.lblTitle': 'Title',
  'eb.title': 'Something went sideways',
  'eb.body': 'Pomela hit an unexpected snag. A reload usually sets things right, and the apps you’ve already built are saved safely on this device.',
  'rp.title': 'Refine your app',
  'sb.noRecent': 'No recent apps',
  'an.empty': 'No apps yet', 'an.emptySub': 'Build your first app and its numbers show up here.',
  'an.subtitle': 'Views, installs and AI activity, per app.', 'an.emptyCta': 'Create an app',
  'adm.cohortsEmpty': 'No cohort data, run the seed script and the aggregator.',
  'adm.noFailures': 'No failures in this window. 🎉', 'adm.userNoApps': 'No apps yet.',
  'adm.noCrashes': 'No crashes in this window. 🎉', 'adm.noPlans': 'No active plans.', 'adm.noModelData': 'No model data.',
  'sb.navigate': 'Navigate', 'sb.recentApps': 'Recent apps', 'sb.starterIdeas': 'Starter ideas', 'sb.helpFeedback': 'Help & feedback',
  'an.title': 'Your apps', 'vh.currentlyViewing': 'Currently viewing',
  'se.noNodes': 'No editable nodes in this app yet.',
  'se.unavailable': 'Quick edit isn’t available for this app',
  'se.unavailableBody': 'It was built before live editing. Use <span class="font-semibold text-[color:var(--fg)]">Refine your app</span> below to change anything with AI.',
  // Admin dashboard (founder-only BI; acronyms MRR/ARPU/DAU stay English)
  'adm.navOverview': 'Overview',
  'adm.navUsers': 'Users',
  'adm.navCohorts': 'Cohorts',
  'adm.navFunnel': 'Funnel',
  'adm.navRevenue': 'Revenue',
  'adm.navApps': 'Apps',
  'adm.navCredits': 'Credits',
  'adm.navGeneration': 'Generation',
  'adm.crashes': 'Crashes',
  'adm.feedback': 'Feedback',
  'adm.navSupport': 'Support',
  'adm.backToCreator': 'Back to creator',
  'adm.appsEmpty': 'No installed apps yet.',
  'adm.themePresets': 'Theme presets',
  'adm.categories': 'Categories',
  'adm.mostInstalled': 'Most installed',
  'adm.colApp': 'App',
  'adm.colCategory': 'Category',
  'adm.colInstalls': 'Installs',
  'adm.colViews': 'Views',
  'adm.noGens': 'No generations yet.',
  'adm.noCatData': 'No category data.',
  'adm.cohortRetention': 'Cohort retention',
  'adm.sortedFreq': 'sorted by frequency',
  'adm.allCrashes': 'All crashes',
  'adm.7days': '7 days',
  'adm.30days': '30 days',
  'adm.90days': '90 days',
  'adm.colType': 'Type',
  'adm.colMessage': 'Message',
  'adm.colLastSeen': 'Last seen',
  'adm.usdPerDay': 'USD per day',
  'adm.noSpend': 'No spend recorded.',
  'adm.dailySpend': 'Daily spend',
  'adm.byModel': 'By model',
  'adm.topConsumers': 'Top consumers',
  'adm.colAccount': 'Account',
  'adm.colSpend': 'Spend',
  'adm.noCredConsumption': 'No credit consumption in this window.',
  'adm.feedbackHint': 'Problems and ideas builders sent in, newest first.',
  'adm.feedbackEmpty': 'No open feedback. When builders send a problem or an idea, it lands here.',
  'adm.convFunnel': 'Conversion funnel',
  'adm.last30': 'Last 30 days',
  'adm.last90': 'Last 90 days',
  'adm.lastYear': 'Last year',
  'adm.failingPrompts': 'most-repeated failing prompts',
  'adm.archHealth': 'demand + per-archetype health',
  'adm.catalogGapsHint': 'prompts the catalog couldn\'t express',
  'adm.rawKindsHint': 'free-coded shape census → next catalog block',
  'adm.failedPrompts': 'Failed prompts',
  'adm.archetypes': 'Archetypes',
  'adm.catalogGaps': 'Catalog gaps',
  'adm.rawKinds': 'Raw kinds',
  'adm.colPrompt': 'Prompt',
  'adm.colError': 'Error',
  'adm.colArchetype': 'Archetype',
  'adm.colGens': 'Gens',
  'adm.colSuccess': 'Success',
  'adm.colAvgRaw': 'Avg raw',
  'adm.colForcedPrompt': 'Prompt that forced free-code',
  'adm.colRawBlocks': 'Raw blocks',
  'adm.catalogCovered': 'None, the catalog covered everything.',
  'adm.sinceUtc': 'since 00:00 UTC',
  'adm.today': 'Today',
  'adm.activity': 'Activity',
  'adm.mrrHint': 'new vs expansion vs churned, by day',
  'adm.churnHint': 'no app generated in 14 days',
  'adm.noChurnRisk': 'No accounts at risk, everyone is active.',
  'adm.mrrMovement': 'MRR movement',
  'adm.planMix': 'Plan mix',
  'adm.churnRisk': 'Churn risk',
  'adm.mtd': 'Month to date',
  'adm.qtd': 'Quarter to date',
  'adm.ytd': 'Year to date',
  'adm.colLastGen': 'Last generation',
  'adm.noSubEvents': 'No subscription events in this window.',
  'adm.supportHint': 'Support requests from the in-app form, newest first.',
  'adm.supportEmpty': 'No support tickets yet. When someone submits the support form, it lands here.',
  'adm.actions': 'Actions',
  'adm.timeline': 'Timeline',
  'adm.appsCreated': 'Apps created',
  'adm.noUsersMatch': 'No accounts match this filter.',
  'adm.newest': 'Newest',
  'adm.colLastActive': 'Last active',
  'adm.colEmail': 'Email',
  'adm.colPlan': 'Plan',
  'adm.colLtv': 'LTV',
  'adm.colJoined': 'Joined',
  // §S679 — editing workspace (Pomy + Magic Wand + draft/publish)
  'ew.tabPomy': 'Pomy',
  'ew.tabWand': 'Magic Wand',
  // §S6 — the structured quick-edit surface (was mislabeled "Magic Wand"):
  'ew.tabCustomize': 'Customize',
  'ew.wandNotReadyPane': 'Click-to-pick isn\'t ready on this app yet — the Customize tab lists every editable part.',
  'ew.wandTurnOn': 'Turn on the Wand',
  'ew.wandLive': 'The wand is on — tap any part of your app in the preview.',
  'ew.wandStep1': 'Turn the wand on',
  'ew.wandStep2': 'Tap any part of your app in the preview',
  'ew.wandStep3': 'Change just that piece — text, price, image or color',
  'ew.wandStep4': 'Apply — it lands on your private draft',
  'ew.wandUndoNote': 'Every change is draft-first: your live app only updates when you publish, and History can bring any version back.',
  'ew.pomyHero': 'Tell me what you want to change.',
  'ew.pomyHeroSub': 'Plain words work. I’ll show you the exact change and its cost before anything is applied — nothing happens without your OK.',
  'ew.chipHeroText': 'Change the main headline',
  'ew.chipToEnglish': 'Translate my app to English',
  'ew.chipAddSection': 'Add a section',
  'ew.chipImproveDesign': 'Improve the design',
  'ew.chipColors': 'Change the colors',
  'ew.chipReviews': 'Add reviews from my customers',
  'ew.chipLaunchReady': 'Make it launch-ready',
  'ew.gateSigninT': 'Sign in to edit this app',
  'ew.gateSigninB': 'Editing is free and safe: every change lands on a private draft — your live app only updates when you publish.',
  'ew.gateAskPomy': 'Ask Pomy instead',
  'ew.gateCap1': 'Name, tagline and colors',
  'ew.gateCap2': 'Light / dark appearance and tab names',
  'ew.gateCap3': 'Your items, prices and photos',
  'ew.gateCap4': 'One-element edits with the Magic Wand',
  'ew.gateForbiddenT': 'This app belongs to another builder',
  'ew.gateBuildOwn': 'Build your own',
  'ew.gateUnsupportedT': 'Structured editing isn’t available for this app',
  'ew.gateErrorT': 'We couldn’t open your app for editing yet',
  'ew.gateErrorB': 'Your app was built, but it didn’t load in time. Try again in a moment — nothing was lost.',
  'ew.backToApp': 'Back to your app',
  'ew.tabRenameSub': 'Tap to rename',
  'ew.stageLive': 'Live preview · edits land on your draft',
  'ew.shareSheetT': 'Share your app',
  'ew.styleGroup': 'Style',
  'ew.styleModern': 'Modern',
  'ew.styleEditorial': 'Editorial',
  'ew.styleRounded': 'Rounded',
  'ew.styleNote': 'Applied by Pomy as a draft revision — uses revise credits.',
  'ew.stylePromptModern': 'Restyle the whole app with a modern, clean direction — crisp type, generous spacing. Keep every piece of content and data exactly as it is.',
  'ew.stylePromptEditorial': 'Restyle the whole app with an editorial direction — refined, magazine-like hierarchy and elegant headings. Keep every piece of content and data exactly as it is.',
  'ew.stylePromptRounded': 'Restyle the whole app with a friendly, rounded direction — softer corners and an approachable feel. Keep every piece of content and data exactly as it is.',
  'ew.saveField': 'Save',
  'ew.blocksGroup': 'Sections',
  'ew.wandBannerT': 'Point at anything.',
  'ew.wandBannerB': 'Turn on the wand, then tap any part of the preview — a title, a button, an image — and edit just that piece. No layers, no code.',
  'ew.wandHelp': 'When armed, hover highlights each element. Tap to select.',
  'ew.wandYouCan': 'You can edit',
  'ew.wandCanTextT': 'Text',
  'ew.wandCanTextS': 'Rewrite, shorten, translate',
  'ew.wandCanColorT': 'Color & style',
  'ew.wandCanColorS': 'Recolor or restyle any element',
  'ew.wandCanImageT': 'Images',
  'ew.wandCanImageS': 'Swap a photo or generate a new one',
  'ew.wandCanSizeT': 'Size & spacing',
  'ew.wandCanSizeS': 'Make things bigger or tighter',
  'ew.pomyTry': 'Try',
  'ew.tabWandShort': 'Wand',
  'ew.modeAutoShort': 'Auto',
  'ew.teachCustomize': 'Quick edits — colors, light/dark, name, tabs & appearance. Instant and free.',
  'ew.stylePack': 'Style',
  'ew.styleMigrateOffer': 'This app has no style yet. Try the one we\u2019d pick for it?',
  'ew.styleMigrateApply': 'Apply this style',
  'ew.styleMigrateUndoHint': 'You can undo this at any time \u2014 your content and data never change.',
  'ew.styleApplied': 'Style applied — your content and data are unchanged.',
  'ew.customizeHint': 'Tap any control to edit it — or tap the Magic Wand to point at one part of your app.',
  'ew.customizeHintList': 'Tap any control below to edit it — colors, theme, name, tabs and more.',
  'ew.tabShare': 'Share',
  'ew.teach': 'Ask Pomy for bigger changes · Use the Magic Wand to edit one part',
  'ew.statusLive': 'Live · published version',
  'ew.statusDraft': 'Editing draft — your live app is unchanged',
  'ew.unpublished': 'Unpublished changes',
  'ew.publish': 'Publish changes',
  'ew.publishing': 'Publishing…',
  'ew.published': 'Published — everyone now sees this version.',
  'ew.publishFailed': 'Couldn’t publish. Your draft is safe — try again.',
  // §V2 publish sheet — the go-live moment's honest states
  'ew.pubT': 'Publishing your app…',
  'ew.pubB': 'Promoting your draft to the live app your users see. This takes a few seconds.',
  'ew.pubOkT': 'You’re live',
  'ew.pubOkB': '{name} is on the web. Send the link to your first five users — that’s how apps find their people.',
  'ew.pubLinkK': 'LINK',
  'ew.pubCopied': 'Link copied',
  'ew.pubOpen': 'Open live app',
  'ew.pubBack': 'Back to editing',
  'ew.pubErrT': 'Publish didn’t finish',
  'ew.pubErrB': 'Something interrupted the promotion. Your draft is safe and nothing was lost — give it another go.',
  'ew.pubRetry': 'Try again',
  'ew.discard': 'Discard draft',
  'ew.discardConfirm': 'Throw away the draft changes? Your live app stays as it is.',
  'ew.discarded': 'Draft discarded — back to the live version.',
  'ew.previewingDraft': 'Previewing your draft',
  'ew.previewingLive': 'Previewing the live version',
  'ew.wandHint': 'Click any part of the preview — or pick one below — and change just that piece. The rest stays exactly the same.',
  'ew.wandPick': 'Pick a part to edit',
  'ew.wandSelected': 'Selected',
  'ew.wandFreeInstant': 'Instant edit · free',
  'ew.identityGroup': 'Your app',
  'ew.appName': 'App name',
  'ew.tagline': 'Tagline',
  'ew.accent': 'Accent color',
  'ew.accentCustom': 'Custom color (hex)',
  'ew.apply': 'Apply',
  'ew.applied': 'Done — only that part changed.',
  'ew.applyFailed': 'That change was rejected — nothing was lost.',
  'ew.actionFailed': "That didn't go through — please try again.",
  'ew.removeItem': 'Remove this item',
  'ew.addItem': 'Add item',
  'ew.newItem': 'New item',
  'ew.itemSaved': 'Saved. The preview shows your draft.',
  'ew.userRecordNote': 'That’s a customer record, not app content. Manage it from your app’s dashboard.',
  'ew.noAddress': 'I can’t edit that piece directly. Describe the change and I’ll apply it as an AI revision.',
  'ew.aiEditPlaceholder': 'Describe the change for this element…',
  'ew.aiEditGo': 'Apply with AI',
  'ew.aiEditNote': 'Bigger change — applied as a revision (drafts first, 10 credits).',
  // §node-edit — targeted per-element edits (text/image/visibility/style/action)
  'ew.nodeTargetedNote': 'Direct edit — only this element changes. Instant · free.',
  'ew.nodeTextPlaceholder': 'New text for this element…',
  'ew.nodeSaveText': 'Save text',
  'ew.nodeReplaceImage': 'Replace image',
  'ew.nodeTextColor': 'Text color',
  'ew.nodeLink': 'Link target',
  'ew.nodeLinkPlaceholder': 'Screen or link id…',
  'ew.nodeHide': 'Hide this',
  'ew.nodeHidden': 'Hidden — only that element changed.',
  'ew.pomyIntro': 'I’m Pomy — tell me how the whole app should change. Direction, tone, structure, new powers. For one specific element, use the Magic Wand.',
  'ew.pomyPlaceholder': 'e.g. Make the whole app feel more premium…',
  'ew.pomyApply': 'Apply change',
  'ew.pomyApplying': 'Applying your change…',
  'ew.pomyChipPremium': 'Make it feel more premium',
  'ew.pomyChipTone': 'Warmer, friendlier copy',
  'ew.pomyChipWhatCanI': 'What can I edit?',
  'ew.pomyChipHebrew': 'Translate my app to Hebrew',
  'ew.editUnavailable': 'This app was built before editing — ask Pomy for a change instead.',
  'ew.loadingSurface': 'Reading your app…',
  'ew.notSignedIn': 'Sign in to edit this app.',
  // §S6 — ownership self-heal (a guest-built app claimed on sign-in)
  'ew.claimTitle': 'Make this app yours',
  'ew.claimBody': 'You built this app before signing in. Claim it to your account to edit and publish it.',
  'ew.claimCta': 'Claim & edit',
  'ew.claiming': 'Claiming…',
  'ew.notYourApp': 'This app belongs to another account, so it can’t be edited here.',
  'ew.wandNotReady': 'Click-to-pick isn’t ready on this app yet — pick a part from the list below.',
  'ew.versionRestoreDraft': 'Loaded as a draft — publish to make it live.',
  'ew.openLive': 'Open',
  'ew.buildAnother': 'Build another',
  'ew.history': 'History',
  'ew.refreshPreview': 'Refresh preview',
  'ew.badgeHidden': 'Badge hidden',
  'ew.removeBadge': 'Remove Pomela badge',
  'ew.exportCode': 'Export code',
  'ew.customDomain': 'Custom domain',
  // §S680 — Magic Wand picking mode + deeper editing workspace
  'ew.wandArm': 'Point & click',
  'ew.wandArmed': 'Picking is on — tap any part of your app',
  'ew.wandExit': 'Done picking',
  'ew.wandEsc': 'Esc to exit',
  'ew.wandListOnly': 'Pick a part below to edit it.',
  'ew.pickHeading': 'Heading',
  'ew.pickText': 'Text',
  'ew.pickImage': 'Image',
  'ew.pickButton': 'Button',
  'ew.pickLink': 'Link',
  'ew.pickSection': 'Section',
  'ew.appearance': 'Appearance',
  'ew.modeAuto': 'Match device',
  'ew.modeLight': 'Light',
  'ew.modeDark': 'Dark',
  'ew.tabsGroup': 'Menu tabs',
  'ew.tabRename': 'Rename tab',
  'ew.moveUp': 'Move up',
  'ew.moveDown': 'Move down',
  'ew.photoLabel': 'Photo',
  'ew.photoUrlPlaceholder': 'https:// image link',
  'ew.photoUpload': 'Upload photo',
  'ew.photoUploading': 'Uploading…',
  'ew.photoRemove': 'Remove photo',
  'ew.teachWand': 'Point at any part of your app and change just that piece — instant and free.',
  'ew.teachPomy': 'Tell Pomy how the whole app should change — it applies as a draft.',
  'ew.teachShare': 'Share your live link and grow.',
  'ew.pomySelChip': 'Pointing at: {label}',
  'ew.pomyAskAbout': 'Ask Pomy about this',
  'ew.pomyApplyCard': 'Pomy will apply:',
  'ew.pomyCost': '10 credits · rebuilds as a draft',
  // §Pomy-routing — a targeted node edit Pomy resolved from the chat: op labels,
  // its FREE credit class, and the applied state.
  'ew.pomyNodeFree': 'Free · instant · updates your draft',
  'ew.pomyApplied': 'Applied to your draft',
  'ew.pomyAppliedTag': 'Applied',
  'ew.pomyOpText': 'Change the text to',
  'ew.pomyOpImage': 'Change the image to',
  'ew.pomyOpLink': 'Change the link to',
  'ew.pomyOpHide': 'Hide this element',
  'ew.pomyOpShow': 'Show this element',
  'ew.pomyOpColor': 'Recolor to {color}',
  'ew.pomyOpStyle': 'Restyle this element',
  'ew.pomyRate': 'Pomy is popular right now — try again in a few seconds.',
  'ew.pomySignin': 'Sign in to chat with Pomy.',
  'ew.pomyRetry': 'Retry',
  'ew.publishConfirmT': 'Publish to your customers?',
  'ew.publishConfirmB': 'Everyone who opens your live link will get this version.',
  'ew.publishGo': 'Publish',
  'ew.discardConfirmT': 'Discard this draft?',
  'ew.discardConfirmB': 'Your live app stays exactly as it is. The draft changes are deleted.',
  'ew.discardGo': 'Discard draft',
  'ew.keepEditing': 'Keep editing',
  'ew.reviseOverlayT': 'Rebuilding your app…',
  'ew.reviseOverlayB': 'Pomy is applying the change. The preview refreshes when the draft is ready.',
  // §S680 nav registry (lib/nav-items.ts) + CommandPalette rows
  'nav.help': 'Support',
  'cmd.helpHint': 'Message the team or copy the support email',
  'cmd.settingsHint': 'Theme, language and app preferences',
  'cmd.accountHint': 'Your plan, credits and sign-in',
  // §S679 owner morning view (TodayOverview)
  'ov.title': 'Today',
  'ov.updated': 'Updated {t}',
  'ov.time.now': 'just now',
  'ov.time.m': '{n}m ago',
  'ov.time.h': '{n}h ago',
  'ov.time.d': '{n}d ago',
  'ov.newBookings': 'New bookings today',
  'ov.newOrders': 'New orders today',
  'ov.pendingActions': 'Pending actions',
  'ov.unavailable': 'Activity data isn’t connected yet',
  'ov.quietTitle': 'Quiet so far today',
  'ov.quietText': 'Share your app to get it in front of people.',
  'ov.capped': 'Counts reflect your most recent activity.',
  // §S680 — Workspace status truth + MyApps chrome
  'wsx.stLive': 'Live',
  'wsx.stDraft': 'Draft pending',
  'wsx.stPreview': 'Preview',
  'wsx.stPublic': 'Public',
  'wsx.fDraft': 'Draft',
  'wsx.newApp': 'New app',
  'wsx.kInstalls': 'installs',
  'wsx.kViews': 'views',
  'wsx.evInstalls': 'New installs',
  'wsx.evUpdated': 'Updated',
  'wsx.evCreated': 'Created',
  'wsx.versionsN': '{n} versions',
  'wsx.installsN': '{n} installs',
  'wsx.activityEmpty': 'Activity from your apps shows here.',
  'wsx.across1': 'across 1 app',
  'wsx.acrossN': 'across {n} apps',
  'wsx.noInstalls': 'No installs yet — real install counts appear here once people add your apps.',
  'wsx.startCategory': 'Start from a category',
  'wsx.catStore': 'Store',
  'wsx.catBooking': 'Booking',
  'wsx.catTracker': 'Tracker',
  'wsx.catCrm': 'CRM',
  'wsx.guideTitle': 'New here? Build your first app',
  'wsx.guideSub': '2-min guide',
  // §S680 — App Detail sections + management panels
  'ad.secOverview': 'Overview',
  'ad.secManage': 'Manage',
  'ad.secShare': 'Share & publish',
  'ad.openLive': 'Open live',
  'ad.shareSub': 'Send your app’s link to customers or copy it to your clipboard.',
  'ad.mgBookingT': 'Manage bookings',
  'ad.mgBookingS': 'Review, confirm and reschedule customer appointments.',
  'ad.mgCommerceT': 'Products & orders',
  'ad.mgCommerceS': 'Manage your catalog and fulfil customer orders.',
  'ad.mgOrderT': 'Menu & orders',
  'ad.mgOrderS': 'Update the menu and track pickup orders.',
  'ad.mgCrmT': 'Leads & pipeline',
  'ad.mgCrmS': 'Work your deals and follow-ups.',
  'ad.mgTrackerT': 'Entries & analytics',
  'ad.mgTrackerS': 'Review logged entries and trends.',
  'ad.mgDirectoryT': 'Listings & moderation',
  'ad.mgDirectoryS': 'Approve submissions and curate listings.',
  'ad.mgCommunityT': 'Posts & moderation',
  'ad.mgCommunityS': 'Moderate community content.',
  'ad.mgAiT': 'Prompts & outputs',
  'ad.mgAiS': 'Review jobs and generated outputs.',
  'ad.notWired': 'Not wired yet',
  'ad.notWiredHint': 'Needs a server-backed management API',
  'ad.discAssets': 'Asset library',
  'ad.discAssetsS': 'Images and files this app can use.',
  'ad.discWaitlist': 'Waitlist',
  'ad.discWaitlistS': 'People waiting for a spot.',
  'ad.discActivity': 'Activity log',
  'ad.discActivityS': 'Who did what in this app.',
  'ad.discWebhooks': 'Webhooks',
  'ad.discWebhooksS': 'Send app events to Zapier, Make or your own endpoint.',
  'mgmt.wh.intro': 'Get a signed POST to your URL whenever a chosen event happens (new order, booking, RSVP…). Works with Zapier, Make, or any HTTPS endpoint.',
  'mgmt.wh.url': 'Endpoint URL (HTTPS)',
  'mgmt.wh.enabled': 'Deliveries enabled',
  'mgmt.wh.events': 'Events (none selected = all)',
  'mgmt.wh.secret': 'Signing secret',
  'mgmt.wh.secretHint': 'Verify the X-Pomela-Signature header (HMAC-SHA256 of the body) with this secret. Keep it private.',
  'mgmt.wh.save': 'Save',
  'mgmt.wh.saving': 'Saving…',
  'mgmt.wh.saved': 'Saved',
  'mgmt.wh.test': 'Send test',
  'mgmt.wh.testing': 'Sending…',
  'mgmt.wh.testOk': 'Test delivered',
  'mgmt.wh.testFail': 'Test failed',
  'mgmt.wh.deliveries': 'Recent deliveries',
  'mgmt.wh.none': 'No deliveries yet.',
  'mgmt.wh.err': 'Something went wrong — please try again.',
  'mgmt.wh.unavailable': 'Webhooks are available once this app is saved to your account.',
  'ov.attnTitle': 'Needs attention',
  'ov.attn.booking1': '1 booking request waiting',
  'ov.attn.bookingN': '{n} booking requests waiting',
  'ov.attn.order1': '1 new order',
  'ov.attn.orderN': '{n} new orders',
  'ov.attn.wait1': '1 waitlist signup',
  'ov.attn.waitN': '{n} waitlist signups',
  'ov.activityTitle': 'Recent activity',
  'ov.ev.shared': 'Shared',
  'ov.ev.installed': 'Installed',
  'ov.ev.opened': 'Opened from a shared link',
  // §S679 — App Detail (health + capability preview + chrome)
  'ad.justNow': 'just now',
  'ad.minAgo': '{n}m ago',
  'ad.hourAgo': '{n}h ago',
  'ad.dayAgo': '{n}d ago',
  'ad.healthTitle': 'App health',
  'ad.hLive': 'Live and serving',
  'ad.hPreview': 'Preview — publish to make it live',
  'ad.hUpdated': 'updated',
  'ad.hInstall': 'Installable on phones (Add to Home Screen)',
  'ad.hPublic': 'Share link is on — anyone with it can open the app',
  'ad.hPrivate': 'Private — only people with the link can open it',
  'ad.hServerData': 'Customer records save to your secure workspace',
  'ad.hLocalData': 'Works offline — data stays on each user’s device',
  'ad.hRoles': 'Owner and customer views are kept separate',
  'ad.hDraft': 'Unpublished draft changes waiting',
  'ad.healthFoot': 'Every line here reflects this app’s real state — nothing estimated.',
  'ad.capTitle': 'What this app includes',
  'ad.cBookings': 'Booking requests',
  'ad.cAvailability': 'Availability control',
  'ad.cCustomers': 'Customer list',
  'ad.cPaymentLink': 'Payment link (manual confirm)',
  'ad.cWaitlist': 'Waitlist',
  'ad.cCsv': 'CSV export',
  'ad.cCatalog': 'Product catalog',
  'ad.cCart': 'Cart & checkout',
  'ad.cOrders': 'Orders',
  'ad.cMenu': 'Menu',
  'ad.cEvents': 'Events',
  'ad.cRsvps': 'RSVPs',
  'ad.cInstallShare': 'Install & share',
  'ad.cEditable': 'Editable with Pomy & Magic Wand',
  'ad.nCardCapture': 'No built-in card payments yet — you confirm payments manually',
  'ad.open': 'Open',
  'ad.edit': 'Edit',
  'ad.share': 'Share',
  'ad.live': 'Live',
  'ad.preview': 'Preview',
  'ad.views': 'Views',
  'ad.installs': 'Installs',
  'ad.interactions': 'Interactions',
  'ad.creditsUsed': 'Credits used',
  'ad.analyticsNote': 'Analytics appear here once this app gets visits. Numbers are real per-app metrics, never estimated.',
  'ad.mgmt': 'App management',
  'ad.notFound': 'App not found',
  'ad.notFoundSub': 'You don’t have access to this app, or it doesn’t exist. Open Workspace to see the apps you own.',
  'ad.backToWorkspace': 'Back to Workspace',
  // §S679 — PublishControls (share link on/off)
  'pub.title': 'Share link',
  'pub.liveTitle': 'Share link is on',
  'pub.liveSub': 'Anyone with the link can open your app.',
  'pub.privateTitle': 'Private — link only',
  'pub.privateSub': 'Only people with the link can use your app. Turn on the share link to get a short address you can hand out.',
  'pub.publish': 'Turn on share link',
  'pub.publishing': 'Turning on…',
  'pub.unpublish': 'Turn off',
  'pub.unpublishing': 'Turning off…',
  'pub.liveBadge': 'Live',
  'pub.copy': 'Copy',
  'pub.copied': 'Copied',
  'pub.ariaCopy': 'Copy the public share link',
  'pub.errPublish': 'Turning on the share link didn’t go through. Check your connection and try again.',
  'pub.errUnpublish': 'Turning off the share link didn’t go through. Check your connection and try again.',
  'pub.errLoad': 'Couldn’t load this app’s share link status.',
  'pub.retry': 'Try again',
  // §S680 — owner management panels (App Detail): shared chrome
  'mgmt.refresh': 'Refresh',
  // §S2 — Owner Order Console (contract-driven ManageRecords). Order-lifecycle + payment labels.
  'mgmt.ord.title': 'Pickup orders',
  // §Owner-console generalization — per-collection console titles (contract-driven ManageRecords).
  'mgmt.tickets.title': 'Support tickets',
  'mgmt.items.title': 'Inventory',
  'mgmt.adjustments.title': 'Stock adjustments',
  'mgmt.attempts.title': 'Quiz attempts',
  'mgmt.rsvps.title': 'RSVPs',
  'mgmt.timeoff.title': 'Time-off requests',
  'mgmt.employees.title': 'Team roster',
  'mgmt.listings.title': 'Listings',
  'mgmt.submissions.title': 'Submissions to review',
  'mgmt.rec.exportCsv': 'Export CSV',
  'mgmt.rec.emptyT': 'No orders yet',
  'mgmt.rec.emptyS': 'New pickup orders show up here — accept them, advance the prep status, and mark them paid.',
  'mgmt.rec.emptyRecordsT': 'Nothing here yet',
  'mgmt.rec.emptyRecordsS': 'Records show up here as they are added.',
  'mgmt.rec.errAuthRecords': 'Sign in as the owner to manage these records.',
  'mgmt.rec.unavailableRecordsS': 'This list turns on once this app has its data backend.',
  'mgmt.rec.errT': "Couldn't load",
  'mgmt.rec.errAuth': 'Sign in as the owner to manage orders.',
  'mgmt.rec.errGeneric': 'Something went wrong. Try again.',
  'mgmt.rec.unavailableT': 'Not available yet',
  'mgmt.rec.unavailableS': 'Order management turns on once this app has its data backend.',
  'mgmt.rec.order.placed': 'New order',
  'mgmt.rec.order.preparing': 'Preparing',
  'mgmt.rec.order.ready': 'Ready for pickup',
  'mgmt.rec.order.completed': 'Completed',
  'mgmt.rec.order.cancelled': 'Cancelled',
  'mgmt.pay.awaiting_payment': 'Awaiting payment',
  'mgmt.pay.external_pending': 'Payment pending',
  'mgmt.pay.pay_at_business_pending': 'Pay at pickup',
  'mgmt.pay.marked_paid': 'Paid',
  'mgmt.pay.payment_canceled': 'Payment canceled',
  'mgmt.pay.refunded_manual': 'Refunded',
  'mgmt.saving': 'Saving…',
  'mgmt.saved': 'Saved',
  'mgmt.errConn': 'Connection lost. Check your network and try again.',
  'mgmt.errConnShort': 'Connection lost. Try again.',
  'mgmt.time.justNow': 'just now',
  'mgmt.time.m': '{n}m ago',
  'mgmt.time.h': '{n}h ago',
  'mgmt.time.d': '{n}d ago',
  // ── SEP-2 / E0 — shared owner-workspace primitive chrome (category-agnostic; every owner console
  // reuses these: ManageEntity / EntityFormSheet / EntityMediaField / OwnerAnalyticsPanel /
  // StateTransitionBar / DestructiveActionGuard / ConsoleSection). NO shop-specific copy here. ──
  'own.add': 'Add',
  'own.addTitle': 'Add new',
  'own.editTitle': 'Edit',
  'own.save': 'Save',
  'own.saving': 'Saving…',
  'own.cancel': 'Cancel',
  'own.close': 'Close',
  'own.edit': 'Edit',
  'own.archive': 'Archive',
  'own.archiveAria': 'Archive {entity} — you can restore it later',
  'own.restore': 'Restore',
  'own.restoreAria': 'Restore {entity}',
  'own.archived': 'Archived',
  'own.undo': 'Undo',
  'own.undoArchiveAria': 'Undo — restore {entity}',
  'own.delete': 'Delete',
  'own.deleteAria': 'Delete {entity} permanently',
  'own.deleteConfirm': 'Yes, delete',
  'own.confirmDeleteT': 'Delete {entity}?',
  'own.confirmDeleteS': 'This permanently removes {entity} and can’t be undone. Archiving keeps it hidden but recoverable.',
  'own.showArchived': 'Show archived ({n})',
  'own.hideArchived': 'Hide archived',
  'own.required': 'Required',
  'own.invalidNumber': 'Enter a number',
  'own.invalidRecord': 'Some details are missing or invalid. Check the fields and try again.',
  'own.fixErrors': 'Please fix the highlighted fields.',
  'own.saveFailed': 'Couldn’t save. Check your connection and try again.',
  'own.emptyT': 'Nothing here yet',
  'own.emptyS': 'New entries will appear here.',
  'own.errT': 'Couldn’t load',
  'own.errAuth': 'Sign in as the owner to manage this.',
  'own.errGeneric': 'Something went wrong. Try again.',
  'own.unavailableT': 'Not available yet',
  'own.unavailableS': 'This turns on once the app has its data backend.',
  'own.transitionsAria': 'Change status',
  'own.transitionTo': 'Move {entity} to {state}',
  'own.thisItem': 'this item',
  'own.mediaUpload': 'Upload image',
  'own.mediaReplace': 'Replace image',
  'own.mediaRemove': 'Remove',
  'own.mediaUploading': 'Uploading…',
  'own.mediaEmpty': 'No image yet',
  'own.mediaUploadFailed': 'Upload failed — try again.',
  'own.mediaAlt': 'Image description (alt text)',
  'own.mediaAltHint': 'Describe the image for screen readers and search.',
  'own.mediaAltRequired': 'Add a short description so the image is accessible.',
  'own.analyticsEmptyT': 'No data yet',
  'own.analyticsEmptyS': 'Real numbers appear here as soon as there’s activity.',
  'own.analyticsErrAuth': 'Sign in as the owner to view analytics.',
  // ── SEP-2 / E1 — SHOP owner consoles (products / inventory / CRM / analytics). Category-true copy. ──
  // Products
  'shop.pr.title': 'Products',
  'shop.pr.add': 'Add product',
  'shop.pr.addTitle': 'Add a product',
  'shop.pr.editTitle': 'Edit product',
  'shop.pr.emptyT': 'No products yet',
  'shop.pr.emptyS': 'Add your first product — a name, a price, and a photo is all it takes to start selling.',
  'shop.pr.unavailableT': 'Products aren’t available yet',
  'shop.pr.errAuth': 'Sign in as the owner to manage your products.',
  'shop.pr.fTitle': 'Product name',
  'shop.pr.fTitlePh': 'e.g. Handmade ceramic mug',
  'shop.pr.fPrice': 'Price',
  'shop.pr.fPriceHelp': 'What a customer pays for one.',
  'shop.pr.fSku': 'SKU',
  'shop.pr.fSkuPh': 'e.g. MUG-001',
  'shop.pr.fCategory': 'Category',
  'shop.pr.fStock': 'In stock',
  'shop.pr.fStockHelp': 'How many you have to sell right now.',
  'shop.pr.fVariants': 'Variants',
  'shop.pr.fVariantsPh': 'Small, Medium, Large',
  'shop.pr.fVariantsHelp': 'Separate options with commas.',
  'shop.pr.fDesc': 'Description',
  'shop.pr.fDescPh': 'What makes this product special.',
  'shop.pr.fMedia': 'Product photo',
  'shop.pr.outOfStock': 'Out of stock',
  'shop.pr.lowStock': 'Low · {n} left',
  'shop.pr.inStock': '{n} in stock',
  'shop.pr.hidden': 'Hidden',
  'shop.pr.visible': 'Visible',
  'shop.pr.untitled': 'Untitled product',
  'shop.pr.thisProduct': 'this product',
  'shop.pr.showToCustomersAria': 'Show {title} to customers',
  'shop.pr.hideFromCustomersAria': 'Hide {title} from customers',
  'shop.pr.editAria': 'Edit {title}',
  // Inventory
  'shop.inv.title': 'Inventory',
  'shop.inv.add': 'Add item',
  'shop.inv.addTitle': 'Add an inventory item',
  'shop.inv.editTitle': 'Edit item',
  'shop.inv.emptyT': 'Nothing tracked yet',
  'shop.inv.emptyS': 'Add the items you keep stock of — you’ll see on-hand counts and get low-stock alerts.',
  'shop.inv.unavailableT': 'Inventory isn’t available yet',
  'shop.inv.errAuth': 'Sign in as the owner to manage stock.',
  'shop.inv.fName': 'Item name',
  'shop.inv.fNamePh': 'e.g. Ceramic mug — blue',
  'shop.inv.fSku': 'SKU',
  'shop.inv.fCategory': 'Category',
  'shop.inv.fCount': 'On hand',
  'shop.inv.fCountHelp': 'How many you physically have now.',
  'shop.inv.fReorder': 'Reorder point',
  'shop.inv.fReorderHelp': 'Warn me when stock drops to this.',
  'shop.inv.fUnit': 'Unit',
  'shop.inv.fUnitPh': 'e.g. pcs, kg',
  'shop.inv.fLocation': 'Location',
  'shop.inv.adjType': 'Adjustment',
  'shop.inv.receive': 'Receive (add)',
  'shop.inv.recount': 'Recount (set)',
  'shop.inv.writeOff': 'Write off (remove)',
  'shop.inv.quantity': 'Quantity',
  'shop.inv.quantityHelp': 'For a recount, this is the new total.',
  'shop.inv.note': 'Note',
  'shop.inv.notePh': 'e.g. Delivery from supplier',
  'shop.inv.adjust': 'Adjust stock',
  'shop.inv.adjustAria': 'Adjust stock for {name}',
  'shop.inv.adjustTitle': 'Adjust stock',
  'shop.inv.applyAdjust': 'Apply adjustment',
  'shop.inv.adjustFailed': 'Couldn’t record that adjustment. Nothing changed — try again.',
  'shop.inv.adjustPartial': 'The adjustment was logged but the on-hand count didn’t update. Refresh and check.',
  'shop.inv.discontinued': 'Discontinued',
  'shop.inv.out': 'Out of stock',
  'shop.inv.low': 'Low stock',
  'shop.inv.inStock': 'In stock',
  'shop.inv.untitled': 'Untitled item',
  'shop.inv.thisItem': 'this item',
  'shop.inv.onHand': '{n} {unit} on hand',
  'shop.inv.reorderAlert': 'At or below reorder point ({rp}) — time to restock',
  'shop.inv.resulting': '→ {n} on hand',
  'shop.inv.historyTitle': 'Stock adjustments',
  'shop.inv.historyEmptyT': 'No adjustments yet',
  'shop.inv.historyEmptyS': 'Every stock change you make is logged here — receipts, recounts, and write-offs.',
  'shop.inv.st.in_stock': 'In stock',
  'shop.inv.st.low_stock': 'Low stock',
  'shop.inv.st.out_of_stock': 'Out of stock',
  'shop.inv.st.discontinued': 'Discontinue',
  // CRM
  'shop.crm.contactsTitle': 'Customers',
  'shop.crm.contactsEmptyT': 'No customers yet',
  'shop.crm.contactsEmptyS': 'Add the people you do business with — leads, prospects, and customers.',
  'shop.crm.addContact': 'Add customer',
  'shop.crm.addContactTitle': 'Add a customer',
  'shop.crm.editContactTitle': 'Edit customer',
  'shop.crm.dealsTitle': 'Deals',
  'shop.crm.dealsEmptyT': 'No deals yet',
  'shop.crm.dealsEmptyS': 'Track opportunities from lead to won — add your first deal.',
  'shop.crm.addDeal': 'Add deal',
  'shop.crm.addDealTitle': 'Add a deal',
  'shop.crm.editDealTitle': 'Edit deal',
  'shop.crm.errAuth': 'Sign in as the owner to manage customers.',
  'shop.crm.dStageNote': 'Pick a deal’s stage when you add it. Moving deals through the pipeline after that is coming soon.',
  'shop.crm.cName': 'Name',
  'shop.crm.cNamePh': 'e.g. Dana Levi',
  'shop.crm.cCompany': 'Company',
  'shop.crm.cEmail': 'Email',
  'shop.crm.cPhone': 'Phone',
  'shop.crm.cRelationship': 'Relationship',
  'shop.crm.cTags': 'Tags',
  'shop.crm.cTagsPh': 'vip, wholesale',
  'shop.crm.cNotes': 'Notes',
  'shop.crm.dTitle': 'Deal name',
  'shop.crm.dTitlePh': 'e.g. Wholesale order — Café Nine',
  'shop.crm.dStage': 'Stage',
  'shop.crm.dStageHelp': 'Where this deal is in your pipeline.',
  'shop.crm.dValue': 'Value',
  'shop.crm.dContact': 'Contact',
  'shop.crm.dContactHelp': 'Link this deal to a customer.',
  'shop.crm.dCloseDate': 'Expected close',
  'shop.crm.dNotes': 'Notes',
  'shop.crm.rel.Lead': 'Lead',
  'shop.crm.rel.Prospect': 'Prospect',
  'shop.crm.rel.Customer': 'Customer',
  'shop.crm.rel.Churned': 'Churned',
  'shop.crm.stage.Lead': 'Lead',
  'shop.crm.stage.Qualified': 'Qualified',
  'shop.crm.stage.Proposal': 'Proposal',
  'shop.crm.stage.Won': 'Won',
  'shop.crm.stage.Lost': 'Lost',
  'shop.crm.unnamed': 'Unnamed contact',
  'shop.crm.thisContact': 'this customer',
  'shop.crm.untitledDeal': 'Untitled deal',
  'shop.crm.thisDeal': 'this deal',
  // CRM activity timeline (TRACK C)
  'shop.crm.activityTitle': 'Activity',
  'shop.crm.activityEmptyT': 'No activity yet',
  'shop.crm.activityEmptyS': 'Deal moves, new customers, orders and bookings show up here as they happen.',
  'shop.crm.actUnknown': 'Unrecognized activity',
  'shop.crm.actor.owner': 'You',
  'shop.crm.actor.customer': 'Customer',
  'shop.crm.actor.system': 'System',
  'shop.crm.act.contact_created': 'Customer added',
  'shop.crm.act.contact_updated': 'Customer updated',
  'shop.crm.act.contact_archived': 'Customer archived',
  'shop.crm.act.contact_restored': 'Customer restored',
  'shop.crm.act.deal_created': 'Deal created',
  'shop.crm.act.deal_stage_changed': 'Deal stage changed',
  'shop.crm.act.deal_won': 'Deal won',
  'shop.crm.act.deal_lost': 'Deal lost',
  'shop.crm.act.deal_archived': 'Deal archived',
  'shop.crm.act.order_placed': 'Order placed',
  'shop.crm.act.order_status_changed': 'Order status changed',
  'shop.crm.act.booking_requested': 'Booking requested',
  'shop.crm.act.booking_status_changed': 'Booking status changed',
  'shop.crm.act.note_added': 'Note added',
  // Shop analytics
  'shop.an.title': 'Shop analytics',
  'shop.an.revenue': 'Revenue',
  'shop.an.revenueHint': 'excl. cancelled',
  'shop.an.orders': 'Orders',
  'shop.an.paid': 'Paid',
  'shop.an.lowStock': 'Low stock',
  'shop.an.topProducts': 'Top products by revenue',
  'shop.an.source': 'From {orders} real orders · {items} tracked items',
  'shop.an.emptyT': 'No sales data yet',
  'shop.an.emptyS': 'Once customers place orders, your revenue, top products, and stock alerts show up here.',
  // ── SEP-2 / F — Booking owner workspace (services / booking analytics / staff &
  //    resources). Additive, category-specific keys built on the shared own.* set.
  'svc.title': 'Services',
  'svc.add': 'Add service',
  'svc.addTitle': 'Add service',
  'svc.editTitle': 'Edit service',
  'svc.untitled': 'Untitled service',
  'svc.thisService': 'this service',
  'svc.free': 'Free',
  'svc.hidden': 'Hidden',
  'svc.bookable': 'Bookable',
  'svc.emptyT': 'No services yet',
  'svc.emptyS': 'Add the services customers can book — a name, how long it takes, and the price. They appear here and on your live app.',
  'svc.unavailableT': 'Services aren’t set up yet',
  'svc.unavailableS': 'This turns on once the app has its data backend.',
  'svc.errAuth': 'Sign in as the app owner to manage services.',
  'svc.fName': 'Service name',
  'svc.fNamePh': 'e.g. Haircut, 30-min consult',
  'svc.fPrice': 'Price',
  'svc.fPriceHelp': 'Leave blank if this service is free.',
  'svc.fDuration': 'Duration (minutes)',
  'svc.fDurationHelp': 'How long each appointment lasts.',
  'svc.fBuffer': 'Buffer after (minutes)',
  'svc.fBufferHelp': 'Extra gap after each appointment before the next can start.',
  'svc.fBooking': 'When a customer books',
  'svc.fBookingPh': 'Choose…',
  'svc.fBookingHelp': 'Auto-confirm accepts requests instantly. Review lets you approve each one first.',
  'svc.bookingAuto': 'Confirm automatically',
  'svc.bookingManual': 'Review each request',
  'svc.fDesc': 'Description',
  'svc.fDescPh': 'What’s included, what to expect…',
  'svc.fMedia': 'Photo',
  'svc.minShort': '{n} min',
  'svc.hrShort': '{n} hr',
  'svc.hrMinShort': '{h} hr {m} min',
  'svc.bufferRow': '+{n} min buffer',
  'svc.showToCustomersAria': 'Make {name} bookable by customers',
  'svc.hideFromCustomersAria': 'Hide {name} from customers',
  'svc.editAria': 'Edit {name}',
  'bkan.title': 'Booking insights',
  'bkan.source': 'Based on {n} bookings (all time)',
  'bkan.emptyT': 'No bookings yet',
  'bkan.emptyS': 'Once customers start booking, your numbers appear here — volume, cancellations and more, from real bookings only.',
  'bkan.mTotal': 'Total bookings',
  'bkan.mConfirmed': 'Confirmed',
  'bkan.mConfirmedHint': 'upcoming',
  'bkan.mCompleted': 'Completed',
  'bkan.mCancelRate': 'Cancellation rate',
  'bkan.mNoShowRate': 'No-show rate',
  'bkan.mPaid': 'Marked paid',
  'bkan.mPaidHint': 'you marked these paid',
  'bkan.mRevenue': 'Revenue',
  'bkan.mRevenueHint': 'from bookings you marked paid',
  'bkan.byService': 'Bookings by service',
  'bkan.unnamedService': 'Appointment',
  'res.title': 'Staff & resources',
  'res.add': 'Add',
  'res.addTitle': 'Add staff or resource',
  'res.editTitle': 'Edit staff or resource',
  'res.untitled': 'Untitled',
  'res.thisResource': 'this resource',
  'res.hidden': 'Not bookable',
  'res.bookable': 'Bookable',
  'res.emptyT': 'No staff or resources yet',
  'res.emptyS': 'Add the people or things a booking is made against — a stylist, a room, a chair. Optional, but handy once you have more than one.',
  'res.unavailableT': 'Not set up yet',
  'res.unavailableS': 'This turns on once the app has its data backend.',
  'res.errAuth': 'Sign in as the app owner to manage staff and resources.',
  'res.fName': 'Name',
  'res.fNamePh': 'e.g. Alex, Room 2',
  'res.fRole': 'Role',
  'res.fRolePh': 'e.g. Senior stylist',
  'res.fRoleHelp': 'What this person or resource is, in your words.',
  'res.fServices': 'Services offered',
  'res.fServicesPh': 'Haircut, Color, Consult',
  'res.fServicesHelp': 'Type service names separated by commas.',
  'res.fNote': 'Availability notes',
  'res.fNotePh': 'e.g. Tue–Sat, mornings only',
  'res.fNoteHelp': 'Free text — a reminder for you, shown here.',
  'res.enableAria': 'Make {name} bookable',
  'res.disableAria': 'Make {name} not bookable',
  'res.editAria': 'Edit {name}',
  // booking status + payment lifecycle labels (SERVER values mapped by key —
  // an unknown raw value falls back to the raw string, never a broken label)
  'mgmt.st.requested': 'Requested',
  'mgmt.st.confirmed': 'Confirmed',
  'mgmt.st.completed': 'Completed',
  'mgmt.st.cancelled': 'Cancelled',
  'mgmt.pay.notRequired': 'No payment',
  'mgmt.pay.awaiting': 'Awaiting payment',
  'mgmt.pay.awaitingLink': 'Awaiting payment (link)',
  'mgmt.pay.payAtBusiness': 'Pay at business',
  'mgmt.pay.paid': 'Paid (manual)',
  'mgmt.pay.canceled': 'Payment canceled',
  'mgmt.pay.refunded': 'Refunded (manual)',
  'mgmt.pm.externalLink': 'Online (link)',
  'mgmt.pm.payAtBusiness': 'Pay at business',
  'mgmt.act.confirm': 'Confirm',
  'mgmt.act.decline': 'Decline',
  'mgmt.act.markDone': 'Mark done',
  'mgmt.act.cancel': 'Cancel',
  'mgmt.act.markPaid': 'Mark paid',
  'mgmt.act.cancelPayment': 'Cancel payment',
  'mgmt.act.refunded': 'Refunded',
  'mgmt.act.markUnpaid': 'Mark unpaid',
  'mgmt.act.reopen': 'Reopen',
  // Manage bookings
  'mgmt.bk.title': 'Manage bookings',
  'mgmt.bk.appointment': 'Appointment',
  'mgmt.bk.paymentRow': 'Payment:',
  'mgmt.bk.err409': 'Owner status updates aren’t enabled on the server yet (pending the owner-update policy).',
  'mgmt.bk.err404': 'That booking no longer exists. Refresh to see the latest.',
  'mgmt.bk.errUpdate': 'Couldn’t update that booking. Please try again.',
  'mgmt.bk.unavailableT': 'Bookings server not configured',
  'mgmt.bk.unavailableS': 'This environment has no Supabase connection. Bookings appear here once the app data backend is live.',
  'mgmt.bk.errT': 'Couldn’t load bookings',
  'mgmt.bk.errAuth': 'Sign in as the app owner to manage bookings.',
  'mgmt.bk.errGeneric': 'Something went wrong fetching this app’s bookings.',
  'mgmt.bk.emptyT': 'No bookings yet',
  'mgmt.bk.emptyS': 'When a customer books on your live app, it shows up here in real time — confirm, complete, or cancel it from this panel.',
  // Customers
  'mgmt.cu.title': 'Customers',
  'mgmt.cu.fallback': 'Customer',
  'mgmt.cu.active': 'Active',
  'mgmt.cu.lastSeen': 'Last seen {t}',
  'mgmt.cu.unavailableT': 'Customer store not configured',
  'mgmt.cu.unavailableS': 'This environment has no Supabase connection. Customers appear here once the app data backend is live.',
  'mgmt.cu.errT': 'Couldn’t load customers',
  'mgmt.cu.errAuth': 'Sign in as the app owner to see your customers.',
  'mgmt.cu.errGeneric': 'Something went wrong fetching this app’s customers.',
  'mgmt.cu.emptyT': 'No customers yet',
  'mgmt.cu.emptyS': 'When someone signs in to your live app, they show up here — their verified email and when they were last active. Real people, never estimates.',
  // Booking availability editor
  'mgmt.av.aria': 'Booking availability',
  'mgmt.av.title': 'Availability',
  'mgmt.av.errLoad': 'Couldn’t load availability.',
  'mgmt.av.unavailableT': 'Availability store not configured',
  'mgmt.av.unavailableS': 'This environment has no app backend connection.',
  'mgmt.av.err403': 'Only the app owner can change availability.',
  'mgmt.av.err401': 'Sign in as the app owner to edit availability.',
  'mgmt.av.errSave': 'Couldn’t save availability.',
  'mgmt.av.window': 'Customers can book up to',
  'mgmt.av.year': '1 year',
  'mgmt.av.days': '{n} days',
  'mgmt.av.weekly': 'Weekly hours',
  'mgmt.av.closed': 'Closed',
  'mgmt.av.timezone': 'Business timezone',
  'mgmt.av.timezoneHint': 'Bookings, hours and "today" use this timezone. Set it to where your business runs.',
  'mgmt.av.duration': 'Service duration (minutes)',
  'mgmt.av.blockedDates': 'Blocked dates (holidays, vacation)',
  'mgmt.av.blockDate': 'Block date',
  'mgmt.av.unblock': 'Unblock {d}',
  'mgmt.av.blockedTimes': 'Blocked times (e.g. lunch)',
  'mgmt.av.block': 'Block',
  'mgmt.av.removeBlocked': 'Remove blocked time',
  'mgmt.av.payment': 'Payment',
  'mgmt.av.payNote': 'Payment is handled outside Pomela. The owner confirms payment manually.',
  'mgmt.av.extEnable': 'Accept payment online (external link)',
  'mgmt.av.extNote': 'Pay online through the business’s payment link.',
  'mgmt.av.extUrl': 'Payment link (https://…)',
  'mgmt.av.extLabel': 'Label (e.g. “Deposit $20”)',
  'mgmt.av.extLabelPh': 'Deposit $20',
  'mgmt.av.instructions': 'Instructions (optional)',
  'mgmt.av.extInstrPh': 'What the customer should know about paying online.',
  'mgmt.av.pabEnable': 'Allow pay at the business (in person)',
  'mgmt.av.pabNote': 'Pay at the business.',
  'mgmt.av.pabInstrPh': 'Pay at the salon after your appointment.',
  'mgmt.av.footNote': 'Customers only ever see times this allows, minus booked slots.',
  'mgmt.av.save': 'Save availability',
  'mgmt.day.sun': 'Sun',
  'mgmt.day.mon': 'Mon',
  'mgmt.day.tue': 'Tue',
  'mgmt.day.wed': 'Wed',
  'mgmt.day.thu': 'Thu',
  'mgmt.day.fri': 'Fri',
  'mgmt.day.sat': 'Sat',
  // Waitlist
  'mgmt.wl.title': 'Waitlist',
  'mgmt.wl.fallback': 'Waitlist entry',
  'mgmt.wl.stNew': 'New',
  'mgmt.wl.stContacted': 'Contacted',
  'mgmt.wl.stCandidate': 'Candidate',
  'mgmt.wl.stDismissed': 'Dismissed',
  'mgmt.wl.markContacted': 'Mark contacted',
  'mgmt.wl.promote': 'Promote',
  'mgmt.wl.dismiss': 'Dismiss',
  'mgmt.wl.backToContacted': 'Back to contacted',
  'mgmt.wl.unavailableT': 'Waitlist not available',
  'mgmt.wl.unavailableS': 'This environment has no data store connection yet.',
  'mgmt.wl.errT': 'Couldn’t load the waitlist',
  'mgmt.wl.errAuth': 'Sign in as the app owner to view the waitlist.',
  'mgmt.wl.errGeneric': 'Something went wrong fetching this app’s waitlist.',
  'mgmt.wl.emptyT': 'No one on the waitlist yet',
  'mgmt.wl.emptyS': 'When a slot, event, or pickup window is full, customers can join the waitlist from the app. Their requests appear here so you can reach out and manage the queue.',
  // Activity log
  'mgmt.al.title': 'Activity log',
  'mgmt.al.fallback': 'Activity',
  'mgmt.al.system': 'system',
  'mgmt.ev.memberJoined': 'Member joined',
  'mgmt.ev.inviteCreated': 'Invite created',
  'mgmt.ev.inviteRedeemed': 'Invite redeemed',
  'mgmt.ev.multiOn': 'Group features turned on',
  'mgmt.ev.multiOff': 'Group features turned off',
  'mgmt.ev.msgEdited': 'Message edited',
  'mgmt.ev.msgDeleted': 'Message deleted',
  'mgmt.ev.bkRequested': 'Booking requested',
  'mgmt.ev.bkConfirmed': 'Booking confirmed',
  'mgmt.ev.bkCancelled': 'Booking cancelled',
  'mgmt.ev.bkRescheduled': 'Booking rescheduled',
  'mgmt.al.unavailableT': 'Activity log not available',
  'mgmt.al.unavailableS': 'This environment has no event store connection yet. Activity appears here once it is live.',
  'mgmt.al.errT': 'Couldn’t load activity',
  'mgmt.al.errAuth': 'Sign in as the app owner to view activity.',
  'mgmt.al.errGeneric': 'Something went wrong fetching this app’s activity.',
  'mgmt.al.emptyT': 'No activity yet',
  'mgmt.al.emptyS': 'Member joins, invites, group-feature toggles, and message edits or deletes are recorded here so you can see exactly who did what, and when.',
  // Asset library
  'mgmt.as.title': 'Asset library',
  'mgmt.as.upload': 'Upload',
  'mgmt.as.uploading': 'Uploading…',
  'mgmt.as.err415': 'That file type isn’t supported.',
  'mgmt.as.err413': 'That file is too large.',
  'mgmt.as.err429': 'Daily upload limit reached — try again tomorrow.',
  'mgmt.as.err503': 'Uploads aren’t enabled in this environment yet.',
  'mgmt.as.errUpload': 'Upload failed. Please try again.',
  'mgmt.as.errNoRef': 'Upload succeeded but returned no file reference.',
  'mgmt.as.errCatalog': 'File uploaded but couldn’t be added to the library.',
  'mgmt.as.err404': 'That asset no longer exists. Refresh to see the latest.',
  'mgmt.as.errRemove': 'Couldn’t remove that asset. Please try again.',
  'mgmt.as.errConnUp': 'Connection lost during upload. Check your network and try again.',
  'mgmt.as.unavailableT': 'Asset storage not configured',
  'mgmt.as.unavailableS': 'This environment has no Supabase Storage connection. Assets appear here once uploads are live.',
  'mgmt.as.errT': 'Couldn’t load assets',
  'mgmt.as.errAuth': 'Sign in as the app owner to manage assets.',
  'mgmt.as.errGeneric': 'Something went wrong fetching this app’s assets.',
  'mgmt.as.emptyT': 'No assets yet',
  'mgmt.as.emptyS': 'Upload an image or document and it’s cataloged here for this app — copy its reference to use it, or remove it anytime.',
  'mgmt.as.copyRef': 'Copy ref',
  'mgmt.as.copied': 'Copied',
  'mgmt.as.remove': 'Remove',
  // Payment link config
  'mgmt.pl.title': 'Payment link',
  'mgmt.pl.errLoad': 'Couldn’t load payment settings.',
  'mgmt.pl.unavailableT': 'Payment store not configured',
  'mgmt.pl.unavailableS': 'This environment has no Supabase connection.',
  'mgmt.pl.err403': 'Only the app owner can change payment settings.',
  'mgmt.pl.err400': 'Please check the payment link.',
  'mgmt.pl.errSave': 'Couldn’t save payment settings.',
  'mgmt.pl.scope': 'Customers pay through your own external link. Pomela doesn’t process or verify payments — you confirm each one by hand in Manage Bookings.',
  'mgmt.pl.require': 'Require payment for bookings',
  'mgmt.pl.linkLabel': 'Payment link (https)',
  'mgmt.pl.httpsErr': 'Must start with https://',
  'mgmt.pl.amountLabel': 'Amount label (shown to customers)',
  'mgmt.pl.amountPh': 'e.g. $40 deposit',
  'mgmt.pl.instrPh': 'e.g. Pay the deposit to hold your slot; balance due in person.',
  'mgmt.pl.taxShipT': 'Tax & shipping',
  'mgmt.pl.taxShipOpt': '(optional · commerce)',
  'mgmt.pl.taxShipS': 'Manual values you set — shown as line items on the cart total. No automatic tax engine; nothing is charged.',
  'mgmt.pl.taxRate': 'Tax rate (%)',
  'mgmt.pl.taxRange': 'Must be 0–100',
  'mgmt.pl.taxLabel': 'Tax label',
  'mgmt.pl.taxPh': 'Sales tax',
  'mgmt.pl.shipFee': 'Shipping fee',
  'mgmt.pl.shipMin': '0 or more',
  'mgmt.pl.shipLabel': 'Shipping label',
  'mgmt.pl.shipPh': 'Shipping',
  'mgmt.pl.save': 'Save payment settings',
  // §S680 — unified error surface (ErrorState variants)
  'state.goHome': 'Go home',
  'state.guide': 'Guide',
  'state.notFound.title': 'This page wandered off',
  'state.notFound.body': 'The page you’re looking for doesn’t exist or has moved. Let’s get you back.',
  'state.appNotFound.code': 'App not found',
  'state.appNotFound.title': 'We couldn’t find that app',
  'state.appNotFound.body': 'It may have been removed, or it isn’t in your workspace on this device.',
  'state.genFailed.code': 'Generation failed',
  'state.genFailed.title': 'That build didn’t go through',
  'state.genFailed.body': 'Pomela couldn’t finish building your app. Your work is saved — try again in a moment.',
  'state.denied.code': 'Access',
  'state.denied.title': 'You don’t have access here',
  'state.denied.body': 'This area is restricted. If you think this is a mistake, contact support.',
  'state.offline.code': 'Offline',
  'state.offline.title': 'You’re offline',
  'state.offline.body': 'Check your connection. Your work is saved and will sync when you’re back.',
  'state.noResults.code': 'No results',
  'state.noResults.title': 'Nothing matched',
  'state.noResults.body': 'Try a different search or broaden your filters.',
  'state.crash.code': 'Something broke',
  'state.crash.title': 'Pomela hit a snag',
  'state.crash.body': 'An unexpected error occurred. Reloading usually fixes it — your apps are safe.',
  'state.generic.code': 'Error',
  'state.generic.title': 'Something went wrong',
  'state.generic.body': 'An unexpected error occurred. Let’s get you back on track.',
  // §S680 — ShareCard localization
  'share.openLive': 'Open live app',
  'share.builtPrefix': 'I built',
  'share.builtSuffix': 'in one sentence:',
  'share.qrHelp': 'Point your phone camera. Add to Home Screen for a fullscreen native app.',
  'share.ariaX': 'Share on X',
  'share.ariaWhatsApp': 'Share on WhatsApp',
  'share.progressAria': '{filled} of {total} first shares sent',
  'share.ogAlt': 'Social preview for {name}',
  // §S680 — Sidebar stragglers
  'sb.menuAria': 'Menu',
  'sb.closeMenu': 'Close menu',
  'sb.brandTag': 'AI app studio',
  'sb.noRecentText': 'The apps you generate collect here, tap any one to reopen it.',
  'sb.signedIn': 'Signed in',
  'sb.notSignedIn': 'Not signed in',
  // §S680 — Settings stragglers (sub-nav, billing, language, danger zone)
  'set.sectionsAria': 'Settings sections',
  'set.billing': 'Billing & credits',
  'set.language': 'Language & RTL',
  'set.privacyNav': 'Privacy',
  'set.danger': 'Danger zone',
  'set.planPro': 'Pro plan',
  'set.planFree': 'Free plan',
  'set.planManage': 'Manage your plan and credits',
  'set.planUpgrade': 'Upgrade to Pro for more monthly credits',
  'set.rtlRow': 'Right-to-left layout',
  'set.rtlOn': 'On — Hebrew interface',
  'set.rtlOff': 'Switches on with a right-to-left language',
  'set.deleteConfirm': 'Delete all your Pomela data on this device? This cannot be undone.',
  // §S680 — Account stragglers (device-scoped delete honesty + info cards)
  'acct.dangerZone': 'Danger zone',
  'acct.delOpen': 'Delete my data on this device',
  'acct.delAria': 'Confirm data deletion',
  'acct.wsT': 'Your workspace',
  'acct.wsB': 'Open and manage every app you build, all in one place.',
  'acct.wsCta': 'Go to Workspace',
  'acct.helpT': 'Need a hand?',
  'acct.helpB': 'Browse the knowledge base or reach support.',
  'acct.helpCta': 'Open Guide',
  // §S680 — App.tsx ErrorPanel + CommandPalette (keys only; patch applied by the orchestrator)
  'err.genFailed': 'Generation failed',
  'common.tryAgain': 'Try again',
  'common.startOver': 'Start over',
  'cmdk.noMatches': 'No matches.',
  // §refusal-honesty — deliberate /v1/generate refusals (sse.ts). The gateway
  // ships friendly English copy in the refusal body; these are the localized
  // twins (shown verbatim for non-EN locales, and the fallback when the body
  // carries no readable message). Keep in sync with generate-orchestrator.ts.
  'err.maintenance': 'Pomela is in private preview — we\'ll email you the moment it opens. 🍊',
  'err.credits': 'You\'ve used all your credits — top up or upgrade to Pro to keep building.',
};

const HE: Record<string, string> = {
  // ─── §V2 exact-port platform namespace — HE twins ───
  'v2.nav.home': 'בית',
  'v2.nav.workspace': 'סביבת העבודה',
  'v2.nav.learn': 'מאגר ידע',
  'v2.support.topicApp': 'האפליקציה שלי לא עובדת',
  'v2.support.topicBilling': 'שאלת חיוב',
  'v2.support.topicIdea': 'יש לי רעיון',
  'v2.support.topicOther': 'משהו אחר',
  'v2.learn.step1s': 'אומרים מה רוצים במשפט אחד.',
  'v2.learn.step2s': 'פומלה בונה מסכים ודאטה אמיתיים.',
  'v2.learn.step3s': 'מלטשים הכול בצ׳אט או בהקשה.',
  'v2.learn.step4s': 'מתקינים ומשתפים בהקשה.',
  'v2.set.light': 'בהיר',
  'v2.set.dark': 'כהה',
  'v2.set.system': 'מערכת',
  'v2.account.dangerSub': 'התנתקו מהמכשיר הזה, או מחקו לצמיתות את המידע שפומלה שומרת כאן.',
  'v2.account.errB': 'משהו השתבש בטעינת החיוב והשימוש שלכם. התוכנית והקרדיטים שלכם בטוחים — זו רק תקלת תצוגה.',
  'v2.account.errT': 'לא הצלחנו לטעון את החשבון שלכם',
  'v2.account.installs': 'התקנות',
  'v2.account.pro': 'Pro',
  'v2.account.statApps': 'אפליקציות שנבנו (סה״כ)',
  'v2.account.statJoined': 'הצטרפתם ב־{y}',
  'v2.account.usageRow': '{n} שימושי AI · {c} קרדיטים',
  'v2.account.usageSub': '{n} אפליקציות · {m} שימושי AI',
  'v2.account.usageT': 'שימוש לפי אפליקציה',
  'v2.account.views': 'צפיות',
  'v2.app.ariaCopy': 'העתקת קישור',
  'v2.app.ariaShareWa': 'שיתוף בוואטסאפ',
  'v2.app.ariaShareX': 'שיתוף ב־X',
  'v2.app.chWa': 'וואטסאפ',
  'v2.app.chX': 'X',
  'v2.app.customDomain': 'דומיין משלכם',
  'v2.app.editApp': 'עריכת האפליקציה',
  'v2.app.errB': 'משהו השתבש בטעינת הנתונים החיים של האפליקציה. הלקוחות שלכם לא הושפעו — זו רק התצוגה הפרטית שלכם.',
  'v2.app.errT': 'לא הצלחנו לטעון את הנתונים שלכם',
  'v2.app.exportCode': 'ייצוא קוד',
  'v2.app.keepBuilding': 'ממשיכים לבנות, דבר אחד בכל פעם',
  'v2.app.linkK': 'קישור',
  'v2.app.liveSub': 'שלחו אותה לחמשת המשתמשים הראשונים — ככה אפליקציות מוצאות את האנשים שלהן.',
  'v2.app.liveT': 'האפליקציה שלכם באוויר',
  'dlv.stTry': 'לניסיון',
  'dlv.title': 'בנויה לניסיון: עדיין לא מוכנה',
  'dlv.sub': 'האפליקציה עדיין לא עושה את כל מה שביקשתם, ולכן היא לא מוצגת כמוכנה. נסו אותה, ואמרו לעורך מה להוסיף.',
  'dlv.unmet': 'לא נבנה',
  'dlv.unverified': 'לא אושר',
  'dlv.try': 'לנסות את האפליקציה',
  'v2.app.manageLabel': 'ניהול · פרטי רק לכם',
  'v2.app.openLiveApp': 'פתיחת האפליקציה החיה',
  'v2.app.previewT': 'האפליקציה שלכם מוכנה לשיתוף',
  'v2.app.privacyBooking': 'הזמנות, הכנסות ואישורים',
  'v2.app.privacyGeneric': 'הרשומות ופרטי הלקוחות שלכם',
  'v2.app.privacyLead': 'הלקוחות שלכם רואים תמיד רק את צד הלקוח.',
  'v2.app.privacyT': 'תצוגת הניהול הזו חיה בתוך פומלה — אף פעם לא בתוך האפליקציה.',
  'v2.app.privacyTail': 'נשארים פרטיים רק לכם, כאן.',
  'v2.app.private': 'פרטי, בקישור בלבד',
  'v2.app.public': 'קישור שיתוף ציבורי',
  'v2.app.retry': 'נסו שוב',
  'v2.app.viewLive': 'צפייה בגרסה החיה',
  'v2.home.attachAria': 'צירוף',
  'v2.home.attachTitle': 'צירוף תמונה או קובץ',
  'v2.home.buildIt': 'לבנות',
  'v2.home.buildMode': 'מצב בנייה',
  'v2.home.chipBooking': 'עמוד הזמנת תורים',
  'v2.home.chipFlash': 'חפיסת כרטיסיות',
  'v2.home.chipRsvp': 'אישורי הגעה לאירוע',
  'v2.home.chipTodo': 'רשימת מטלות',
  'v2.home.chipSurprise': 'תפתיעו אותי',
  'v2.home.chipWater': 'מעקב שתיית מים',
  // §Launch-honesty — מחרוזות ספארק/מיני נתמכות בלבד (מחליפות טיול/תקציב/CRM/מתכונים/משחק).
  'v2.home.sparkClassN': 'הזמנת שיעורים',
  'v2.home.sparkClassTag': 'שתפו קישור אחד — בוחרים זמן ותופסים מקום.',
  'v2.home.sparkClassP': 'עמוד הזמנת שיעורים שבו בוחרים זמן פנוי ותופסים מקום',
  'v2.home.miniClassName': 'הזמנת שיעור',
  'v2.home.miniClassH': 'תפסו מקום',
  'v2.home.sparkWorkoutN': 'יומן אימונים',
  'v2.home.sparkWorkoutTag': 'תעדו סטים וחזרות, וצפו בנפח השבועי מטפס.',
  'v2.home.sparkWorkoutP': 'יומן אימונים עם תרגילים, סטים וחזרות, טיימרי מנוחה וגרף נפח שבועי',
  'v2.home.miniWorkoutName': 'יומן אימונים',
  'v2.home.miniWorkoutH': 'השבוע',
  'v2.home.miniWorkoutSets': 'סטים',
  'v2.home.miniWorkoutVol': 'נפח',
  'v2.home.sparkInvN': 'מעקב מלאי',
  'v2.home.sparkInvTag': 'כל פריט, הכמות שלו, ומה עומד להיגמר.',
  'v2.home.sparkInvP': 'מעקב מלאי לחנות עם ספירת פריטים, עדכוני מלאי והתראות על מלאי נמוך',
  'v2.home.miniInvName': 'מלאי',
  'v2.home.miniInvH': 'פריטים',
  'v2.home.miniInvI1': 'חלב שיבולת שועל',
  'v2.home.miniInvSub1': '12 במלאי',
  'v2.home.miniInvI2': 'פולי אספרסו',
  'v2.home.miniInvSub2': 'נמוך · נותרו 3',
  'v2.home.sparkFlashN': 'חפיסת כרטיסיות',
  'v2.home.sparkFlashTag': 'למידה עם חזרה בריווח, כרטיסייה אחת בכל פעם.',
  'v2.home.sparkFlashP': 'חפיסת כרטיסיות עם חזרה בריווח, חפיסות לפי נושא וחזרה יומית',
  'v2.home.miniFlashName': 'כרטיסיות',
  'v2.home.miniFlashH': 'ספרדית 101',
  'v2.home.miniFlashCards': 'כרטיסים',
  'v2.home.miniFlashDue': 'להיום',
  'v2.home.sparkTodoN': 'רשימת מטלות',
  'v2.home.sparkTodoTag': 'פרויקטים, צ׳ק-ליסטים ותאריכי יעד שבאמת נדבקים.',
  'v2.home.sparkTodoP': 'אפליקציית מטלות עם פרויקטים, צ׳ק-ליסטים, תאריכי יעד והערות מהירות',
  'v2.home.miniTodoName': 'מטלות',
  'v2.home.miniTodoH': 'היום',
  'v2.home.miniTodoDone': 'הושלמו',
  'v2.home.miniTodoOpen': 'לביצוע',
  'v2.home.continueT': 'להמשיך מאיפה שעצרתם',
  'v2.home.fAll': 'הכול',
  'v2.home.fBusiness': 'עסק',
  'v2.home.fHobby': 'תחביב',
  'v2.home.fPersonal': 'אישי',
  'v2.home.fSocial': 'חברתי',
  'v2.home.ghBody': 'בלי קוד, בלי המתנה, בלי אישורי חנות. פשוט מתארים.',
  'v2.home.ghCta': 'להתחיל לבנות',
  'v2.home.ghEyebrow': 'מוכנים כשאתם מוכנים',
  'v2.home.ghTitle': 'הרעיון שלכם במרחק משפט אחד.',
  'v2.home.heroW1': 'כותבים.',
  'v2.home.heroW2': 'מקישים.',
  'v2.home.heroW3': 'מתקינים.',
  'v2.home.lede': 'מתארים במשפט אחד. מתקינים על הטלפון — בלי קוד, בלי חנות אפליקציות.',
  'v2.home.miniBookH': 'קביעת תור',
  'v2.home.miniBookName': 'תורים לסטודיו',
  'v2.home.miniBudgetEnv': 'מעטפות',
  'v2.home.miniBudgetH': 'החודש',
  'v2.home.miniBudgetName': 'צנצנת תקציב',
  'v2.home.miniCrmC1': 'נועה פרץ',
  'v2.home.miniCrmC2': 'דנה כהן',
  'v2.home.miniCrmH': 'לקוחות',
  'v2.home.miniCrmName': 'CRM לקוחות',
  'v2.home.miniCrmSub1': 'פולו-אפ · חמישי',
  'v2.home.miniCrmSub2': 'יום הולדת · בעוד 3 ימים',
  'v2.home.miniGameH': 'מי בעניין ביום שישי?',
  'v2.home.miniGameName': 'ערב משחקים',
  'v2.home.miniLangDays': '14 ימים',
  'v2.home.miniLangH': 'תרגול יומי',
  'v2.home.miniLangName': 'רצף שפה',
  'v2.home.miniLangStreak': 'רצף',
  'v2.home.miniRecipeCooked': 'בושלו',
  'v2.home.miniRecipeH': 'המתכונים שלכם',
  'v2.home.miniRecipeName': 'קופסת מתכונים',
  'v2.home.miniSaved': 'שמורים',
  'v2.home.miniTripCount': '4 מגיעים · 1 אולי',
  'v2.home.miniTripCta': 'לתוכנית',
  'v2.home.miniTripH': 'סופ״ש באילת',
  'v2.home.miniTripInv': 'הוזמנתם',
  'v2.home.miniTripName': 'תכנון טיול',
  'v2.home.miniWaterGlasses': 'כוסות היום',
  'v2.home.miniWaterH': 'לשתות מספיק',
  'v2.home.miniWaterName': 'מעקב שתייה',
  'v2.home.openWorkspace': 'לסביבת העבודה',
  'v2.home.removeAsset': 'הסרה',
  'v2.home.sparkBookN': 'תורים לסטודיו',
  'v2.home.sparkBookP': 'עמוד הזמנת תורים לסטודיו שלי עם מקדמות ותזכורות',
  'v2.home.sparkBookTag': 'תורים ומקדמות, בלי טלפונים הלוך-חזור.',
  'v2.home.sparkBudgetN': 'צנצנת תקציב',
  'v2.home.sparkBudgetP': 'אפליקציית תקציב עם מעטפות הוצאה במקום גיליון',
  'v2.home.sparkBudgetTag': 'מעטפות להוצאות, בלי אקסל.',
  'v2.home.sparkCrmN': 'CRM לקוחות',
  'v2.home.sparkCrmP': 'CRM פשוט לשמירת הערות על כל לקוח ברשימה אחת',
  'v2.home.sparkCrmTag': 'כל לקוח, כל הערה, ברשימה אחת.',
  'v2.home.sparkGameN': 'ערב משחקים RSVP',
  'v2.home.sparkGameP': 'עמוד אישורי הגעה לערב משחקים שבו אני שולח קישור אחד ורואה מי מגיע',
  'v2.home.sparkGameTag': 'קישור אחד, ויודעים מי באמת מגיע.',
  'v2.home.sparkLangN': 'רצף שפה',
  'v2.home.sparkLangP': 'מעקב רצף יומי של 10 דקות תרגול שפה',
  'v2.home.sparkLangTag': '10 דקות ביום, במעקב כמו הרגל.',
  'v2.home.sparkRecipeN': 'קופסת מתכונים',
  'v2.home.sparkRecipeP': 'קופסת מתכונים שבה אני שומר ומחפש את המתכונים שלי',
  'v2.home.sparkRecipeTag': 'המתכונים שלכם, בחיפוש, לא הולכים לאיבוד.',
  'v2.home.sparkSub': 'הקישו על רעיון והוא ייכנס ישר לתיבה למעלה — ערכו אותו, או בנו אותו כמו שהוא.',
  'v2.home.sparkT': 'לא בטוחים מה לבנות? התחילו מניצוץ.',
  'v2.home.sparkTripN': 'תכנון טיול',
  'v2.home.sparkTripP': 'מתכנן טיול שבו חברים רואים תאריכים, מסמכים משותפים ומי מגיע',
  'v2.home.sparkTripTag': 'מקום אחד לתאריכים, מסמכים ומי מגיע.',
  'v2.home.sparkWaterN': 'מעקב שתייה',
  'v2.home.sparkWaterP': 'מעקב שתיית מים שמזכיר לי בעדינות לשתות לאורך היום',
  'v2.home.sparkWaterTag': 'כוס ביום, עם תזכורת עדינה.',
  'v2.home.stat1L': 'כמו אפליקציה אמיתית',
  'v2.home.stat1N': 'מתקינים',
  'v2.home.stat2L': 'עם פומי, הקופיילוט שלכם',
  'v2.home.stat2N': 'עורכים מתי שרוצים',
  'v2.home.stat3L': 'מגובה בשרת, שלכם לתמיד',
  'v2.home.stat3N': 'דאטה אמיתי',
  'v2.home.stat4L': 'מתארים במשפט אחד',
  'v2.home.stat4N': 'אפס קוד',
  'v2.home.surprisePrompt': 'יומן אימונים עם תרגילים, סטים וחזרות, טיימרי מנוחה וגרף נפח שבועי',
  'v2.home.uploading': 'מעלה…',
  'v2.home.useIdea': 'להשתמש ברעיון',
  'v2.learn.curatedT': 'כדאי להתחיל מאלה',
  'v2.learn.heroSub': 'פומלה הופכת משפט אחד לאפליקציית מובייל שלמה שמתקינים: מסכים אמיתיים, אינטראקציות אמיתיות, דאטה אמיתי. זה מדריך השטח — מה לבנות, איך לנסח, והטריקים שהופכים אפליקציה מטובה לכזו שהיא באמת שלכם.',
  'v2.learn.noResB': 'לא מצאנו מדריך לחיפוש הזה. נסו מילה רחבה יותר, או שאלו את פומי — הוא מכיר את המוצר לעומק.',
  'v2.learn.noResT': 'אין עדיין מדריכים שמתאימים',
  'v2.nav.askPomy': 'לשאול את פומי',
  'v2.nav.collapse': 'כיווץ סרגל הצד',
  'v2.nav.support': 'תמיכה',
  'v2.signout.error': 'לא הצלחנו להתנתק — בדקו את החיבור ונסו שוב.',
  'v2.pomy.aria': 'לשאול את פומי — רעיונות, עצות, כל דבר על פומלה',
  'v2.pomy.chip0': 'תנו לי רעיון לאפליקציה לעסק שלי',
  'v2.pomy.chip1': 'מה אפשר לבנות עם פומלה?',
  'v2.pomy.chip2': 'איך עובדים הקרדיטים?',
  'v2.pomy.chip3': 'מה אפשר באמת לבנות כאן?',
  'v2.pomy.err': 'לא הצלחתי להגיע לפומי כרגע — נסו שוב עוד רגע.',
  'v2.pomy.hello': 'היי, אני פומי. בקשו ממני רעיונות לאפליקציות, שאלו איך משהו בפומלה עובד, או התייעצו מה לבנות עכשיו.',
  'v2.pomy.ph': 'שאלו את פומי כל דבר…',
  'v2.pomy.sub': 'רעיונות, עצות, כל דבר על פומלה',
  'v2.send': 'שליחה',
  'v2.set.account': 'חשבון',
  'v2.set.appearance': 'מראה',
  'v2.set.billing': 'חיוב וקרדיטים',
  'v2.set.billingProSub': 'תוכנית Pro · ניהול הקרדיטים שלכם',
  'v2.set.copiedToast': 'הקישור הועתק ✓',
  'v2.set.copy': 'העתקה',
  'v2.set.danger': 'אזור רגיש',
  'v2.set.dangerSub': 'התנתקות או מחיקת המידע שלכם',
  'v2.set.dirLtr': 'English · משמאל לימין',
  'v2.set.dirRtl': 'עברית · מימין לשמאל',
  'v2.set.inviteSub': 'פומלה נהיית טובה יותר ככל שיותר אנשים בונים איתה. שלחו את הקישור שלכם — חבר נוחת ישר בבילדר, מוכן לבנות אפליקציה ראשונה.',
  'v2.set.language': 'שפה ופריסה',
  'v2.set.notifications': 'התראות',
  'v2.set.prefs': 'העדפות',
  'v2.set.privacy': 'פרטיות',
  'v2.set.privacySub': 'דאטה, מעקב וייצוא',
  'v2.set.sectionsAria': 'קטעי ההגדרות',
  'v2.set.security': 'אבטחה',
  'v2.set.securitySub': 'איך פומלה מגינה על החשבון והמידע שלכם',
  'v2.set.sub': 'החשבון, ההעדפות ובקרות המערכת — הכול במקום אחד.',
  'v2.set.systemSection': 'מערכת',
  'v2.set.tapFlip': 'הקישו להחלפה',
  'v2.set.themeToast': 'המראה הוגדר ל־{mode}',
  'v2.set.title': 'הגדרות',
  'v2.set.yourLink': 'הקישור שלכם',
  'v2.support.badge': 'זמן תגובה ממוצע: פחות מ־24 שעות',
  'v2.support.copied': 'הכתובת הועתקה ✓',
  'v2.support.copy': 'העתקה',
  'v2.support.emailLabel': 'האימייל שלכם',
  'v2.support.emailSub': 'מעדיפים אימייל? העתיקו את הכתובת שלנו',
  'v2.support.errBody': 'משהו השתבש אצלנו. בדקו את החיבור ונסו שוב.',
  'v2.support.errTitle': 'זה לא נשלח',
  'v2.support.eyebrow': 'קונסיירז׳',
  'v2.support.foot': 'נבנה בקפידה, 2026',
  'v2.support.hero1': 'אנחנו כאן.',
  'v2.support.hero2': 'תמיד.',
  'v2.support.heroSub': 'בני אדם אמיתיים מאחורי כל תשובה. המייסד קורא כל הודעה, ורוב הפניות נענות בתשומת לב בתוך יום.',
  'v2.support.linkIdea': 'שיתוף רעיון',
  'v2.support.linkIdeaSub': 'ספרו לנו מה פומלה צריכה לבנות עכשיו.',
  'v2.support.linkKb': 'מרכז הידע',
  'v2.support.linkKbSub': 'מדריכים, טיפים ותבניות ניסוח.',
  'v2.support.linkPress': 'עיתונות ושיתופי פעולה',
  'v2.support.linkPressSub': 'נשמח לשמוע מעיתונאים ויוצרים.',
  'v2.support.more': 'עוד מפומלה',
  'v2.support.msgLabel': 'איך נוכל לעזור?',
  'v2.support.msgPh': 'ספרו לנו מה קורה עם האפליקציה שלכם…',
  'v2.support.send': 'שליחת הודעה',
  'v2.support.sendAnother': 'לשלוח עוד אחת',
  'v2.support.sendTitle': 'שלחו לנו הודעה',
  'v2.support.sending': 'שולח…',
  'v2.support.successBody': 'נחזור אליכם בתוך יום. שימו עין על תיבת הדואר של האימייל שמסרתם.',
  'v2.support.successTitle': 'ההודעה נשלחה',
  'v2.support.tryAgain': 'נסו שוב',
  'v2.try': 'נסו',
  'v2.workspace.emptyBody': 'תארו את האפליקציה הראשונה שלכם במשפט אחד — היא תחיה כאן, מוכנה לעריכה, לפרסום ולשיתוף.',
  'v2.workspace.installsTotal': 'סה״כ בכל האפליקציות שלכם',
  'v2.workspace.offlineBody': 'אי אפשר להגיע כרגע לאפליקציות המסונכרנות שלכם. בדקו את החיבור ונסו שוב.',
  'v2.workspace.retry': 'נסו שוב',
  'v2.workspace.searchPh': 'חיפוש אפליקציות…',
  'v2.workspace.statApps': 'אפליקציות בסביבת העבודה',
  'v2.workspace.statDrafts': 'טיוטות',
  'v2.workspace.sub': 'כל מה שיצרתם, במקום אחד.',
  'nav.home': 'בית',
  'nav.copilot': 'Pomy',
  'nav.workspace': 'סביבת העבודה',
  'nav.connectors': 'אינטגרציות',
  'cmd.connectorsHint': 'עיון במקורות נתונים חיים שאפשר לחבר',
  'nav.guide': 'מאגר ידע',
  'nav.settings': 'הגדרות',
  'nav.account': 'חשבון',
  'nav.admin': 'ניהול',
  'nav.credits': 'קרדיטים',
  'nav.signIn': 'התחברות',
  'nav.ownerGodMode': 'בעלים · מצב על',
  'nav.search': 'חיפוש',
  'nav.streak': 'רצף של {n} ימים',
  'nav.streakNone': 'בנו משהו היום',
  'nav.creditsLeft': 'קרדיטים נותרו',
  'common.home': 'בית',
  'common.skip': 'דילוג',
  'common.back': 'חזרה',
  'common.goBack': 'חזרה',
  'common.openMenu': 'פתחו תפריט',
  'common.menu': 'תפריט',
  'common.next': 'הבא',
  'common.save': 'שמירה',
  'common.cancel': 'ביטול',
  'common.retry': 'נסו שוב',
  'common.close': 'סגירה',
  'common.signIn': 'התחברות',
  'home.promptPlaceholder': 'תארו אפליקציה שתרצו לבנות…',
  'home.build': 'בנו אותה',
  'home.quick': 'מהיר',
  'home.deep': 'מעמיק',
  'auth.title': 'אתם בונים מהר.',
  'auth.sub': 'ניצלתם את הבנייה החינמית שלכם. התחברו, זה נשאר חינם, כדי להמשיך לבנות ולסנכרן את האפליקציות בכל המכשירים.',
  'state.loading': 'טוען…',
  'state.offline': 'אתם במצב לא מקוון',
  'state.retry': 'נסו שוב',
  'state.error': 'זה לא עבר. נסו שוב בעוד רגע.',
  'home.sub': 'תארו אפליקציה והתקינו אותה על הטלפון. אפליקציות אמיתיות בתחושה נייטיב, בלי חנות אפליקציות.',
  'home.ex1': 'מעקב שתיית מים',
  'home.ex2': 'עמוד הזמנות',
  'home.ex3': 'חפיסת כרטיסיות',
  'home.ex4': 'אישור הגעה לאירוע',
  'sb.workspace.title': 'סביבת העבודה',
  'sb.workspace.eyebrow': 'האפליקציות שלכם',
  'sb.workspace.intro': 'כל אפליקציה שתייצרו תיאסף כאן. פתחו אחת מחדש כדי להמשיך לערוך, שכפלו אותה כנקודת פתיחה למשהו חדש, או נקו אותה מסביבת העבודה.',
  'sb.workspace.empty': 'עדיין אין אפליקציות',
  'sb.workspace.emptyText': 'האפליקציות שתייצרו ינחתו כאן אוטומטית. פתחו כל אחת בנגיעה.',
  'sb.workspace.search': 'חיפוש אפליקציות',
  'sb.workspace.fAll': 'הכל',
  'sb.workspace.fLive': 'פעיל',
  'sb.workspace.fPreview': 'תצוגה',
  'sb.workspace.sortRecent': 'אחרונים',
  'sb.workspace.activity': 'פעילות אחרונה',
  'sb.workspace.installs': 'התקנות',
  'sb.workspace.emptyBtn': 'בנו את האפליקציה הראשונה שלכם',
  'sb.help.title': 'תמיכה',
  'sb.help.eyebrow': 'שירות אישי',
  'sb.guide.title': 'מאגר ידע',
  'sb.guide.eyebrow': 'ללמוד',
  'sb.copilot.title': 'Pomy',
  'sb.copilot.eyebrow': 'שותף לבנייה',
  'sb.account.title': 'חשבון',
  'sb.account.eyebrow': 'פרופיל',
  'sb.upgrade.title': 'שדרוג',
  'sb.upgrade.eyebrow': 'Pro',
  'cookie.msg': 'אנחנו משתמשים בעוגיות חיוניות כדי להפעיל את Pomela. באישורכם, נשתמש גם בכמה כדי להבין מה לבנות בהמשך.',
  'cookie.privacy': 'מדיניות פרטיות',
  'cookie.essential': 'חיוניות בלבד',
  'cookie.all': 'אישור הכול',
  'install.iosTitle': 'הוספה למסך הבית',
  'install.iosSub': 'שומר על האפליקציה במסך מלא — בלי סרגלי דפדפן.',
  'install.iosStep1': 'הקישו על כפתור השיתוף למטה',
  'install.iosStep2': 'גללו אל "הוספה למסך הבית"',
  'install.iosStep3': 'הקישו עליו',
  'install.iosStep4': 'פתחו מהמסך הבית',
  'install.androidCta': 'התקנת האפליקציה',
  'invite.eyebrow': 'שתפו את השמחה',
  'invite.title': 'הזמינו חבר לבנות עם Pomela.',
  'invite.sub': 'Pomela משתפרת ככל שיותר אנשים בונים איתה. שלחו את הקישור שלכם — החבר שלכם נוחת ישר בבונה, מוכן ליצור את האפליקציה הראשונה.',
  'invite.yourLink': 'הקישור שלכם',
  'invite.share': 'שיתוף הזמנה',
  'invite.copy': 'העתקת קישור',
  'invite.copied': 'הועתק',
  'invite.compactTitle': 'הזמינו חבר',
  'invite.compactSub': 'הביאו חבר ל-Pomela',
  'invite.counter': 'שיתפתם {n} פעמים. תמשיכו ככה.',
  'invite.shareText': 'אני בונה אפליקציות קטנות בטלפון תוך כ-90 שניות עם Pomela. נסו:',
  'invite.shareTitle': 'בנו אפליקציה עם Pomela',
  'invite.compactAria': 'הזמינו חבר',
  'invite.copyFail': 'ההעתקה נכשלה. לחצו לחיצה ארוכה על הקישור במקום.',
  'set.appearance': 'מראה',
  'set.light': 'בהיר',
  'set.dark': 'כהה',
  'set.system': 'מערכת',
  'set.notifications': 'התראות',
  'set.dataPrivacy': 'נתונים ופרטיות',
  'set.export': 'ייצוא הנתונים שלי',
  'set.exportSub': 'הורידו את כל מה ששמור במכשיר הזה כקובץ JSON',
  'set.delete': 'מחיקת הנתונים שלי',
  'set.deleteSub': 'הסירו לצמיתות את האפליקציות וההיסטוריה שלכם',
  'set.about': 'אודות',
  'set.version': 'גרסה',
  'set.privacy': 'מדיניות פרטיות',
  'set.security': 'אבטחה',
  'set.title': 'הגדרות',
  'set.saved': 'כל השינויים נשמרו',
  'set.eyebrow': 'העדפות',
  'set.profile': 'פרופיל',
  'set.signedIn': 'מחוברים',
  'set.notSignedIn': 'לא מחוברים',
  'set.manageAccount': 'ניהול החשבון שלכם',
  'set.signInSync': 'התחברו כדי לסנכרן בין המכשירים',
  'set.pushLabel': 'התראות פוש',
  'set.pushOn': 'פעיל: אפליקציות יכולות להציג התראות כשהן פתוחות במכשיר הזה',
  'set.pushOffMuted': 'כבוי: מושתק במכשיר הזה. לאיפוס מלא, היכנסו להגדרות האתר.',
  'set.pushDefault': 'אפשרו לאפליקציות להציג התראות במכשיר הזה',
  'set.pushUnsupported': 'לא נתמך בדפדפן הזה',
  'set.pushBlocked': 'חסום בהגדרות הדפדפן שלכם',
  'cop.greet': 'היי, אני Pomy.',
  'cop.intro': 'אני שותף הבנייה שלכם. ספרו לי על האפליקציה שחשבתם עליה: החיכוך, החלום, הרגע שבו רציתם שמשהו יתקיים. נעצב את זה יחד לפרומפט.',
  'guide.heroTitle': 'כתבו משפט. קבלו אפליקציה אמיתית בטלפון.',
  'upg.heroTitle': 'בנו בלי גבולות',
  'upg.heroSub': 'מאגר בנייה גדול בהרבה, יצירת וידאו עם AI, דומיינים מותאמים, והקוד שלכם לקחת לכל מקום.',
  // Sign-in (magic-link flow)
  'si.welcome': 'ברוכים הבאים',
  'si.title': 'התחברו ל-Pomela',
  'si.sub': 'הקשה אחת עם מייל, בלי סיסמה לזכור. נשלח קוד בן 6 ספרות.',
  'si.errEmail': 'זה לא נראה כמו כתובת מייל.',
  'si.continue': 'המשך',
  'si.agreeAria': 'אישור התנאים ומדיניות הפרטיות',
  'si.emailAria': 'כתובת אימייל',
  'si.codeAria': 'קוד הכניסה בן שש הספרות מהמייל',
  'si.consentPre': 'אני מסכים/ה ל',
  'si.consentMid': ' ול',
  'si.terms': 'תנאי השימוש',
  'si.privacy': 'מדיניות הפרטיות',
  'si.checkEmail': 'בדקו את המייל',
  'si.enterCode': 'הזינו את הקוד',
  'si.codeSent': 'שלחנו קוד בן 6 ספרות ל-{email}. תוקפו 10 דקות.',
  'si.errSend': 'שליחת מייל ההתחברות נכשלה. נסו שוב בעוד רגע.',
  'si.errCode': 'הזינו את הקוד בן 6 הספרות מהמייל.',
  'si.verify': 'אימות והתחברות',
  'si.differentEmail': 'השתמשו במייל אחר',
  'si.errCodeBad': 'הקוד לא עבד, ייתכן שפג תוקפו. שלחו קוד חדש.',
  'si.youreIn': 'אתם בפנים',
  'si.signedIn': 'מחוברים',
  'si.doneSub': 'האפליקציות שלכם מסונכרנות עכשיו בכל מכשיר שמחובר כ-{email}.',
  'si.startBuilding': 'התחילו לבנות',
  'si.oneMoment': 'רגע אחד…',
  // Prompt canvas (home dock)
  'pi.placeholder': 'תארו אפליקציה שתרצו לבנות…',
  'pi.whatBuild': 'מה תרצו לבנות?',
  'pi.addToPrompt': 'הוסיפו לפרומפט',
  'pi.quick': 'מהיר',
  'pi.quickHint': 'דקה בערך',
  'pi.deep': 'עמוק',
  'pi.deepHint': '3 דק׳ בערך · רב-מסכי',
  'pi.deepSoon': 'בקרוב',
  'pi.deepSoonAria': 'בנייה עמוקה תגיע בקרוב — עדיין לא זמינה. הבנייה מתבצעת במצב מהיר.',
  'pi.generate': 'יצירה',
  // Home canvas
  'home.tap1': 'כותבים. מקישים.',
  'home.tap2': 'מתקינים.',
  'home.tapAria': 'כותבים. מקישים. מתקינים.',
  'home.canvasAria': 'דף הבית של Pomela: תארו אפליקציה לבנייה',
  'home.dockPlaceholder': 'תארו אפליקציה… מעקב הרגלים, תפריט, דף הזמנות',
  'help.heroTitle': 'אנחנו כאן. תמיד.',
  'help.heroSub': 'אנשים אמיתיים מאחורי כל תשובה. המייסד קורא כל הודעה, ורובן מקבלות תשובה מהורהרת תוך יום.',
  'help.sendMsg': 'שלחו לנו הודעה',
  'help.moreFrom': 'עוד מ-Pomela',
  'acc.signInSync': 'התחברו כדי לסנכרן את האפליקציות שאתם בונים בכל מכשיר: טלפון, טאבלט ומחשב.',
  'acc.signIn': 'התחברות',
  'acc.appsBuilt': 'אפליקציות שנבנו',
  'acc.devices': 'מכשירים',
  'acc.joined': 'הצטרפתם',
  'acc.memberSince': 'חברים מאז',
  'acc.signOut': 'התנתקות',
  'acc.signOutConfirm': 'להתנתק מ-Pomela?',
  'acc.managePlan': 'ניהול מנוי',
  'upg.free1': 'מספיק מקום לבנות ולשחק',
  'upg.free2': 'מנוע היצירה המלא, בדיוק כמו ב-Pro',
  'upg.free3': 'התקינו כל אפליקציה למסך הבית',
  'upg.free4': 'עובד גם לא מקוון, בלי חשבון',
  'upg.pro1': 'מאגר בנייה חודשי נדיב',
  'upg.pro2': 'דומיינים מותאמים לאפליקציות שלכם',
  'upg.pro3': 'ייצוא קוד בלחיצה אחת — פרויקט Vite סטנדרטי, שלכם לתמיד',
  'upg.pro4': 'יצירת וידאו עם AI',
  'upg.pro5': 'כל מה שיש ב-Free',
  'acc.signOutBody': 'האפליקציות שלכם נשארות במכשיר הזה. פשוט תפסיקו לסנכרן עד שתתחברו שוב.',
  'cop.s1t': 'מעקב הרגלים עם רצפים',
  'cop.s1d': 'הרגל יחיד, מבוסס-רצף, תיעוד בנגיעה. בסגנון Apple Health.',
  'cop.s1p': 'אפליקציית מעקב להרגל יחיד עם רצפים יומיים ותיעוד בנגיעה אחת',
  'cop.s2t': 'חפיסת כרטיסיות ללמידה',
  'cop.s2d': 'חפיסות לפי נושא, חזרה בריווח, כרטיסייה אחת בכל פעם.',
  'cop.s2p': 'אפליקציית כרטיסיות עם חפיסות לפי נושא, חזרה בריווח (spaced repetition) ומפגש לימוד יומי',
  'cop.s3t': 'יומן אימונים עם סטים וחזרות',
  'cop.s3d': 'תיעוד תרגילים, מעקב סטים וחזרות, וצפייה בנפח השבועי גדל.',
  'cop.s3p': 'יומן אימונים שבו אני מתעד תרגילים, סטים וחזרות, עם טיימרי מנוחה וגרף נפח שבועי',
  'cop.s4t': 'עזרו לי לבחור רעיון',
  'cop.s4d': 'עוד לא בטוחים? תארו איך אתם רוצים שזה ירגיש, לא מה שזה עושה.',
  'cop.s4p': 'אני רוצה לבנות אפליקציה קטנה ויפה אבל לא בטוח מה. עזרו לי לבחור משהו שמתאים לטעם שלי',
  'cop.s5t': 'אני תקוע על הפרומפט',
  'cop.s5d': 'ספרו לי מה יש לכם, ואעזור לחדד את הבריף.',
  'cop.s5p': 'ניסיתי לתאר את רעיון האפליקציה שלי אבל זה לא יוצא טוב. תוכל לשאול אותי כמה שאלות כדי לחלץ ממני את הבריף?',
  'cop.s6t': 'איך אני הופך את זה לאלגנטי יותר?',
  'cop.s6d': 'הביאו לי את האפליקציה שיצרתם ואציע שדרוגים קטנים.',
  'cop.s6p': 'בניתי אפליקציה עם Pomela והיא עובדת אבל מרגישה גנרית. אילו שיפורים קטנים בדרך כלל גורמים לאפליקציה להרגיש פרימיום?',
  'cop.placeholder': 'בקשו רעיון לאפליקציה…',
  'help.copyEmail': 'מעדיפים מייל? העתיקו את הכתובת שלנו',
  'help.responseTime': 'זמן תגובה ממוצע: פחות מ-24 שעות.',
  'help.topicAppIssue': 'האפליקציה שלי לא עובדת',
  'help.topicBilling': 'שאלת חיוב',
  'help.topicIdea': 'יש לי רעיון',
  'help.topicOther': 'משהו אחר',
  'help.emailLabel': 'המייל שלכם',
  'help.messageLabel': 'איך נוכל לעזור?',
  'help.messagePlaceholder': 'ספרו לנו מה קורה…',
  'help.send': 'שליחת הודעה',
  'help.sending': 'שולח…',
  'help.sent': 'ההודעה נשלחה',
  'help.sendAnother': 'שליחת הודעה נוספת',
  'help.copy': 'העתקה',
  'help.copied': 'הועתק',
  'help.foot': 'נבנה באהבה ב-2026',
  'help.errEmail': 'הזינו מייל תקין כדי שנוכל להשיב.',
  'help.errMsg': 'ספרו לנו עוד קצת כדי שנוכל לעזור.',
  'help.errRate': 'כבר שלחתם כמה. תנו לזה רגע.',
  'help.errGeneric': 'משהו השתבש. נסו שוב.',
  'help.errNetwork': 'תקלת רשת. בדקו את החיבור ונסו שוב.',
  'help.attachLabel': 'קבצים מצורפים (רשות)',
  'help.attachAdd': 'צירוף צילום מסך או וידאו',
  'help.attachHint': 'גררו לכאן או הקישו לבחירה — עד 5 קבצים · תמונות עד 10MB · וידאו עד 50MB',
  'help.attachRemove': 'הסרת קובץ מצורף',
  'help.attachErrType': 'ניתן לצרף תמונות (PNG, JPG, GIF, WebP) או וידאו (MP4, MOV, WebM) בלבד.',
  'help.attachErrSize': 'הקובץ גדול מדי — תמונות עד 10MB, וידאו עד 50MB.',
  'help.attachErrCount': 'ניתן לצרף עד 5 קבצים.',
  'help.uploading': 'מעלה… {pct}%',
  'upg.recommended': 'מומלץ',
  'upg.proBadge': 'Pomela Pro',
  'upg.perMonth': '/ חודש',
  'upg.free': 'חינם',
  'upg.forever': 'לתמיד',
  'upg.cancelTitle': 'ביטול בכל עת. בלי מלכודות.',
  'upg.onPro': 'אתם על Pro',
  'upg.starting': 'מתחיל תשלום…',
  'upg.goPro': 'שדרגו ל-Pro',
  'upg.signInPro': 'התחברו כדי לשדרג',
  'upg.yourPlan': 'התוכנית שלכם',
  'upg.checkoutErr': 'לא הצלחנו להתחיל תשלום. נסו שוב בעוד רגע.',
  // Guide knowledge base — atlas chrome (search, hero, how-it-works, browse, ask)
  'g.searchPh': 'חיפוש במדריך — „פרסום”, „נתונים”, „קרדיטים”…',
  'g.askPomy': 'שאלו את Pomy',
  'g.heroBadge': 'מתחילים',
  'g.heroCta': 'התחילו לבנות',
  'g.howTitle': 'איך Pomela עובדת',
  'g.step1t': 'תארו', 'g.step1s': 'אמרו מה אתם רוצים במשפט',
  'g.step2t': 'צרו', 'g.step2s': 'Pomela בונה מסכים ונתונים אמיתיים',
  'g.step3t': 'ערכו', 'g.step3s': 'שיחה כדי לחדד כל דבר',
  'g.step4t': 'פרסמו', 'g.step4s': 'התקנה ושיתוף בלחיצה',
  'g.browseTitle': 'עיון במדריך',
  'g.countSuffix': 'מדריכים',
  'g.askTitle': 'עדיין תקועים? שאלו את Pomy',
  'g.askSub': 'קבלו תשובה מיידית מעוזר ה‑AI שלכם',
  'g.noResultsT': 'אין במדריך תוצאה מתאימה',
  'g.noResultsS': 'נסו מילה אחרת, או שאלו את Pomy לתשובה מיידית.',
  'g.askResults': 'שאלו את Pomy במקום',
  'g.clearSearch': 'ניקוי חיפוש',
  'g.heroSub': 'Pomela הופכת משפט אחד לאפליקציה שלמה שאפשר להתקין: מסכים אמיתיים, אינטראקציות אמיתיות, נתונים אמיתיים. בלי קוד, בלי חנות אפליקציות, בלי „נחזור אליכם בעוד שישה שבועות”. זה המדריך לניצול המרבי שלה: מה לבנות, איך לנסח, והטריקים שהופכים אפליקציה מטובה למשהו שבאמת שלכם.',
  'g.toc1': 'מה אפשר לבנות',
  'g.toc2': 'איך להשיג את התוצאות הטובות ביותר',
  'g.toc3': 'יכולות מתקדמות',
  'g.toc4': 'טיפים ומלכודות',
  'g.s1e': 'הטווח', 'g.s1t': 'מה אפשר לבנות',
  'g.s1l': 'Pomela הכי שמחה לבנות אפליקציות מובייל אמיתיות, פונות-לקוח, מהסוג שעסק קטן או יוצר באמת משיקים. הנה איפה היא זוהרת, עם פרומפטים שאפשר לגנוב.',
  'g.s2e': 'ניסוח פרומפט', 'g.s2t': 'איך להשיג את התוצאות הטובות ביותר',
  'g.s2l': 'הסוד לאפליקציה מעולה הוא משפט מעולה. לא צריך להיות טכניים, צריך להיות ספציפיים. הנה ההבדל.',
  'g.s3e': 'להעמיק', 'g.s3t': 'לנצל את Pomela עד הסוף',
  'g.s3l': 'הדברים הטובים שרובם מפספסים. אלה מה שהופכים יצירה חד-פעמית לאפליקציה שבאמת מנהלים איתה את החיים או העסק.',
  'g.s4e': 'מניסיון', 'g.s4t': 'טיפים, דפוסים ומלכודות',
  'g.s4l': 'הדברים הקטנים שמייצרים אפליקציות טובות יותר באופן עקבי. גנבו את כולם.',
  'g.archFoot': 'כלל אצבע אחד: אם זו אפליקציה מבוססת-מסכים שאדם היה מקיש דרכה בטלפון, סביר ש-Pomela תוכל לבנות אותה. התוצאות החזקות ביותר מגיעות מהארכיטיפים שלמעלה, שם המנוע הכי דעתן והכי יפה.',
  'g.a1t': 'אפליקציות הזמנות ושירות',
  'g.a1b': 'מספרות, מרפאות, מאמנים, מורים, סטודיואים: כל מי שהלקוחות שלו צריכים לקבוע תור. הלקוחות בוחרים שירות ומשבצת בשתי הקשות; אתם שומרים 100% מכל הזמנה.',
  'g.a1e1': '„מספרה עם שירותים, ספרים, ותהליך קביעת תורים.”',
  'g.a1e2': '„אפליקציה למאמן אישי שבה לקוחות קובעים אימונים אישיים ורואים את התוכנית שלהם.”',
  'g.a1e3': '„אפליקציית שיעורים פרטיים עם מקצועות, משבצות פנויות, ואישור הזמנה.”',
  'g.a2t': 'חנויות ומסחר',
  'g.a2b': 'מכרו כל דבר: מוצרים, חבילות, מוצרים דיגיטליים. עיון, עגלה, תשלום, סיימתם. חברו את מעבד התשלומים שלכם והכסף נוחת בחשבון שלכם, לא שלנו.',
  'g.a2e1': '„חנות צמחים עם קטלוג, עמודי מוצר, עגלה ותשלום.”',
  'g.a2e2': '„מאפייה עם תפריט, אפשרויות הזמנה מותאמת, ותזמון איסוף.”',
  'g.a2e3': '„חנות בוטיק עם קולקציות, וריאציות מוצר, ועגלה.”',
  'g.a3t': 'מעקבים',
  'g.a3b': 'הרגלים, אימונים, כסף, צמחים, מצבי רוח, קריאה. מעקבים הם גן עדן נייטיב: הם חיים על מסך הבית, עובדים לא מקוון, ומזכירים לכם.',
  'g.a3e1': '„מעקב הרגלים עם רצפים יומיים וגרף התקדמות שבועי.”',
  'g.a3e2': '„יומן אימונים עם שגרות, טיימר מנוחה חי, ושיאים אישיים.”',
  'g.a3e3': '„מעקב תקציב עם קטגוריות, גרף הוצאות, ומגבלות חודשיות.”',
  'g.a4t': 'אפליקציות תוכן ופיד',
  'g.a4b': 'מתכונים, מדריכים, ניוזלטרים, תיקי עבודות, אוסף משאבים. פיד לעיון, תצוגת פירוט לצלילה, ורשימת „שמורים” לחזור אליה.',
  'g.a4e1': '„אפליקציית מתכונים עם קטגוריות, עמודי מתכון, ורשימת שמורים.”',
  'g.a4e2': '„מדריך עיר לטיולים עם שכונות, מקומות, ורשימה בסגנון מפה.”',
  'g.a4e3': '„רשימת קריאה עם מאמרים, תגיות, ותצוגת קורא נקייה.”',
  'g.a5t': 'מדריכים ואינדקסים',
  'g.a5b': 'מקומות מקומיים, רשימת צוות, ספריית משאבים, מערך אירועים. ניתן לחיפוש, סינון והקשה: האינדקס הצנוע הוא בסתר אחת האפליקציות השימושיות שאפשר להחזיק.',
  'g.a5e1': '„אינדקס של בתי קפה מקומיים עם מסננים ועמודי פירוט.”',
  'g.a5e2': '„אפליקציית כנס עם לוח זמנים, מרצים, ופרטי הרצאות.”',
  'g.a5e3': '„אינדקס צוות עם תפקידים, אנשי קשר, וחיפוש.”',
  'g.a6t': 'כלים ומחשבונים',
  'g.a6b': 'מחלק טיפים, ממיר יחידות, מחשבון משכנתא, כלי פנימי קטן לצוות. קטן, חד, ומשביע רצון באופן מוזר להתקין על מסך הבית.',
  'g.a6e1': '„מחשבון טיפ שמחלק את החשבון בין אנשים.”',
  'g.a6e2': '„מחשבון תעריף לפרילנסרים עם שעות, הוצאות, ומרווח.”',
  'g.a6e3': '„ממיר יחידות למידות בישול.”',
  'g.c1t': 'נקבו במסכים, בנתונים ובזרימה',
  'g.c1p': 'המנוע מבריק בהשלמת פרטים, אבל הוא לא יכול לקרוא את מחשבותיכם. אמרו לו אילו מסכים אתם רוצים, מה יש בהם, ואיך עוברים ביניהם.',
  'g.c1bad': '„תבנה לי אפליקציית מספרה.”',
  'g.c1good': '„אפליקציית מספרה עם מסך בית, תפריט שירותים מקובץ לפי קטגוריה, עמוד ספרים, וזרימת הזמנה שעוברת שירות ← ספר ← תאריך ← שעה ← אישור.”',
  'g.c2t': 'תארו את חוויית המובייל שאתם רוצים',
  'g.c2p': 'Pomela בונה אפליקציות מובייל נייטיב, אז דברו על זה כך. הזכירו סרגל לשוניות תחתון, כרטיסים, משיכה-לרענון, גיליון תחתון, רטט: המרקמים של אפליקציית טלפון אמיתית.',
  'g.c2b1': 'אמרו את הוייב: „אלגנטי ומינימלי”, „נועז ושובב”, „כהה וטכני”. אסתטיקה ברורה נותנת לכל האפליקציה אישיות.',
  'g.c2b2': 'נקבו בצבעים ובמצב הרוח אם יש לכם: „שנהב חמים עם הדגשת רוז-גולד”, „ירוק יער וטרקוטה”.',
  'g.c3t': 'הימנעו משלושת הדברים שמכשילים אנשים',
  'g.c3p': 'רוב האפליקציות המאכזבות מגיעות מאותם כמה פרומפטים. עקפו אותם ואתם מסודרים:',
  'g.c3b1': 'מעורפל מדי. „אפליקציה לעסק שלי” לא נותנת למנוע במה להיאחז. שני משפטים נוספים על מסכים ותוכן משנים הכול.',
  'g.c3b2': 'תיאור האינסטלציה, לא החוויה. אפליקציות Pomela יכולות לחבר אנשים, לאחסן נתונים חי, לקבל תשלומים אמיתיים ולקבל העלאת קבצים. מגיעים לזה בתיאור מה שהמשתמש עושה („מסך תשלום עם סיכום הזמנה”, „תנו לאנשים לשמור מועדפים”), לא חיווט הצד-שרת. בקשו את החוויה; Pomela מטפלת בשאר.',
  'g.c3b3': 'דחיסת מסך אחד. עשר פיצ׳רים בעמוד אחד נקראים כעומס. פזרו אותם על כמה מסכים עם סרגל לשוניות. זה נייטיב יותר והרבה יותר יפה.',
  'g.c4t': 'ואז שכללו, בלי לאבד דבר',
  'g.c4p': 'היצירה הראשונה היא נקודת התחלה, לא תשובה סופית. הקסם האמיתי הוא הלוך-ושוב: שנו דבר אחד בכל פעם וצפו באפליקציה מתפתחת, כשכל הנתונים ושאר האפליקציה נשארים שלמים לחלוטין.',
  'g.c4b1': '„הגדילו את ההירו בבית והוסיפו סלוגן.” „השתמשו בהדגשה חמה יותר.” „הוסיפו אזור נאמנות לפרופיל.” עריכות קטנות וספציפיות עובדות הכי טוב.',
  'g.c4b2': 'כל אפליקציית דוגמה ב-Pomela, כולל אלה בעמוד הבית שלנו, נבנתה בדיוק כך: פרומפט אחד, ואז שכלול בתיאור שינויים. בלי יוצאים מן הכלל, בלי מוקאפים.',
  'g.c5t': 'עריכה מהירה: שנו הכול, אל תאבדו דבר',
  'g.c5p': 'שכללו כל חלק באפליקציה מוגמרת בלי לבנות אותה מחדש. בתצוגה המקדימה, הקישו על שרביט הקסם, הצביעו על האלמנט המדויק שאתם מתכוונים אליו (כותרת, כפתור, כרטיס) ותארו את השינוי. Pomela מתקנת רק את החלק הזה ומשאירה את השאר, וכל הנתונים שלכם, ללא נגיעה.',
  'g.c5b1': 'זה ההבדל בין „לעשות הכול מחדש ולהתפלל” לבין „לדחוף את הכפתור הזה”. ערכו ללא חשש.',
  'g.c6t': 'התקנה, לא-מקוון ופוש: שלישיית הנייטיב',
  'g.c6p': 'כל אפליקציה שאתם בונים היא אפליקציה אמיתית שאפשר להתקין, לא סימנייה. זו כל המהות של Pomela: היא מרגישה נייטיב כי היא בעצם כזו.',
  'g.c6b1': 'התקנה: הוסיפו אותה למסך הבית, עם אייקון משלה, מסך מלא, בלי כרום של דפדפן. (אייפון: שיתוף ← „הוסף למסך הבית”. אנדרואיד: ⋮ ← „התקן אפליקציה”.)',
  'g.c6b2': 'לא-מקוון: היא ממשיכה לעבוד ברכבת התחתית. הנתונים שלכם חיים על המכשיר.',
  'g.c6b3': 'פוש: האפליקציות הנכונות יכולות להזכיר לאנשים, תזכורת הזמנה, „הגיע הזמן להשקות את הצמח”, רצף שעומד להישבר.',
  'g.c7t': 'חברו את התשלומים שלכם, שמרו 100%',
  'g.c7p': 'בונים משהו שמוכר? חברו את מעבד התשלומים שלכם והכסף הולך ישר אליכם. Pomela לעולם לא יושבת בינכם לבין הכסף של הלקוחות. זה העסק שלכם, החשבון שלכם, ה-100% שלכם.',
  'g.c8t': 'הוסיפו יכולות AI אמיתיות בתוך האפליקציה',
  'g.c8p': 'האפליקציות שלכם יכולות להיות חכמות גם: ממליץ, עוזר קטן, כפתור „סכם את זה”. תארו את היכולת החכמה שאתם רוצים ו-Pomela מחברת אותה, מונעת על ידי אותו מנוע שבנה את האפליקציה.',
  'g.c9t': 'איך קרדיטים עובדים (הגרסה בעברית פשוטה)',
  'g.c9p': 'יתרה פשוטה אחת מפעילה הכול: בניית אפליקציות ויכולות ה-AI שבתוכן שואבות מאותו מאגר. בלי חשבונות, בלי תמחור פר-פעולה לפענח.',
  'g.c9b1': 'נגמר? טענו בהקשה, או הביאו מפתח AI משלכם לשימוש בלתי מוגבל המחויב ישירות לספק שלכם.',
  'g.c9b2': 'Free נותן לכם הרבה מקום לבנות ולחקור. Pro פותח מאגר בנייה גדול בהרבה, יצירת וידאו עם AI, דומיינים מותאמים, ייצוא קוד והסרת התווית.',
  'g.c10t': 'הקוד שלכם, באמת שלכם',
  'g.c10p': 'בלי נעילה, אף פעם. מכל אפליקציה מוגמרת, פתחו ייצוא:',
  'g.c10b1': 'דחיפה ל-GitHub (Pro): הפרויקט המלא נוחת ב-repo חדש לגמרי, מוכן לשכפול ולהמשך בנייה.',
  'g.c10b2': 'הורדת הפרויקט: פרויקט Vite + Preact + Tailwind סטנדרטי, חינם לכולם. קחו אותו איתכם מתי שתרצו.',
  'g.c11t': 'שיתוף האפליקציה',
  'g.c11p': 'אפליקציות Pomela נוצרו כדי לנדוד. שתפו קישור והוא נפתח מיד עם תצוגה מקדימה יפהפייה.',
  'g.c11b1': 'הפעילו את קישור השיתוף בעורך כדי לקבל כתובת קצרה שאפשר לחלק.',
  'g.c11b2': 'שלחו את הקישור בוואטסאפ או ב-X, או הציגו את קוד ה-QR שלו, ישר מכרטיס השיתוף.',
  'g.c11b3': 'כל אפליקציה משותפת נושאת „נבנה עם Pomela” מעוצב, העבודה שלכם עושה לכם שיווק בשקט.',
  'g.c12t': 'דפוסים שפשוט עובדים',
  'g.c12b1': 'פתחו עם הירו. כותרת בטוחה אחת ופעולה ברורה בראש קובעות את הטון לכל האפליקציה.',
  'g.c12b2': 'שלוש עד חמש לשוניות, מקסימום. סרגל לשוניות תחתון ממוקד מנצח תפריט מסורבל בכל פעם.',
  'g.c12b3': 'בקשו תוכן דמו אמיתי. „זרעו אותה בתריסר מוצרים מציאותיים” גורם לאפליקציה להרגיש חיה מיד. אפליקציות ריקות נראות לא גמורות.',
  'g.c12b4': 'נקבו בצבע הדגשה אחד. הדגשה בטוחה אחת עושה ל„פרימיום” יותר מכל כמות של גרדיאנטים.',
  'g.c13t': 'מלכודות שכדאי לחמוק מהן',
  'g.c13b1': 'אל תבקשו התחברויות או חשבונות אמיתיים. תארו במקום זאת מסך פרופיל: אותה תחושה, בלי אינסטלציה שבורה.',
  'g.c13b2': 'אל תדחסו „דאשבורד” של אריחי מדדים. קיר של כרטיסי סטטיסטיקה נקרא כגנרי. שימו את העושר בפיד, ברשימה, או בתצוגת פירוט.',
  'g.c13b3': 'אל תכתבו מחדש כשאפשר לערוך. אם 90% נכון, ערכו כירורגית את ה-10%. יצירה מחדש מאפס זורקת את החלקים הטובים (ואת הנתונים שלכם).',
  'g.c14t': 'מתכון של 60 שניות לאפליקציה ראשונה מעולה',
  'g.c14p': 'העתיקו את הצורה הזו, החליפו ברעיון שלכם, ותקבלו משהו מקסים בניסיון הראשון:',
  'g.c14good': '„אפליקציית [סוג] בסגנון [וייב] בשם [שם]. מסכים: [מסך 1 עם מה שיש בו], [מסך 2], [מסך 3]. סרגל לשוניות תחתון ביניהם. [זרימה אחת ייחודית, צעד-אחר-צעד]. זרעו אותה בתוכן דמו מציאותי. [צבע / מצב רוח].”',
  'g.c14foot': 'ואז התקינו אותה, חיו איתה יום, וערכו את החלקים שמפריעים לכם. הלולאה הזו (לבנות, להשתמש, לדחוף) היא איך כל אפליקציית Pomela מעולה נוצרת.',
  'g.compareBad': 'מעורפל', 'g.compareGood': 'ספציפי',
  'g.foot': 'זהו המדריך. סגרו אותו ולכו לבנות משהו שבאמת תשתמשו בו. 🍊',
  'help.linkKb': 'מאגר ידע', 'help.linkKbSub': 'הדרכות, טיפים ודפוסי פרומפט.',
  'help.linkPress': 'עיתונות ושותפויות', 'help.linkPressSub': 'נשמח לשמוע מעיתונאים ויוצרים.',
  'acc.proFeat': 'מאגר גדול · וידאו AI · דומיינים מותאמים · ייצוא קוד · $19 לחודש',
  'acc.active': 'פעיל',
  'acc.proAll': 'מאגר בנייה גדול בהרבה, יצירת וידאו עם AI, דומיינים מותאמים וייצוא קוד. הכול שלכם.',
  'acc.upgraded': 'החשבון שלכם שודרג. מאגר הבנייה הגדול ויצירת הווידאו פתוחים.',
  'acc.signInVia': 'התחברות עם {p}',
  'acc.email': 'אימייל',
  'upg.refund1': 'בטלו מההגדרות ← חשבון בהקשה אחת. אתם נשארים Pro עד סוף מחזור החיוב הנוכחי.',
  'upg.refund2': 'בלי העלאות מחיר באמצע מחזור. אם נעלה אי פעם את המחיר, המנוי הקיים שלכם נשאר נעול בתעריף שבו נרשמתם.',
  'upg.refund3': 'האפליקציות שלכם הן שלכם. הן ממשיכות לעבוד על מסך הבית גם אחרי שתבטלו.',
  'upg.refundFoot': 'מחירים בדולר ארה״ב. מסים מחושבים בתשלום היכן שרלוונטי.',
  'cr.title': 'קרדיטים',
  'cr.sub': 'מפעיל את הבנייה שלכם ואת ה-AI של האפליקציות שלכם.',
  'cr.signIn': 'התחברו כדי לראות את הקרדיטים שלכם.',
  'cr.loadErr': 'לא הצלחנו לטעון את הקרדיטים שלכם.',
  'cr.left': 'קרדיטים שנותרו',
  'cr.exhausted': 'נגמרו לכם הקרדיטים',
  'cr.topupHint': 'טענו כדי להמשיך לבנות ולהשאיר את ה-AI של האפליקציות פעיל.',
  'cr.upgradeHint': 'שדרגו ל-Pro למאגר חודשי גדול בהרבה, או הוסיפו מפתח API משלכם.',
  'cr.buy': 'קניית קרדיטים',
  'cr.upgradeCta': 'שדרגו ל-Pro',
  'cr.useKey': 'השתמשו במפתח שלכם',
  'cr.buyMore': 'קנו עוד קרדיטים',
  'cr.upgradeMore': 'שדרגו לעוד',
  'cr.ownKey': 'השתמשו במפתח משלכם',
  'cr.unlimited': 'בלתי מוגבל',
  'cr.keyBilled': 'הקריאות רצות על הספק שלכם, ומחויבות אליכם.',
  'cr.remove': 'הסרה',
  'cr.yourKey': 'המפתח שלכם:',
  'cr.used': '{pct}% נוצלו.',
  'cr.alertTopup': 'טענו כדי שהבנייה שלכם וה-AI של האפליקציות ימשיכו לרוץ.',
  'cr.alertUpgrade': 'שדרגו ל-Pro למאגר חודשי גדול בהרבה.',
  'cr.keyLabel': 'מפתח ה-API של Anthropic שלכם',
  'cr.save': 'שמירה',
  'cr.keyStored': 'נשמר מוצפן בכספת שלכם. לא מוצג שוב, ולא נשלח לדפדפן.',
  'cr.errKeyRejected': 'המפתח נדחה.',
  'cr.errSaveKey': 'לא הצלחנו לשמור את המפתח.',
  'cr.errRemoveKey': 'לא הצלחנו להסיר את המפתח.',
  'cr.errTopup': 'לא הצלחנו להתחיל את הטעינה.',
  // לוח ניהול (תצוגת מייסד). ראשי תיבות BI (MRR/ARPU/DAU/WAU/MAU) נשארים באנגלית בכוונה.
  'a.admin': 'ניהול',
  'a.godMode': 'מצב מייסד',
  'a.welcome': 'ברוכים השבים, {email}',
  'a.founder': 'מייסד',
  'a.openAnalytics': 'פתחו אנליטיקה מלאה',
  'a.cap1': 'מגבלות יצירה עקופות',
  'a.cap2': 'קיצור OTP למייסד פעיל',
  'a.pulse': 'דופק הפלטפורמה',
  'a.pulseSub': 'מספרים חיים מהטבלאות החיות, נשמר במטמון לחמש דקות.',
  'a.totalUsers': 'סך המשתמשים',
  'a.paidUsers': 'משתמשים משלמים',
  'a.proSuffix': '{n}% Pro',
  'a.arpuSuffix': '{v} ARPU',
  'a.ofMau': '{n}% מ-MAU',
  'a.appsGenerated': 'אפליקציות שנוצרו',
  'a.last30': '30 הימים האחרונים',
  'a.dailyActive': 'משתמשים פעילים יומית',
  'a.today': 'היום',
  'a.todaySub': 'ספירות מאז תחילת היום ב-UTC.',
  'a.recent': 'פעילות אחרונה',
  'a.recentSub': '50 האירועים האחרונים מכל מכשיר וחשבון.',
  'a.noEvents': 'אין עדיין אירועים. יצירות, הרשמות והתחברויות יופיעו כאן ככל שהפלטפורמה רואה פעילות.',
  'a.tSignups': 'הרשמות',
  'a.tSignins': 'התחברויות',
  'a.tSessions': 'הפעלות שהתחילו',
  'a.tApps': 'אפליקציות שנוצרו',
  'a.tFree': 'יצירות בחינם',
  'a.tPro': 'יצירות Pro',
  'a.tCheckouts': 'תשלומים שהתחילו',
  'a.tSubs': 'מנויים חדשים',
  'a.anon': 'אנונימי',
  'a.sessionExpired': 'ההתחברות פגה',
  'a.sessionExpiredBody': 'התחברו שוב כדי לטעון את נתוני הלוח.',
  'a.forbidden': 'אין הרשאה',
  'a.forbiddenBody': 'החשבון מחובר אך אינו מורשה לנקודות הקצה של הניהול.',
  'a.loadErr': 'לא הצלחנו לטעון מדדים',
  'a.loadErrBody': 'הבקשה ל-/v1/admin/overview נכשלה ({msg}). גרפים ומדדים יופיעו כשהשער יהיה זמין.',
  'a.founderOnly': 'גישת מייסד בלבד',
  'a.founderOnlyBody': 'התחברו עם חשבון המייסד כדי לפתוח את הלוח.',
  'a.now': 'עכשיו',
  'a.secAgo': 'לפני {n} שנ׳',
  'a.minAgo': 'לפני {n} דק׳',
  'a.hrAgo': 'לפני {n} שע׳',
  'a.dayAgo': 'לפני {n} ימ׳',
  'a.wkAgo': 'לפני {n} שב׳',
  'a.evSignup': 'חשבון חדש',
  'a.evSignin': 'התחברות',
  'a.evSignout': 'התנתקות',
  'a.evSession': 'הפעלה התחילה',
  'a.evAppGen': 'אפליקציה נוצרה',
  'a.evAppRev': 'אפליקציה עודכנה',
  'a.evAppExp': 'אפליקציה יוצאה',
  'a.evAppClone': 'אפליקציה שוכפלה',
  'a.evAppInstall': 'אפליקציה הותקנה',
  'a.evCheckout': 'תשלום התחיל',
  'a.evSubStart': 'מנוי התחיל',
  'a.evSubUp': 'מנוי שודרג',
  'a.evSubCancel': 'מנוי בוטל',
  'a.evCredits': 'קרדיטים נוצלו',
  'a.tickets': 'פניות תמיכה',
  'a.ticketsSub': '{open} פתוחות · {total} בסך הכל',
  'a.loading': 'טוען…',
  'a.ticketsErr': 'לא הצלחנו לטעון פניות ({err}).',
  'a.noTickets': 'אין עדיין פניות תמיכה.',
  'a.stReviewed': 'נבדק',
  'a.stResolved': 'נפתר',
  'a.stDismissed': 'נדחה',
  'a.stOpen': 'פתוח',
  // מסך היצירה (מוצג בכל בנייה)
  'gen.building': 'בונים',
  'gen.fallbackName': 'האפליקציה החדשה שלכם',
  'gen.working': 'עובדים על זה',
  'gen.stage.blueprint': 'משרטטים את התוכנית',
  'gen.stage.hydrate': 'בונים את המסכים',
  'gen.stage.aesthetic': 'בוחרים את האסתטיקה',
  'gen.stage.polish': 'מלטשים את הפרטים',
  'gen.stage.ready': 'נוחתים על מסך הבית',
  'gen.status.0': 'משרטטים את המבנה…',
  'gen.status.1': 'בוחרים את הפלטה הנכונה…',
  'gen.status.2': 'מנסחים את המסכים…',
  'gen.status.3': 'בוחרים טיפוגרפיה שמרגישה נכון…',
  'gen.status.4': 'מרכיבים את הניווט…',
  'gen.status.5': 'מחווטים את האינטראקציות…',
  'gen.status.6': 'מחדדים את הריווח…',
  'gen.status.7': 'מוסיפים את הנגיעות האחרונות…',
  'gen.status.8': 'מכינים את חבילת ההתקנה…',
  'gen.status.9': 'עוד רגע נוחתים על מסך הבית…',
  'gen.ariaProgress': 'התקדמות היצירה',
  'gen.ariaMilestones': 'אבני דרך ביצירה',
  'gen.queueLine': 'אתם בתור',
  'gen.queueEtaSec': 'כ-{n} שנ׳',
  'gen.queueEtaMin': 'כ-{n} דק׳',
  'gen.queuePos': 'מקום #{n} בתור',
  'gen.queueDemand': 'יש ביקוש גבוה ל-Pomela כרגע, האפליקציה שלכם מתחילה ברגע שמתפנה מקום.',
  'gen.slow': 'אנחנו עדיין כאן, אפליקציות מורכבות יכולות לקחת את כל שלוש הדקות.',
  'gen.cancel': 'ביטול',
  'gen.timeout': 'זה לקח יותר מהצפוי. הפרומפט שלכם שמור, נסו שוב מתי שתרצו.',
  'gen.retry': 'נסו שוב',
  'gen.echo': 'נבנית מתוך:',
  'gen.attachHeading': 'נבנית ממה שצירפתם',
  'gen.attachAria': 'הקבצים שצורפו לבנייה הזו',
  'gen.attachKind.image': 'תמונה',
  'gen.attachKind.rows': 'גיליון',
  'gen.attachKind.pdf': 'PDF',
  'gen.attachRows': '{n} שורות',
  'gen.hold': 'כמעט שלכם — מחכים שהיא תסיים לעלות לאוויר. זו המסירה האמיתית, לא ספירה לאחור.',
  'gen.previewNote': 'תצוגת פריסה',
  'gen.reassure': 'זה בדרך כלל לוקח דקה-שתיים. השאירו את הטאב פתוח — האפליקציה תופיע ברגע שהיא באמת באוויר.',
  'gen.tipK': 'בזמן שאתם מחכים',
  'gen.tip.0': 'האפליקציה מותקנת למסך הבית בנגיעה אחת — בלי חנות אפליקציות.',
  'gen.tip.1': 'אחרי הבנייה, הקישו על כל אלמנט עם שרביט הקסם כדי לערוך רק אותו.',
  'gen.tip.2': 'בקשו מפומי לעצב מחדש את כל האפליקציה במילים פשוטות — בלי קוד.',
  'gen.tip.3': 'פרסום נפרד מעריכה: השינויים נשארים טיוטה עד שתחליטו לעלות לאוויר.',
  // §V2 המשך מסך הבית
  'hm.recents.title': 'להמשיך מאיפה שעצרתם',
  'hm.recents.open': 'לסביבת העבודה',
  'hm.sparks.title': 'לא בטוחים מה לבנות? התחילו מניצוץ.',
  'hm.sparks.sub': 'הקישו על רעיון והוא ייכנס ישר לתיבה למעלה — ערכו אותו, או בנו אותו כמו שהוא.',
  'hm.f.all': 'הכול',
  'hm.f.personal': 'אישי',
  'hm.f.business': 'עסק',
  'hm.f.hobby': 'תחביב',
  'hm.f.social': 'חברתי',
  'hm.useIdea': 'להשתמש ברעיון',
  'hm.spark.0.name': 'מעקב שתייה',
  'hm.spark.0.tag': 'כוס ביום, עם תזכורת עדינה.',
  'hm.spark.0.prompt': 'מעקב שתיית מים שמזכיר לי בעדינות לשתות לאורך היום',
  'hm.spark.1.name': 'תורים לסטודיו',
  'hm.spark.1.tag': 'תורים ומקדמות, בלי טלפונים הלוך-חזור.',
  'hm.spark.1.prompt': 'עמוד הזמנת תורים לסטודיו שלי עם מקדמות ותזכורות',
  'hm.spark.2.name': 'רצף שפה',
  'hm.spark.2.tag': '10 דקות ביום, במעקב כמו הרגל.',
  'hm.spark.2.prompt': 'מעקב רצף יומי של 10 דקות תרגול שפה',
  'hm.spark.3.name': 'תכנון טיול',
  'hm.spark.3.tag': 'מקום אחד לתאריכים, מסמכים ומי מגיע.',
  'hm.spark.3.prompt': 'מתכנן טיול שבו חברים רואים תאריכים, מסמכים משותפים ומי מגיע',
  'hm.spark.4.name': 'צנצנת תקציב',
  'hm.spark.4.tag': 'מעטפות להוצאות, בלי אקסל.',
  'hm.spark.4.prompt': 'אפליקציית תקציב עם מעטפות הוצאה במקום גיליון',
  'hm.spark.5.name': 'CRM לקוחות',
  'hm.spark.5.tag': 'כל לקוח, כל הערה, ברשימה אחת.',
  'hm.spark.5.prompt': 'CRM פשוט לשמירת הערות על כל לקוח ברשימה אחת',
  'hm.spark.6.name': 'קופסת מתכונים',
  'hm.spark.6.tag': 'המתכונים שלכם, בחיפוש, לא הולכים לאיבוד.',
  'hm.spark.6.prompt': 'קופסת מתכונים שבה אני שומר ומחפש את המתכונים שלי',
  'hm.spark.7.name': 'ערב משחקים RSVP',
  'hm.spark.7.tag': 'קישור אחד, ויודעים מי באמת מגיע.',
  'hm.spark.7.prompt': 'עמוד אישורי הגעה לערב משחקים שבו אני שולח קישור אחד ורואה מי מגיע',
  'hm.cta.ey': 'מוכנים כשאתם',
  'hm.cta.title': 'הרעיון שלכם במרחק משפט אחד.',
  'hm.cta.sub': 'בלי קוד, בלי המתנה, בלי אישור חנות. פשוט תארו אותו.',
  'hm.cta.btn': 'להתחיל לבנות',
  // חלון השדרוג (שערי מנוי)
  'pw.removeBadge.headline': 'שיהיה שלכם.',
  'pw.removeBadge.body': 'הסירו את התווית „נבנה עם Pomela” כדי שהאפליקציה תיקרא כמותג שלכם מהרגע הראשון.',
  'pw.removeBadge.b1': 'התווית נעלמת בכל התקנה',
  'pw.removeBadge.b2': 'צבע ערכת נושא מותאם מופיע בכרום של הדפדפן',
  'pw.removeBadge.b3': 'מאגר בנייה חודשי גדול בהרבה',
  'pw.removeBadge.b4': 'כל מה שיש ב-Pro: אפליקציות ללא הגבלה, דומיינים מותאמים, ייצוא קוד',
  'pw.removeBadge.cta': 'הסירו תווית ושדרגו',
  'pw.exportCode.headline': 'ייצאו את כל הפרויקט.',
  'pw.exportCode.body': 'קבלו את פרויקט ה-Vite + Preact + Tailwind המלא ודחפו אותו ישר ל-repo משלכם ב-GitHub בלחיצה אחת.',
  'pw.exportCode.b1': 'יצירת מאגר GitHub בלחיצה אחת',
  'pw.exportCode.b2': 'כולל את כל קוד המקור שנוצר: JSX, CSS, manifest',
  'pw.exportCode.b3': 'הקוד שלכם — נבנה ורץ בכל מקום; נתונים שמורים והתחברות נשארים ב-Pomela',
  'pw.exportCode.b4': 'כל מה שיש ב-Pro: אפליקציות ללא הגבלה, דומיינים מותאמים, הסרת תווית',
  'pw.exportCode.cta': 'ייצאו קוד ושדרגו',
  'pw.customDomain.headline': 'השיקו על דומיין משלכם.',
  'pw.customDomain.body': 'חברו דומיין מותאם כמו app.yourbrand.com. SSL אוטומטי, הקצאת DNS מיידית, בלי הגדרות.',
  'pw.customDomain.b1': 'חברו כל דומיין ראשי או תת-דומיין',
  'pw.customDomain.b2': 'SSL אוטומטי של Let’s Encrypt, בלי תעודות לנהל',
  'pw.customDomain.b3': 'הנחיות DNS מובנות: העתיקו והדביקו את הרשומה',
  'pw.customDomain.b4': 'כל מה שיש ב-Pro: אפליקציות ללא הגבלה, ייצוא קוד, הסרת תווית',
  'pw.customDomain.cta': 'חברו דומיין ושדרגו',
  'pw.for': 'עבור',
  'pw.cadence': '/ חודש · ביטול בכל עת',
  'pw.maybeLater': 'אולי אחר כך',
  'pw.fineprintPitch': 'תשלום מאובטח. המנוי שלכם מפעיל את המנוע וכל יכולת של Pomela שאתם כבר משתמשים בה.',
  'pw.back': 'חזרה',
  // §LG2 — Pomela Checkout (אמיתי, מוטמע, מעובד ע"י PayPal; בלי ממשק תשלום מדומה)
  'pc.proTitle': 'שדרוג ל-Pomela Pro',
  'pc.proLine': 'Pomela Pro · ${v} לחודש · ביטול בכל עת',
  'pc.topupTitle': 'קניית קרדיטים',
  'pc.topupSub': 'הוסיפו קרדיטים כדי להמשיך לבנות.',
  'pc.pkgLine': '{n} קרדיטים · ${v} חד-פעמי',
  'pc.choosePack': 'בחרו חבילת קרדיטים',
  'pc.loading': 'טוען תשלום מאובטח…',
  'pc.unavailable': 'התשלום אינו זמין כרגע. נסו שוב בעוד רגע.',
  'pc.disabled': 'תוכניות בתשלום עדיין אינן זמינות. לא חויבתם, ובינתיים השימוש בפומלה חינם.',
  'pc.sdkError': 'חלון התשלום נתקל בבעיה. לא חויבתם — נסו שוב.',
  'pc.fallbackNote': 'התשלום המוטמע לא נטען כאן. אפשר לסיים בבטחה ב-PayPal.',
  'pc.fallbackCta': 'המשיכו ב-PayPal',
  'pc.verifying': 'מאמתים את התשלום…',
  'pc.credited': 'נוספו {n} קרדיטים!',
  'pc.balanceNow': 'היתרה שלכם כעת {n} קרדיטים.',
  'pc.already': 'התשלום הזה כבר עובד — הקרדיטים בחשבונכם.',
  'pc.proActive': 'ברוכים הבאים ל-Pomela Pro',
  'pc.doneCta': 'סיום',
  'pc.failed': 'התשלום לא הושלם',
  'pc.failedLine': 'התשלום לא הושלם, כך שלא חויבתם ולא נוספו קרדיטים. אפשר לנסות שוב.',
  'pc.webhookPending': 'התשלום אושר — משלימים הפעלה…',
  'pc.webhookPendingLine': 'PayPal אישר את התשלום. ההפעלה עשויה לקחת דקה ומסתיימת אוטומטית — אם התוכנית לא מתעדכנת, פנו לתמיכה ונדאג לזה.',
  'pc.supportTitle': 'התשלום התקבל — הקרדיטים עוד לא מוצגים',
  'pc.supportLine': 'ייתכן שהתשלום עבר, אך לא הצלחנו לאשר את היתרה החדשה. אל תשלמו שוב — פנו לתמיכה ונטפל בזה.',
  'pc.supportCta': 'פנו לתמיכה',
  'pc.cancelled': 'התשלום בוטל — לא חויבתם.',
  'pc.creditedToast': 'נוספו {n}+ קרדיטים לחשבונכם',
  'pc.captureFailed': 'לא הצלחנו לאשר את התשלום. אם אישרתם אותו ב-PayPal, פנו לתמיכה — אל תשלמו שוב.',
  'pc.payNote': 'התשלומים מעובדים באופן מאובטח על ידי PayPal. Pomela לעולם לא רואה את פרטי הכרטיס שלכם.',
  // §LG2 — באג באונטי
  'bb.title': 'באג באונטי',
  'bb.sub': 'באג אמיתי שניתן לשחזר מזכה ב-{n} קרדיטים.',
  'bb.cta': 'דיווח על באג',
  'bb.category': 'קטגוריה',
  'bb.cat.generation': 'יצירת אפליקציה',
  'bb.cat.editing': 'עריכה',
  'bb.cat.ui': 'ממשק',
  'bb.cat.payment': 'תשלומים וקרדיטים',
  'bb.cat.data-loss': 'אובדן נתונים',
  'bb.cat.security': 'אבטחה ופרטיות',
  'bb.cat.other': 'אחר',
  'bb.desc': 'מה קרה?',
  'bb.descPh': 'תארו את הבאג — מה ציפיתם לעומת מה שקרה בפועל (לפחות 20 תווים).',
  'bb.steps': 'שלבי שחזור',
  'bb.stepsPh': '1. פתחו… 2. הקישו… 3. הבאג מופיע.',
  'bb.urlPh': 'קישור לאפליקציה או לעמוד (אופציונלי)',
  'bb.submit': 'שליחת דיווח',
  'bb.cancel': 'ביטול',
  'bb.fine': 'הדיווחים נבדקים על ידי אדם. באג מאומת, ייחודי וניתן לשחזור מזכה ב-{n} קרדיטים — כפילויות ולא-באגים לא.',
  'bb.mine': 'הדיווחים שלי',
  'bb.submitted': 'הדיווח נשלח — נבדוק אותו בקרוב.',
  'bb.tooShort': 'הוסיפו תיאור מלא יותר ואת שלבי השחזור.',
  'bb.tooMany': 'הגעתם למכסת הדיווחים היומית — הדיווחים הקיימים בבדיקה.',
  'bb.submitErr': 'לא הצלחנו לשלוח את הדיווח. נסו שוב.',
  'bb.st.submitted': 'בבדיקה',
  'bb.st.needs_info': 'נדרש מידע',
  'bb.st.accepted': 'התקבל',
  'bb.st.duplicate': 'כפילות',
  'bb.st.rejected': 'לא באג באונטי',
  'bb.st.awarded': 'הוענק',
  'pw.welcome': 'ברוכים הבאים ל-Pomela Pro',
  'pw.upgradedReload': 'החשבון שלכם שודרג. טוענים מחדש כדי ששאר האפליקציה תתעדכן…',
  'pw.toastSignInFirst': 'התחברו קודם, ואז ניקח אתכם לתשלום.',
  'pw.toastSignInUpgrade': 'התחברו כדי לשדרג.',
  'pw.toastBillingOffline': 'החיוב במצב לא מקוון זמנית. נסו שוב בקרוב.',
  'pw.toastConnLost': 'החיבור אבד. בדקו את האינטרנט ונסו שוב.',
  // קיר ההתחברות (חסם הבנייה החינמית)
  'auth.cta': 'התחברו, זה חינם',
  'auth.freeForever': 'חינם לתמיד',
  'auth.noCard': 'בלי כרטיס',
  'auth.magicLink': 'התחברות בקישור קסם',
  'auth.maybeLater': 'אולי אחר כך',
  'auth.ariaSheet': 'התחברו כדי להמשיך לבנות',
  'auth.ariaDismiss': 'גררו למטה לסגירה',
  'auth.ariaGuarantees': 'הבטחות המסלול החינמי',
  // כרטיס שיתוף (משטח ויראלי אחרי יצירה)
  'share.text': 'בניתי את {name} במשפט אחד עם Pomela ✨',
  'share.headline0': 'האפליקציה שלכם באוויר. שלחו אותה ל-5 המשתמשים הראשונים שלכם.',
  'share.headlineDone': 'הגעתם ל-5 שיתופים. תמשיכו בתנופה.',
  'share.toGoOne': 'עוד שיתוף אחד, תמשיכו בתנופה.',
  'share.toGoMany': 'עוד {n} שיתופים, תמשיכו בתנופה.',
  'share.share': 'שיתוף',
  'share.shareLink': 'שיתוף קישור',
  'share.ariaShare': 'שתפו את האפליקציה',
  'share.ariaCopy': 'העתקת קישור השיתוף',
  'share.ariaQr': 'הצגת קוד QR',
  // לוח הפקודות (Cmd+K)
  'cmd.new': 'התחילו פרויקט חדש',
  'cmd.newHint': 'פתחו את דף הבית עם פרומפט חדש',
  'cmd.ai': 'עברו ל-Pomy',
  'cmd.aiHint': 'סיעור מוחות לרעיון אפליקציה עם השותף',
  'cmd.workspaceHint': 'פתחו מחדש אפליקציות שכבר בניתם',
  'cmd.kbHint': 'טיפים, פרומפטים ודפוסים לבנייה עם Pomela',
  'cmd.placeholder': 'לאן תרצו ללכת?',
  'cmd.aria': 'לוח פקודות',
  'cmd.ariaSearch': 'חיפוש פקודות',
  'cmd.ariaCommands': 'פקודות',
  'cmd.close': 'סגירה',
  // מצבי שגיאה של Pomy
  'cop.errBusyWait': 'השותף עסוק. המתינו {n} שניות לפני הרעיון הבא.',
  'cop.errBusyMoment': 'השותף עסוק. תנו לזה רגע לפני הרעיון הבא.',
  'cop.errAuth': 'התחברו כדי להמשיך לעשות סיעור מוחות עם השותף.',
  'cop.errServer': 'השותף לוקח נשימה. נסו שוב בעוד רגע.',
  'cop.errGeneric': 'השותף נתקל בתקלה. נסו שוב בעוד רגע.',
  'cop.errNetwork': 'החיבור אבד. בדקו את האינטרנט ונסו שוב.',
  // מצבי שגיאה של צירוף קובץ
  'pi.attachErr': 'לא הצלחנו לצרף את הקובץ. נסו שוב.',
  'pi.attachType': 'סוג הקובץ הזה אינו נתמך. צרפו תמונה, PDF, CSV או קובץ Excel.',
  'pi.attachSize': 'הקובץ קצת גדול מדי. תמונות עד 8MB, מסמכים עד 15MB.',
  'pi.attachRate': 'צירפתם הרבה היום. נסו שוב בעוד זמן מה.',
  // מצבי דחיית עריכה כירורגית
  'se.rejectTitle': 'השינוי נדחה, שום דבר לא אבד',
  'se.rejectBody': 'האפליקציה שלכם המשיכה להציג את הגרסה הקודמת.',
  'se.rejectNetwork': 'לא הצלחנו להגיע לסטודיו. בדקו את החיבור ונסו שוב.',
  // דף הנחיתה הציבורי (רשימת המתנה) — הדלת הקדמית
  'lp.navCta': 'גישה מוקדמת',
  'lp.heroTitle': 'אפליקציות שמרגישות <span class="lp-title-accent">נייטיב</span>.<br /> בנויות ממשפט אחד.',
  'lp.heroEyebrow': 'כותבים. מקישים. מתקינים.',
  'lp.heroSub': 'Pomela בונה אפליקציית מובייל אמיתית, לא רק אתר. היא מותקנת על מסך הבית ועובדת גם לא מקוון. תארו אותה, הקישו פעם אחת, והיא על הטלפון שלכם תוך כדקה.',
  'lp.heroTrust': 'מייל אחד בהשקה. בלי ספאם, בלי שיווק. מבטיחים.',
  'lp.formSending': 'שולחים…',
  'lp.formSuccess': 'אתם ברשימה ✓',
  'lp.formAriaEmail': 'כתובת מייל',
  'lp.s1Title': 'משפט הופך לאפליקציה אמיתית תוך כדקה.',
  'lp.s1Sub': 'אין קנבס ללמוד, אין תבניות להיאבק בהן. אתם מתארים בשפה פשוטה, Pomela בונה את הכול, וזה נוחת על מסך הבית: ניתן להתקנה, מוכן לעבודה לא מקוונת, שלכם.',
  'lp.step1Kind': 'כותבים',
  'lp.step1Title': 'תארו במשפט אחד',
  'lp.step1Body': '„אפליקציית הזמנות לסטודיו שלי עם יומן ושעות פעילות.” זה כל הבריף. מילים פשוטות, בלי כלי עיצוב, בלי קוד.',
  'lp.step2Kind': 'מקישים',
  'lp.step2Title': 'Pomela בונה את הדבר האמיתי',
  'lp.step2Body': 'מסכים אמיתיים, ניווט אמיתי, נתונים אמיתיים שנשמרים. לא שרטוט ולא מוקאפ, אפליקציה עובדת ושלמה. שכללו אותה בתיאור שינויים.',
  'lp.step3Kind': 'מתקינים',
  'lp.step3Title': 'הוסיפו למסך הבית',
  'lp.step3Body': 'הקשה אחת והיא אייקון על הטלפון שלכם. נפתחת במסך מלא ועובדת לא מקוון. היא מרגישה נייטיב כי בטלפון, היא בעצם כזו.',
  'lp.s2Eyebrow': 'ראש הגשר',
  'lp.s2Title': 'לאנשים שלא היו קוראים לעצמם מתכנתים.',
  'lp.s2Sub': 'אפליקציות פונות-לקוח ליוצרים ולעסקים קטנים, מהסוג שבדרך כלל הייתם משלמים עליו לסוכנות חמש ספרות ומחכים שלושה חודשים.',
  'lp.uc1Kind': 'הזמנות ושירות',
  'lp.uc1Title': 'הלקוחות שלכם מזמינים בשתי הקשות',
  'lp.uc1Body': 'מרפאות, מאמנים, מורים, מספרות. הציגו את השירותים וקבלו תורים, כשכל התורים במקום אחד. חברו את מעבד התשלומים שלכם ושמרו 100%.',
  'lp.uc2Kind': 'חנויות · בקרוב',
  'lp.uc2Title': 'מכרו כל דבר. הכסף שלכם.',
  'lp.uc2Body': 'עיון, עגלה, תשלום: חנות אמיתית על מסך הבית. התשלומים עוברים דרך החשבון שלכם, לא שלנו. אנחנו לעולם לא יושבים בינכם לבין הכסף של הלקוחות.',
  'lp.uc3Kind': 'מעקבים',
  'lp.uc3Title': 'חי על מסך הבית',
  'lp.uc3Body': 'הרגלים, אימונים, מלאי, כרטיסיות. מעקבים הם גן עדן נייטיב: מתקינים פעם אחת, עובד לא מקוון, תמיד במרחק הקשה ממסך הבית.',
  'lp.s3Eyebrow': 'למה זה מרגיש אחרת',
  'lp.s3Title': 'הפרטים שבונים אחרים מדלגים עליהם.',
  'lp.s3Sub': 'אתר בלשונית דפדפן הוא לא אפליקציה. אלה הדברים שגורמים לאפליקציות Pomela להרגיש כמו הדבר האמיתי.',
  'lp.d1Title': 'אשליית הנייטיב',
  'lp.d1Body': 'התקנה למסך הבית עם אייקון משלה, פתיחה במסך מלא, עבודה לא מקוונת. היא מרגישה נייטיב כי בטלפון, היא בעצם כזו.',
  'lp.d2Title': 'שנו דבר אחד, שמרו על כל השאר',
  'lp.d2Body': 'הצביעו על כל חלק באפליקציה ואמרו מה לשנות. Pomela מעדכנת רק את זה ומשאירה את השאר, וכל הנתונים שלכם, בדיוק כפי שהיו. ערכו ללא חשש.',
  'lp.d3Title': 'ממשפט לאפליקציה תוך כדקה',
  'lp.d3Body': 'לא שרטוט. לא אב-טיפוס. אפליקציה שלמה, עובדת וניתנת להתקנה: מסכים אמיתיים, אינטראקציות אמיתיות, נתונים אמיתיים, בזמן שהקפה שלכם עוד חם.',
  'lp.d4Title': 'באמת שלכם',
  'lp.d4Body': 'ייצאו את כל הפרויקט ל-GitHub או הורידו אותו: אפליקציית Vite + Preact + Tailwind סטנדרטית. בלי נעילה, אף פעם. קחו את הקוד שלכם מתי שתרצו.',
  'lp.bipEyebrow': 'נבנה בגלוי',
  'lp.bipTitle': 'צוות קטן, אובססיבי במקצת לגרום ליצירת מובייל להרגיש כמו קסם.',
  'lp.bipBody': 'Pomela נבנית בגלוי, על ידי אנשים שחושבים שהפיכת משפט לאפליקציה אמיתית בטלפון צריכה להרגיש כמו קסם, לא כמו עבודה. אנחנו פותחים את הדלתות בקרוב, ורשימת ההמתנה נכנסת ראשונה.',
  'lp.bipStat1Num': 'כדקה',
  'lp.bipStat1Lbl': 'ממשפט<br />לאפליקציה ניתנת להתקנה',
  'lp.bipStat2Num': '100%',
  'lp.bipStat2Lbl': 'מכל תשלום נשאר<br />שלכם (המעבד שלכם)',
  'lp.bipStat3Num': '0',
  'lp.bipStat3Lbl': 'נעילה, ייצאו<br />את הקוד מתי שתרצו',
  'lp.bipNote': 'יכולנו למלא את החלק הזה בציטוטים של חמישה כוכבים מאנשים שלא קיימים. אנחנו מעדיפים להרוויח את האמיתיים. הסיפור שלכם יכול להיות הראשון: <a href="#waitlist">הצטרפו לרשימת ההמתנה</a>.',
  'lp.finalTitle': 'היו ראשונים בדלת.',
  'lp.finalSub': 'השאירו את המייל ותהיו מהראשונים להפוך משפט לאפליקציה על מסך הבית.',
  'lp.finalTrust': 'מייל אחד בהשקה. בלי ספאם, בלי שיווק.',
  'lp.footPrivacy': 'פרטיות',
  'lp.footTerms': 'תנאים',
  'lp.footSecurity': 'אבטחה',
  'lp.footTag': 'כותבים. מקישים. מתקינים.',
  'lp.ariaRoot': 'Pomela, הצטרפו לרשימת ההמתנה',
  'lp.ariaPrimary': 'ראשי',
  'lp.ariaLegal': 'משפטי',
  'lp.errEmail': 'זה לא נראה כמו מייל. לנסות שוב?',
  'lp.errNetwork': 'לא הצלחנו להגיע לרשימה כרגע. בדקו את החיבור ונסו שוב.',
  // הסבר התשלומים (PaymentConnect)
  'pc.aria': 'תשלומים',
  'pc.title': 'תשלומים',
  'pc.sub': 'מכרו מהאפליקציות שלכם, התשלום ישר אליכם.',
  'pc.errSignIn': 'התחברו כדי לחבר תשלומים.',
  'pc.errLoad': 'לא הצלחנו לטעון את הגדרות התשלום.',
  'pc.errRejected': 'המפתח נדחה.',
  'pc.errConnect': 'לא הצלחנו להתחבר.',
  'pc.errDisconnect': 'לא הצלחנו להתנתק.',
  'pc.connected': '{p} מחובר',
  'pc.connectedSub': 'התשלומים הולכים ישר לחשבון ה-{p} שלכם.',
  'pc.disconnect': 'ניתוק',
  'pc.connectCta': 'חברו מעבד תשלומים',
  'pc.secretAria': 'מפתח סודי של המעבד',
  'pc.connect': 'חיבור',
  'pc.cancel': 'ביטול',
  'pc.encrypted': 'נשמר מוצפן בכספת שלכם. Pomela לעולם לא מחזיקה את הכסף שלכם ולא לוקחת עמלה.',
  'pc.how': 'איך התשלומים עובדים',
  'pc.how1': '<span class="font-semibold text-[color:var(--fg)]">1 · חברו את המעבד שלכם.</span> הדביקו מפתח סודי מחשבון Stripe או PayPal משלכם. הוא מוצפן באחסון ולא מוצג שוב.',
  'pc.how2': '<span class="font-semibold text-[color:var(--fg)]">2 · האפליקציות שלכם מתחילות לגבות.</span> כל אפליקציה שבניתם עם עגלה מקבלת תשלום עובד: לקוחות מקישים על תשלום ומשלימים בעמוד המאובטח של המעבד שלכם. הכסף נוחת בחשבון שלכם; Pomela לא נוגעת בו ולא לוקחת עמלה.',
  'pc.how3': '<span class="font-semibold text-[color:var(--fg)]">3 · עד אז, התשלום כן.</span> בלי חיבור, העגלות עדיין עובדות אבל התשלום אומר ללקוחות שהחנות עוד לא גובה תשלומים. שום דבר מזויף, בלי מבוי סתום.',
  'pc.howReq': 'דרישות: חשבון אצל אחד מארבעת המעבדים והמפתח הסודי שלו (למשל Stripe ← Developers ← API keys). החזרים, קבלות ומחלוקות מטופלים בלוח הבקרה של המעבד שלכם.',
  // חשבון — ניהול מנוי + מחיקת נתונים
  'acct.delErrConn': 'החיבור אבד, החשבון שלכם לא נמחק. בדקו את הרשת ונסו שוב.',
  'acct.delErrService': 'לא הצלחנו להגיע לשירות המחיקה. החשבון שלכם לא השתנה. נסו שוב בעוד רגע, או שלחו מייל ל-support@pomela.app.',
  'acct.delErrExpired': 'ההתחברות פגה. רעננו ונסו שוב.',
  'acct.delErrStart': 'לא הצלחנו להתחיל את המחיקה. נסו שוב.',
  'acct.delErrResponse': 'שירות המחיקה החזיר תשובה לא צפויה. החשבון שלכם לא השתנה. נסו שוב.',
  'acct.delErrFinish': 'אישרנו את בקשת המחיקה אבל לא הצלחנו לסיים את הסרת החשבון. הנתונים שלכם לא השתנו. נסו שוב.',
  'acct.delErrConn2': 'החיבור אבד. החשבון שלכם לא השתנה. בדקו את האינטרנט ונסו שוב.',
  'acct.cancelInfo': 'תישארו Pro עד סוף מחזור החיוב הנוכחי, ואז החשבון עובר ל-Free. שאלות? <strong style="color:#fff">support@pomela.app</strong> (או דרך התמיכה בתפריט). אנחנו קוראים כל הודעה.',
  'acct.cancelling': 'מבטלים…',
  'acct.cancelPro': 'ביטול Pro',
  'acct.keepPro': 'להישאר Pro',
  'acct.cancelFailed': 'לא הצלחנו לבטל את המנוי — נסו שוב בעוד רגע.',
  // §S680 — המחיקה היא ברמת המכשיר (deviceId), והטקסט מבטיח בדיוק את זה.
  'acct.delTitle': 'למחוק את נתוני Pomela שלכם במכשיר הזה?',
  'acct.delBody': 'זה מוחק לצמיתות את האפליקציות, היסטוריית הבנייה והמצב המסונכרן שנוצרו במכשיר הזה. אפליקציות שנבנו במכשירים אחרים שלכם לא נפגעות. אפשר לייצא הכול קודם מ-<strong>הגדרות ← נתונים ופרטיות</strong>.',
  'acct.delList1': 'החשבון והנתונים שלכם נמחקים מיד. קישורים לאפליקציות שפורסמו עשויים להפסיק לפעול בתוך 24 שעות.',
  'acct.delList2': 'יומני גיבוי נשמרים עד 30 יום למניעת הונאה, ואז נמחקים.',
  'acct.delList3': 'תוכלו ליצור חשבון חדש מאוחר יותר עם אותו מייל.',
  'acct.delTypeLabel': 'הקלידו <strong>delete</strong> לאישור',
  'acct.deleting': 'מוחקים את הנתונים שלכם…',
  'acct.delConfirm': 'מחיקה לצמיתות',
  'acct.keepAccount': 'להשאיר את הנתונים שלי',
  // שגיאות ייצוא ל-GitHub / הורדה
  'ex.errToken': 'הטוקן לא עבד. בדקו שהוא תקף ולא פג תוקפו.',
  'ex.errPerms': 'הטוקן לא יכול ליצור מאגרים. הוא צריך הרשאת יצירת מאגר וכתיבת תוכן.',
  'ex.errRepoName': 'כבר יש לכם מאגר בשם הזה. נסו שם אחר למטה.',
  'ex.errNoToken': 'הדביקו את הטוקן של GitHub.',
  'ex.errNoCode': 'הקוד של האפליקציה הזו לא זמין במכשיר הזה.',
  'ex.errPro': 'ייצוא ל-GitHub הוא יכולת Pro.',
  'ex.errGeneric': 'הייצוא נכשל. נסו שוב בעוד רגע.',
  'ex.errReach': 'לא הצלחנו להגיע ל-GitHub. נסו שוב בעוד רגע.',
  'ex.errDl': 'ההורדה נכשלה. נסו שוב בעוד רגע.',
  // שגיאות דומיין מותאם + פקדים
  'dom.errInvalid': 'זה לא נראה כמו דומיין. נסו משהו כמו app.yoursite.com',
  'dom.errTaken': 'הדומיין הזה כבר מחובר לאפליקציה אחרת.',
  'dom.errNotConfigured': 'דומיינים מותאמים עדיין לא מופעלים בפריסה הזו.',
  'dom.errNotFound': 'לא הצלחנו למצוא את האפליקציה הזו במכשיר שלכם.',
  'dom.errProvision': 'לא הצלחנו להגדיר את הדומיין הזה כרגע. נסו שוב בקרוב.',
  'dom.errPro': 'דומיינים מותאמים הם יכולת Pro.',
  'dom.errGeneric': 'משהו השתבש. נסו שוב בעוד רגע.',
  'dom.errReach': 'לא הצלחנו להגיע לשרת. נסו שוב בעוד רגע.',
  'dom.ariaConnect': 'חברו דומיין מותאם',
  'dom.connecting': 'מתחברים…',
  'dom.connect': 'חברו דומיין',
  // תצוגה מקדימה / פרסום
  'pv.errReach': 'לא הצלחנו להגיע לאפליקציה שלכם. ייתכן שמארח התצוגה המקדימה הופעל מחדש. נסו שוב.',
  'pv.errLoad': 'לא הצלחנו לטעון את האפליקציה שלכם. ייתכן שמארח התצוגה המקדימה הופעל מחדש. נסו שוב.',
  'pv.versionHistory': 'היסטוריית גרסאות',
  'pv.viewingOriginal': 'צופים במקור',
  'pv.unavailable': 'התצוגה המקדימה לא זמינה',
  'pv.preparing': 'מכינים את התצוגה המקדימה…',
  'pv.wandOn': 'שרביט הקסם פעיל, הקישו על אלמנט',
  'pv.wand': 'שרביט קסם',
  'pv.publishErr': 'לא הצלחנו לפרסם. נסו שוב בעוד רגע.',
  // אנליטיקת אפליקציה
  'an.errSignIn': 'התחברו כדי לראות את האנליטיקה שלכם.',
  'an.errLoad': 'לא הצלחנו לטעון את האנליטיקה.',
  // כרטיס אפליקציה (סביבת עבודה)
  'ac.share': 'שיתוף',
  'ac.linkCopied': 'הקישור הועתק',
  // שבב הרצף
  'streak.chip': 'רצף בנייה של {current} ימים, שיא {best}',
  // הצעות יכולת הבאה (שבבי „תמשיכו לבנות” אחרי יצירה)
  'nc.header': 'תמשיכו לבנות, דבר אחד בכל פעם',
  'nc.or': 'או',
  'cap.food_ordering': 'הוסיפו הזמנה אונליין', 'cap.food_ordering.why': 'תנו ללקוחות להזמין ולשלם מהתפריט',
  'cap.food_photos': 'הוסיפו תמונות פריטים', 'cap.food_photos.why': 'מנות עם תמונות נמכרות הרבה יותר טוב',
  'cap.fit_chart': 'הוסיפו גרף התקדמות', 'cap.fit_chart.why': 'ראו מגמות ושמרו על מוטיבציה',
  'cap.fit_reminders': 'הוסיפו תזכורות', 'cap.fit_reminders.why': 'דחיפה קטנה מחזירה אנשים כל יום',
  'cap.shop_checkout': 'הוסיפו תשלום', 'cap.shop_checkout.why': 'הפכו עיון למכירות',
  'cap.shop_payments': 'קבלו תשלומים', 'cap.shop_payments.why': 'קבלו תשלום ישר באפליקציה',
  'cap.event_rsvp': 'הוסיפו אישור הגעה', 'cap.event_rsvp.why': 'דעו מי מגיע',
  'cap.event_share': 'הוסיפו הזמנות', 'cap.event_share.why': 'תנו לאנשים להזמין חברים',
  'cap.course_progress': 'הוסיפו מעקב התקדמות', 'cap.course_progress.why': 'הראו כמה הם התקדמו',
  'cap.course_accounts': 'הוסיפו התחברות', 'cap.course_accounts.why': 'כדי שההתקדמות תישמר לחשבון שלהם',
  'cap.u_accounts': 'הוסיפו התחברות', 'cap.u_accounts.why': 'כדי שהנתונים של כל אחד יהיו שלו',
  'cap.u_chart': 'הוסיפו תובנות', 'cap.u_chart.why': 'הציגו את הנתונים במבט אחד',
  'cap.u_reminders': 'הוסיפו תזכורות', 'cap.u_reminders.why': 'החזירו אנשים',
  'cap.u_share': 'הוסיפו שיתוף', 'cap.u_share.why': 'תנו לאנשים לשתף או לשתף פעולה',
  'cap.u_search': 'הוסיפו חיפוש', 'cap.u_search.why': 'מצאו כל דבר מהר',
  // הצעות חכמות (שבבי „לבנות עוד אפליקציה?”)
  'ss.0.t': 'מתכנן ארוחות', 'ss.0.b': 'תכננו ארוחות סביב האימונים',
  'ss.1.t': 'יומן התאוששות', 'ss.1.b': 'עקבו אחר שינה, כאבי שרירים וימי מנוחה',
  'ss.2.t': 'מדידות גוף', 'ss.2.b': 'תמונה ומדידות לאורך זמן',
  'ss.3.t': 'מעקב מזווה', 'ss.3.b': 'דעו מה כבר יש לכם',
  'ss.4.t': 'אוסף מתכונים', 'ss.4.b': 'שמרו מועדפים ובנו רשימות אוטומטית',
  'ss.5.t': 'מעקב תקציב', 'ss.5.b': 'ראו לאן הולך הכסף על מכולת',
  'ss.6.t': 'יומן מצב רוח', 'ss.6.b': 'שלבו את ההרגלים עם נתוני מצב רוח',
  'ss.7.t': 'טיימר מדיטציה', 'ss.7.b': 'עוגן בוקר שקט',
  'ss.8.t': 'יומן קריאה', 'ss.8.b': 'בנו הרגל קריאה',
  'ss.9.t': 'רשימת קניות', 'ss.9.b': 'בנייה אוטומטית ממתכונים',
  'ss.10.t': 'מעקב מזווה', 'ss.10.b': 'אל תקנו מה שכבר יש',
  'ss.11.t': 'מתכנן ארוחות', 'ss.11.b': 'גררו שבוע לכדי סדר',
  'ss.12.t': 'מעקב חשבונות', 'ss.12.b': 'אל תפספסו תאריך תשלום',
  'ss.13.t': 'יעד חיסכון', 'ss.13.b': 'עקבו אחר ההתקדמות ליעד',
  'ss.14.t': 'ביקורת מנויים', 'ss.14.b': 'ראו על מה אתם משלמים',
  'ss.15.t': 'כספת ציטוטים', 'ss.15.b': 'שמרו ציטוטים שאתם אוהבים',
  'ss.16.t': 'מעקב הרגלים', 'ss.16.b': 'הפכו קריאה להרגל יומי',
  'ss.17.t': 'יומן מצב רוח', 'ss.17.b': 'שלבו ספרים עם רגשות',
  'ss.18.t': 'יומן הכרת תודה', 'ss.18.b': 'שלושה דברים, כל ערב',
  'ss.19.t': 'טיימר מדיטציה', 'ss.19.b': 'עגנו את הערב',
  'ss.20.t': 'מעקב שינה', 'ss.20.b': 'חברו מצב רוח למנוחה',
  'ss.21.t': 'מעקב שינה', 'ss.21.b': 'הירגעו עם נתונים',
  'ss.22.t': 'יומן הכרת תודה', 'ss.22.b': 'סיימו את היום ברוך',
  'ss.23.t': 'יומן מצב רוח', 'ss.23.b': 'נגיעה אחת ביום',
  'ss.24.t': 'יומן קריאה', 'ss.24.b': 'שלבו עם זמן לימוד',
  'ss.25.t': 'מעקב הרגלים', 'ss.25.b': 'למדו קצת כל יום',
  'ss.26.t': 'טיימר פומודורו', 'ss.26.b': 'ספרינטים של ריכוז עם הפסקות',
  'ss.27.t': 'רשימת קניות', 'ss.27.b': 'הדבר הפשוט שעובד',
  'ss.28.t': 'מעקב הרגלים', 'ss.28.b': 'צ׳ק-אין אחד ביום',
  'ss.29.t': 'טיימר פומודורו', 'ss.29.b': 'ספרינטים של ריכוז והפסקות',
  'ps.habit': 'מעקב הרגלים', 'ps.workout': 'יומן אימונים', 'ps.flash': 'חפיסת כרטיסיות',
  'ps.todo': 'רשימת מטלות', 'ps.booking': 'עמוד הזמנות', 'ps.inventory': 'מעקב מלאי',
  // טוסטים של הגדרות (פוש + מחיקת נתונים)
  'set.pushOnToast': 'התראות פוש פעילות.',
  'set.pushOffToast': 'התראות פוש כבויות.',
  'set.pushBlockedToast': 'הדפדפן שלכם חוסם התראות. הפעילו אותן בהגדרות האתר.',
  'set.delSignIn': 'נדרשת התחברות לפני מחיקת הנתונים שלכם.',
  'set.delRate': 'יותר מדי בקשות. נסו שוב בעוד דקה.',
  'set.delStart': 'לא הצלחנו להתחיל את המחיקה. נסו שוב.',
  'set.delDone': 'הנתונים שלכם ב-Pomela נמחקו מהמכשיר הזה.',
  'set.delExpired': 'האישור הזה פג תוקף. נסו שוב את כפתור המחיקה.',
  'set.delSnag': 'נתקלנו בתקלה במחיקת הנתונים שלכם. נסו שוב.',
  'set.delNet': 'החיבור אבד. בדקו את האינטרנט ונסו שוב.',
  // תוויות גישה ותיאורים שונים
  'cop.ariaMessage': 'כתבו לשותף',
  'cop.ariaSend': 'שליחת הודעה',
  'cop.ariaThinking': 'Pomy חושב',
  'cop.newChat': 'צ׳אט חדש',
  // Pomy two-pane build workspace (real handoff into the existing builder)
  'cop.buildThis': 'בנו את זה',
  'cop.latestBuild': 'הבנייה האחרונה שלכם',
  'cop.livePreview': 'תצוגה חיה',
  'cop.openBuilder': 'פתחו בבונה',
  'cop.openWorkspace': 'פתחו בסביבת העבודה',
  'cop.timelineTitle': 'ציר הגרסאות',
  'cop.versionsN': 'גרסאות',
  'cop.versionN': 'גרסה',
  'cop.localOnly': 'נשמר במכשיר הזה',
  'cop.restoreHint': 'פתחו בבונה כדי לשחזר גרסה',
  'cop.buildEmptyT': 'הבניות שלכם יופיעו כאן',
  'cop.buildEmptyS': 'תארו אפליקציה ופומי מעביר את התקציר לבונה — האפליקציה החיה והגרסאות שלה ינחתו כאן.',
  'cop.original': 'בנייה ראשונה',
  'guide.ariaSections': 'מדורי המדריך',
  'cookie.ariaPrefs': 'העדפות עוגיות',
  'common.dismiss': 'סגירה',
  'pi.attachTitle': 'צרפו תמונות, גיליון או PDF כדי להנחות את תוכן האפליקציה שלכם',
  'pi.attachAria': 'צירוף קבצים',
  'ex.ariaExport': 'ייצוא ל-GitHub',
  'pv.wandPlaceholder': 'הגדילו, שנו צבע, נסחו מחדש…',
  'app.openMenu': 'פתחו תפריט',
  'app.tapAria': 'כותבים. מקישים. מתקינים.',
  // האפליקציות שלי + גיליונות + פקדים שונים
  'my.confirmRemove': 'להסיר מהאפליקציות שלי?',
  'my.duplicate': 'שכפלו כאפליקציה חדשה',
  'my.remove': 'הסירו מהאפליקציות שלי',
  'ex.title': 'ייצוא ל-GitHub',
  'ex.pushed': 'נדחף ל-GitHub',
  'ex.repoName': 'שם המאגר',
  'ex.makePublic': 'הפכו את המאגר לציבורי',
  'dom.title': 'חברו דומיין מותאם',
  'dom.registered': 'הדומיין נרשם',
  'dom.yourDomain': 'הדומיין שלכם',
  'rp.placeholder': 'הפכו למצב כהה · הוסיפו טיימר · השתמשו בהדגשה ירוקה…',
  'nav.ariaHome': 'עברו לדף הבית של Pomela',
  'cr.ariaKey': 'מפתח API של Anthropic',
  'cel.aria': 'האפליקציה הראשונה שלכם ב-Pomela באוויר',
  'se.ariaQuickEdit': 'עריכה מהירה',
  'se.placeholder': 'הקלידו את הטקסט החדש…',
  // זנב אחרון — פעולות מהירות, התחלות, תוויות אלמנט, מצבים ריקים
  'cop.qa1': 'עזרו לי לחשוב', 'cop.qa2': 'אני תקוע', 'cop.qa3': 'חדדו את הפרומפט שלי',
  'se.lblHeadline': 'כותרת ראשית', 'se.lblTitle': 'כותרת',
  'eb.title': 'משהו השתבש',
  'eb.body': 'Pomela נתקלה בתקלה לא צפויה. טעינה מחדש בדרך כלל מסדרת את זה, והאפליקציות שכבר בניתם שמורות בבטחה במכשיר הזה.',
  'rp.title': 'שכללו את האפליקציה',
  'sb.noRecent': 'אין אפליקציות אחרונות',
  'an.empty': 'עדיין אין אפליקציות', 'an.emptySub': 'בנו את האפליקציה הראשונה והמספרים שלה יופיעו כאן.',
  'an.subtitle': 'צפיות, התקנות ופעילות AI, לכל אפליקציה.', 'an.emptyCta': 'צרו אפליקציה',
  'adm.cohortsEmpty': 'אין נתוני קבוצות, הריצו את סקריפט הזריעה והאגרגטור.',
  'adm.noFailures': 'אין כשלים בחלון הזה. 🎉', 'adm.userNoApps': 'עדיין אין אפליקציות.',
  'adm.noCrashes': 'אין קריסות בחלון הזה. 🎉', 'adm.noPlans': 'אין מסלולים פעילים.', 'adm.noModelData': 'אין נתוני מודל.',
  'sb.navigate': 'ניווט', 'sb.recentApps': 'אפליקציות אחרונות', 'sb.starterIdeas': 'רעיונות להתחלה', 'sb.helpFeedback': 'עזרה ומשוב',
  'an.title': 'האפליקציות שלכם', 'vh.currentlyViewing': 'צופים כעת',
  'se.noNodes': 'אין עדיין אלמנטים לעריכה באפליקציה הזו.',
  'se.unavailable': 'עריכה מהירה לא זמינה לאפליקציה הזו',
  'se.unavailableBody': 'היא נבנתה לפני עריכה חיה. השתמשו ב-<span class="font-semibold text-[color:var(--fg)]">שכללו את האפליקציה</span> למטה כדי לשנות כל דבר עם AI.',
  // לוח ניהול (מייסד בלבד)
  'adm.navOverview': 'סקירה',
  'adm.navUsers': 'משתמשים',
  'adm.navCohorts': 'קבוצות',
  'adm.navFunnel': 'משפך',
  'adm.navRevenue': 'הכנסות',
  'adm.navApps': 'אפליקציות',
  'adm.navCredits': 'קרדיטים',
  'adm.navGeneration': 'יצירה',
  'adm.crashes': 'קריסות',
  'adm.feedback': 'משוב',
  'adm.navSupport': 'תמיכה',
  'adm.backToCreator': 'חזרה ליוצר',
  'adm.appsEmpty': 'עדיין אין אפליקציות מותקנות.',
  'adm.themePresets': 'ערכות נושא',
  'adm.categories': 'קטגוריות',
  'adm.mostInstalled': 'הכי מותקנות',
  'adm.colApp': 'אפליקציה',
  'adm.colCategory': 'קטגוריה',
  'adm.colInstalls': 'התקנות',
  'adm.colViews': 'צפיות',
  'adm.noGens': 'עדיין אין יצירות.',
  'adm.noCatData': 'אין נתוני קטגוריות.',
  'adm.cohortRetention': 'שימור קבוצות',
  'adm.sortedFreq': 'ממוין לפי תדירות',
  'adm.allCrashes': 'כל הקריסות',
  'adm.7days': '7 ימים',
  'adm.30days': '30 ימים',
  'adm.90days': '90 ימים',
  'adm.colType': 'סוג',
  'adm.colMessage': 'הודעה',
  'adm.colLastSeen': 'נראה לאחרונה',
  'adm.usdPerDay': 'דולר ליום',
  'adm.noSpend': 'לא נרשמה הוצאה.',
  'adm.dailySpend': 'הוצאה יומית',
  'adm.byModel': 'לפי מודל',
  'adm.topConsumers': 'הצרכנים הגדולים',
  'adm.colAccount': 'חשבון',
  'adm.colSpend': 'הוצאה',
  'adm.noCredConsumption': 'אין צריכת קרדיטים בחלון הזה.',
  'adm.feedbackHint': 'בעיות ורעיונות שבונים שלחו, מהחדש לישן.',
  'adm.feedbackEmpty': 'אין משוב פתוח. כשבונים שולחים בעיה או רעיון, זה מגיע לכאן.',
  'adm.convFunnel': 'משפך המרה',
  'adm.last30': '30 הימים האחרונים',
  'adm.last90': '90 הימים האחרונים',
  'adm.lastYear': 'השנה האחרונה',
  'adm.failingPrompts': 'הפרומפטים הכושלים הנפוצים',
  'adm.archHealth': 'ביקוש + בריאות לפי ארכיטיפ',
  'adm.catalogGapsHint': 'פרומפטים שהקטלוג לא הצליח לבטא',
  'adm.rawKindsHint': 'מפקד צורות חופשי ← בלוק קטלוג הבא',
  'adm.failedPrompts': 'פרומפטים שנכשלו',
  'adm.archetypes': 'ארכיטיפים',
  'adm.catalogGaps': 'פערי קטלוג',
  'adm.rawKinds': 'סוגים גולמיים',
  'adm.colPrompt': 'פרומפט',
  'adm.colError': 'שגיאה',
  'adm.colArchetype': 'ארכיטיפ',
  'adm.colGens': 'יצירות',
  'adm.colSuccess': 'הצלחה',
  'adm.colAvgRaw': 'ממוצע גולמי',
  'adm.colForcedPrompt': 'פרומפט שאילץ קוד חופשי',
  'adm.colRawBlocks': 'בלוקים גולמיים',
  'adm.catalogCovered': 'אין, הקטלוג כיסה הכול.',
  'adm.sinceUtc': 'מאז 00:00 UTC',
  'adm.today': 'היום',
  'adm.activity': 'פעילות',
  'adm.mrrHint': 'חדש מול הרחבה מול נטישה, לפי יום',
  'adm.churnHint': 'לא נוצרה אפליקציה ב-14 יום',
  'adm.noChurnRisk': 'אין חשבונות בסיכון, כולם פעילים.',
  'adm.mrrMovement': 'תנועת MRR',
  'adm.planMix': 'תמהיל מסלולים',
  'adm.churnRisk': 'סיכון נטישה',
  'adm.mtd': 'מתחילת החודש',
  'adm.qtd': 'מתחילת הרבעון',
  'adm.ytd': 'מתחילת השנה',
  'adm.colLastGen': 'יצירה אחרונה',
  'adm.noSubEvents': 'אין אירועי מנוי בחלון הזה.',
  'adm.supportHint': 'פניות תמיכה מהטופס באפליקציה, מהחדש לישן.',
  'adm.supportEmpty': 'אין עדיין פניות תמיכה. כשמישהו שולח את טופס התמיכה, זה מגיע לכאן.',
  'adm.actions': 'פעולות',
  'adm.timeline': 'ציר זמן',
  'adm.appsCreated': 'אפליקציות שנוצרו',
  'adm.noUsersMatch': 'אין חשבונות שתואמים את הסינון הזה.',
  'adm.newest': 'החדשים',
  'adm.colLastActive': 'פעיל לאחרונה',
  'adm.colEmail': 'אימייל',
  'adm.colPlan': 'מסלול',
  'adm.colLtv': 'LTV',
  'adm.colJoined': 'הצטרף',
  // §S679 — סביבת העריכה (Pomy + שרביט הקסם + טיוטה/פרסום)
  'ew.tabPomy': 'Pomy',
  'ew.tabWand': 'שרביט הקסם',
  // §S6 — משטח העריכה המובנה המהיר (לשעבר סומן בטעות "שרביט הקסם"):
  'ew.tabCustomize': 'התאמה',
  'ew.wandNotReadyPane': 'בחירה-בהקשה עוד לא זמינה באפליקציה הזו — טאב ההתאמה מציג את כל החלקים שאפשר לערוך.',
  'ew.wandTurnOn': 'הפעילו את השרביט',
  'ew.wandLive': 'השרביט פעיל — הקישו על כל חלק באפליקציה בתצוגה המקדימה.',
  'ew.wandStep1': 'מפעילים את השרביט',
  'ew.wandStep2': 'מקישים על כל חלק באפליקציה בתצוגה המקדימה',
  'ew.wandStep3': 'משנים רק את החלק הזה — טקסט, מחיר, תמונה או צבע',
  'ew.wandStep4': 'מחילים — וזה נוחת על הטיוטה הפרטית שלכם',
  'ew.wandUndoNote': 'כל שינוי הוא קודם־כול טיוטה: האפליקציה החיה מתעדכנת רק כשמפרסמים, וההיסטוריה מחזירה כל גרסה.',
  'ew.pomyHero': 'ספרו לי מה לשנות.',
  'ew.pomyHeroSub': 'מילים פשוטות עובדות. אראה לכם את השינוי המדויק ואת העלות לפני שמשהו מוחל — שום דבר לא קורה בלי אישור שלכם.',
  'ew.chipHeroText': 'שנו את הכותרת הראשית',
  'ew.chipToEnglish': 'תרגמו את האפליקציה לאנגלית',
  'ew.chipAddSection': 'הוסיפו סקשן',
  'ew.chipImproveDesign': 'שפרו את העיצוב',
  'ew.chipColors': 'שנו את הצבעים',
  'ew.chipReviews': 'הוסיפו ביקורות של לקוחות',
  'ew.chipLaunchReady': 'תכינו אותה להשקה',
  'ew.gateSigninT': 'התחברו כדי לערוך את האפליקציה',
  'ew.gateSigninB': 'העריכה חינמית ובטוחה: כל שינוי נוחת על טיוטה פרטית — האפליקציה החיה מתעדכנת רק כשמפרסמים.',
  'ew.gateAskPomy': 'או שאלו את פומי',
  'ew.gateCap1': 'שם, סלוגן וצבעים',
  'ew.gateCap2': 'מראה בהיר/כהה ושמות הטאבים',
  'ew.gateCap3': 'הפריטים, המחירים והתמונות שלכם',
  'ew.gateCap4': 'עריכת אלמנט בודד עם שרביט הקסם',
  'ew.gateForbiddenT': 'האפליקציה הזו שייכת ליוצר אחר',
  'ew.gateBuildOwn': 'בנו משלכם',
  'ew.gateUnsupportedT': 'עריכה מובנית לא זמינה לאפליקציה הזו',
  'ew.gateErrorT': 'עדיין לא הצלחנו לפתוח את האפליקציה לעריכה',
  'ew.gateErrorB': 'האפליקציה נבנתה, אבל היא לא נטענה בזמן. נסו שוב בעוד רגע — שום דבר לא אבד.',
  'ew.backToApp': 'חזרה לאפליקציה',
  'ew.tabRenameSub': 'הקישו לשינוי שם',
  'ew.stageLive': 'תצוגה חיה · עריכות נוחתות על הטיוטה',
  'ew.shareSheetT': 'שתפו את האפליקציה',
  'ew.styleGroup': 'סגנון',
  'ew.styleModern': 'מודרני',
  'ew.styleEditorial': 'מגזיני',
  'ew.styleRounded': 'מעוגל',
  'ew.styleNote': 'מוחל על ידי פומי כרוויזיית טיוטה — צורך קרדיטים של רוויזיה.',
  'ew.stylePromptModern': 'עצבו מחדש את כל האפליקציה בכיוון מודרני ונקי — טיפוגרפיה חדה וריווח נדיב. שמרו על כל התוכן והנתונים בדיוק כפי שהם.',
  'ew.stylePromptEditorial': 'עצבו מחדש את כל האפליקציה בכיוון מגזיני — היררכיה מעודנת וכותרות אלגנטיות. שמרו על כל התוכן והנתונים בדיוק כפי שהם.',
  'ew.stylePromptRounded': 'עצבו מחדש את כל האפליקציה בכיוון ידידותי ומעוגל — פינות רכות ותחושה מזמינה. שמרו על כל התוכן והנתונים בדיוק כפי שהם.',
  'ew.saveField': 'שמירה',
  'ew.blocksGroup': 'סקשנים',
  'ew.wandBannerT': 'הצביעו על כל דבר.',
  'ew.wandBannerB': 'הפעילו את השרביט, ואז הקישו על כל חלק בתצוגה — כותרת, כפתור, תמונה — וערכו רק את החלק הזה. בלי שכבות, בלי קוד.',
  'ew.wandHelp': 'כשהשרביט פעיל, מעבר עכבר מדגיש כל אלמנט. הקישו לבחירה.',
  'ew.wandYouCan': 'אפשר לערוך',
  'ew.wandCanTextT': 'טקסט',
  'ew.wandCanTextS': 'שכתוב, קיצור, תרגום',
  'ew.wandCanColorT': 'צבע וסגנון',
  'ew.wandCanColorS': 'צביעה או עיצוב מחדש של כל אלמנט',
  'ew.wandCanImageT': 'תמונות',
  'ew.wandCanImageS': 'החלפת תמונה או יצירת חדשה',
  'ew.wandCanSizeT': 'גודל וריווח',
  'ew.wandCanSizeS': 'להגדיל דברים או להדק',
  'ew.pomyTry': 'נסו',
  'ew.tabWandShort': 'שרביט',
  'ew.modeAutoShort': 'אוטומטי',
  'ew.teachCustomize': 'עריכות מהירות — צבעים, בהיר/כהה, שם, טאבים ומראה. מיידי ובחינם.',
  'ew.stylePack': 'סגנון',
  'ew.styleMigrateOffer': 'לאפליקציה הזו עדיין אין סגנון. לנסות את זה שהיינו בוחרים עבורה?',
  'ew.styleMigrateApply': 'החל את הסגנון',
  'ew.styleMigrateUndoHint': 'אפשר לבטל בכל רגע \u2014 התוכן והנתונים שלך לא משתנים.',
  'ew.styleApplied': 'הסגנון הוחל — התוכן והנתונים שלך לא השתנו.',
  'ew.customizeHint': 'הקישו על כל פקד כדי לערוך אותו — או הקישו על שרביט הקסם כדי להצביע על חלק אחד באפליקציה.',
  'ew.customizeHintList': 'הקישו על כל פקד למטה כדי לערוך אותו — צבעים, ערכת נושא, שם, טאבים ועוד.',
  'ew.tabShare': 'שיתוף',
  'ew.teach': 'בקשו מ‑Pomy שינויים גדולים · ערכו חלק אחד עם שרביט הקסם',
  'ew.statusLive': 'חי · הגרסה המפורסמת',
  'ew.statusDraft': 'עריכת טיוטה — האפליקציה החיה לא השתנתה',
  'ew.unpublished': 'שינויים שטרם פורסמו',
  'ew.publish': 'פרסום השינויים',
  'ew.publishing': 'מפרסם…',
  'ew.published': 'פורסם — כולם רואים עכשיו את הגרסה הזו.',
  'ew.publishFailed': 'הפרסום נכשל. הטיוטה שלך שמורה — נסו שוב.',
  // §V2 גיליון הפרסום — מצבי אמת לרגע העלייה לאוויר
  'ew.pubT': 'מפרסמים את האפליקציה…',
  'ew.pubB': 'מקדמים את הטיוטה לגרסה החיה שהמשתמשים שלכם רואים. זה לוקח כמה שניות.',
  'ew.pubOkT': 'אתם באוויר',
  'ew.pubOkB': '{name} באוויר. שלחו את הקישור לחמשת המשתמשים הראשונים — ככה אפליקציות מוצאות את האנשים שלהן.',
  'ew.pubLinkK': 'קישור',
  'ew.pubCopied': 'הקישור הועתק',
  'ew.pubOpen': 'לאפליקציה החיה',
  'ew.pubBack': 'חזרה לעריכה',
  'ew.pubErrT': 'הפרסום לא הושלם',
  'ew.pubErrB': 'משהו קטע את הקידום. הטיוטה שלכם בטוחה ושום דבר לא אבד — נסו שוב.',
  'ew.pubRetry': 'נסו שוב',
  'ew.discard': 'מחיקת הטיוטה',
  'ew.discardConfirm': 'לזרוק את שינויי הטיוטה? האפליקציה החיה נשארת כפי שהיא.',
  'ew.discarded': 'הטיוטה נמחקה — חזרנו לגרסה החיה.',
  'ew.previewingDraft': 'תצוגה מקדימה של הטיוטה',
  'ew.previewingLive': 'תצוגה מקדימה של הגרסה החיה',
  'ew.wandHint': 'לחצו על כל חלק בתצוגה — או בחרו למטה — ושנו רק אותו. כל השאר נשאר בדיוק אותו דבר.',
  'ew.wandPick': 'בחרו חלק לעריכה',
  'ew.wandSelected': 'נבחר',
  'ew.wandFreeInstant': 'עריכה מיידית · חינם',
  'ew.identityGroup': 'האפליקציה שלך',
  'ew.appName': 'שם האפליקציה',
  'ew.tagline': 'שורת תיאור',
  'ew.accent': 'צבע ראשי',
  'ew.accentCustom': 'צבע מותאם (hex)',
  'ew.apply': 'החלה',
  'ew.applied': 'בוצע — רק החלק הזה השתנה.',
  'ew.applyFailed': 'השינוי נדחה — שום דבר לא אבד.',
  'ew.actionFailed': 'זה לא עבר — נסו שוב.',
  'ew.removeItem': 'הסרת הפריט',
  'ew.addItem': 'הוספת פריט',
  'ew.newItem': 'פריט חדש',
  'ew.itemSaved': 'נשמר. התצוגה מציגה את הטיוטה שלך.',
  'ew.userRecordNote': 'זו רשומת לקוח, לא תוכן של האפליקציה. אפשר לנהל אותה מלוח הניהול.',
  'ew.noAddress': 'אי אפשר לערוך את החלק הזה ישירות. תארו את השינוי ואחיל אותו כעדכון AI.',
  'ew.aiEditPlaceholder': 'תארו את השינוי לאלמנט הזה…',
  'ew.aiEditGo': 'החלה עם AI',
  'ew.aiEditNote': 'שינוי גדול — מוחל כעדכון (קודם טיוטה, 10 קרדיטים).',
  // §node-edit — עריכה ממוקדת לאלמנט (טקסט/תמונה/נראות/סגנון/פעולה)
  'ew.nodeTargetedNote': 'עריכה ישירה — רק האלמנט הזה משתנה. מיידי · חינם.',
  'ew.nodeTextPlaceholder': 'טקסט חדש לאלמנט הזה…',
  'ew.nodeSaveText': 'שמירת טקסט',
  'ew.nodeReplaceImage': 'החלפת תמונה',
  'ew.nodeTextColor': 'צבע טקסט',
  'ew.nodeLink': 'יעד קישור',
  'ew.nodeLinkPlaceholder': 'מזהה מסך או קישור…',
  'ew.nodeHide': 'הסתרה',
  'ew.nodeHidden': 'הוסתר — רק האלמנט הזה השתנה.',
  'ew.pomyIntro': 'אני Pomy — ספרו לי איך כל האפליקציה צריכה להשתנות: כיוון, טון, מבנה, יכולות חדשות. לאלמנט ספציפי — שרביט הקסם.',
  'ew.pomyPlaceholder': 'למשל: שהאפליקציה כולה תרגיש יוקרתית יותר…',
  'ew.pomyApply': 'החלת השינוי',
  'ew.pomyApplying': 'מחיל את השינוי…',
  'ew.pomyChipPremium': 'שתרגיש יוקרתית יותר',
  'ew.pomyChipTone': 'ניסוח חם וידידותי יותר',
  'ew.pomyChipWhatCanI': 'מה אפשר לערוך?',
  'ew.pomyChipHebrew': 'תרגום האפליקציה לעברית',
  'ew.editUnavailable': 'האפליקציה הזו נבנתה לפני יכולת העריכה — בקשו מ‑Pomy שינוי במקום.',
  'ew.loadingSurface': 'קורא את האפליקציה…',
  'ew.notSignedIn': 'התחברו כדי לערוך את האפליקציה.',
  // §S6 — ownership self-heal (a guest-built app claimed on sign-in)
  'ew.claimTitle': 'הפכו את האפליקציה לשלכם',
  'ew.claimBody': 'בניתם את האפליקציה הזו לפני ההתחברות. שייכו אותה לחשבון שלכם כדי לערוך ולפרסם.',
  'ew.claimCta': 'שייכו וערכו',
  'ew.claiming': 'משייך…',
  'ew.notYourApp': 'האפליקציה הזו שייכת לחשבון אחר, ולכן לא ניתן לערוך אותה כאן.',
  'ew.wandNotReady': 'ההצבעה־ולחיצה עדיין לא זמינה באפליקציה הזו — בחרו חלק מהרשימה למטה.',
  'ew.versionRestoreDraft': 'נטען כטיוטה — פרסמו כדי להפוך אותה לחיה.',
  'ew.openLive': 'פתיחה',
  'ew.buildAnother': 'לבנות עוד אחת',
  'ew.history': 'היסטוריה',
  'ew.refreshPreview': 'רענון התצוגה',
  'ew.badgeHidden': 'התג הוסר',
  'ew.removeBadge': 'הסרת תג Pomela',
  'ew.exportCode': 'ייצוא קוד',
  'ew.customDomain': 'דומיין מותאם',
  // §S680 — Magic Wand picking mode + deeper editing workspace
  'ew.wandArm': 'הצבעה ולחיצה',
  'ew.wandArmed': 'מצב בחירה פעיל — הקישו על כל חלק באפליקציה',
  'ew.wandExit': 'סיום בחירה',
  'ew.wandEsc': 'Esc ליציאה',
  'ew.wandListOnly': 'בחרו חלק מהרשימה למטה כדי לערוך אותו.',
  'ew.pickHeading': 'כותרת',
  'ew.pickText': 'טקסט',
  'ew.pickImage': 'תמונה',
  'ew.pickButton': 'כפתור',
  'ew.pickLink': 'קישור',
  'ew.pickSection': 'מקטע',
  'ew.appearance': 'מראה',
  'ew.modeAuto': 'לפי המכשיר',
  'ew.modeLight': 'בהיר',
  'ew.modeDark': 'כהה',
  'ew.tabsGroup': 'לשוניות התפריט',
  'ew.tabRename': 'שינוי שם לשונית',
  'ew.moveUp': 'הזזה למעלה',
  'ew.moveDown': 'הזזה למטה',
  'ew.photoLabel': 'תמונה',
  'ew.photoUrlPlaceholder': 'קישור לתמונה https://',
  'ew.photoUpload': 'העלאת תמונה',
  'ew.photoUploading': 'מעלה…',
  'ew.photoRemove': 'הסרת התמונה',
  'ew.teachWand': 'הצביעו על כל חלק באפליקציה ושנו רק אותו — מיידי ובחינם.',
  'ew.teachPomy': 'ספרו לפומי איך כל האפליקציה צריכה להשתנות — השינוי מוחל כטיוטה.',
  'ew.teachShare': 'שתפו את הקישור החי וצמחו.',
  'ew.pomySelChip': 'מצביעים על: {label}',
  'ew.pomyAskAbout': 'לשאול את פומי על זה',
  'ew.pomyApplyCard': 'פומי יחיל:',
  'ew.pomyCost': '10 קרדיטים · נבנה מחדש כטיוטה',
  // §Pomy-routing — עריכת צומת ממוקדת שפומי פתר מהצ׳אט: תוויות פעולה, מחלקת חינם, ומצב הוחל.
  'ew.pomyNodeFree': 'חינם · מיידי · מעדכן את הטיוטה',
  'ew.pomyApplied': 'הוחל על הטיוטה שלך',
  'ew.pomyAppliedTag': 'הוחל',
  'ew.pomyOpText': 'שינוי הטקסט ל־',
  'ew.pomyOpImage': 'שינוי התמונה ל־',
  'ew.pomyOpLink': 'שינוי הקישור ל־',
  'ew.pomyOpHide': 'הסתרת האלמנט הזה',
  'ew.pomyOpShow': 'הצגת האלמנט הזה',
  'ew.pomyOpColor': 'צביעה מחדש ל־{color}',
  'ew.pomyOpStyle': 'עיצוב מחדש של האלמנט הזה',
  'ew.pomyRate': 'פומי עמוס כרגע — נסו שוב בעוד כמה שניות.',
  'ew.pomySignin': 'התחברו כדי לשוחח עם פומי.',
  'ew.pomyRetry': 'ניסיון נוסף',
  'ew.publishConfirmT': 'לפרסם ללקוחות שלכם?',
  'ew.publishConfirmB': 'כל מי שיפתח את הקישור החי יקבל את הגרסה הזו.',
  'ew.publishGo': 'פרסום',
  'ew.discardConfirmT': 'למחוק את הטיוטה?',
  'ew.discardConfirmB': 'האפליקציה החיה נשארת בדיוק כפי שהיא. שינויי הטיוטה יימחקו.',
  'ew.discardGo': 'מחיקת הטיוטה',
  'ew.keepEditing': 'להמשיך לערוך',
  'ew.reviseOverlayT': 'בונה מחדש את האפליקציה…',
  'ew.reviseOverlayB': 'פומי מחיל את השינוי. התצוגה תתרענן כשהטיוטה מוכנה.',
  // §S680 nav registry (lib/nav-items.ts) + CommandPalette rows
  'nav.help': 'תמיכה',
  'cmd.helpHint': 'שלחו הודעה לצוות או העתיקו את כתובת התמיכה',
  'cmd.settingsHint': 'ערכת נושא, שפה והעדפות האפליקציה',
  'cmd.accountHint': 'המנוי, הקרדיטים וההתחברות שלכם',
  // §S679 owner morning view (TodayOverview)
  'ov.title': 'היום',
  'ov.updated': 'עודכן {t}',
  'ov.time.now': 'ממש עכשיו',
  'ov.time.m': 'לפני {n} דק׳',
  'ov.time.h': 'לפני {n} שע׳',
  'ov.time.d': 'לפני {n} ימים',
  'ov.newBookings': 'תורים חדשים היום',
  'ov.newOrders': 'הזמנות חדשות היום',
  'ov.pendingActions': 'ממתינים לטיפול',
  'ov.unavailable': 'נתוני פעילות עדיין לא מחוברים',
  'ov.quietTitle': 'שקט בינתיים היום',
  'ov.quietText': 'שתפו את האפליקציה כדי שאנשים יגלו אותה.',
  'ov.capped': 'המספרים משקפים את הפעילות האחרונה שלך.',
  // §S680 — סטטוס אמיתי בסביבת העבודה + כרום MyApps
  'wsx.stLive': 'פעיל',
  'wsx.stDraft': 'טיוטה ממתינה',
  'wsx.stPreview': 'תצוגה מקדימה',
  'wsx.stPublic': 'ציבורי',
  'wsx.fDraft': 'טיוטה',
  'wsx.newApp': 'אפליקציה חדשה',
  'wsx.kInstalls': 'התקנות',
  'wsx.kViews': 'צפיות',
  'wsx.evInstalls': 'התקנות חדשות',
  'wsx.evUpdated': 'עודכנה',
  'wsx.evCreated': 'נוצרה',
  'wsx.versionsN': '{n} גרסאות',
  'wsx.installsN': '{n} התקנות',
  'wsx.activityEmpty': 'פעילות מהאפליקציות שלכם תופיע כאן.',
  'wsx.across1': 'באפליקציה אחת',
  'wsx.acrossN': 'ב‑{n} אפליקציות',
  'wsx.noInstalls': 'אין התקנות עדיין — מספרי התקנות אמיתיים יופיעו כאן ברגע שאנשים יוסיפו את האפליקציות שלכם.',
  'wsx.startCategory': 'התחילו מקטגוריה',
  'wsx.catStore': 'חנות',
  'wsx.catBooking': 'תורים',
  'wsx.catTracker': 'מעקב',
  'wsx.catCrm': 'ניהול לקוחות',
  'wsx.guideTitle': 'חדשים כאן? בנו את האפליקציה הראשונה שלכם',
  'wsx.guideSub': 'מדריך של 2 דקות',
  // §S680 — מקטעי פרטי אפליקציה + פאנלי ניהול
  'ad.secOverview': 'סקירה',
  'ad.secManage': 'ניהול',
  'ad.secShare': 'שיתוף ופרסום',
  'ad.openLive': 'פתיחת האפליקציה',
  'ad.shareSub': 'שלחו ללקוחות את הקישור לאפליקציה או העתיקו אותו.',
  'ad.mgBookingT': 'ניהול תורים',
  'ad.mgBookingS': 'צפייה, אישור ושינוי מועדים של תורי לקוחות.',
  'ad.mgCommerceT': 'מוצרים והזמנות',
  'ad.mgCommerceS': 'ניהול הקטלוג וטיפול בהזמנות של לקוחות.',
  'ad.mgOrderT': 'תפריט והזמנות',
  'ad.mgOrderS': 'עדכון התפריט ומעקב אחרי הזמנות לאיסוף.',
  'ad.mgCrmT': 'לידים ומכירות',
  'ad.mgCrmS': 'טיפול בעסקאות ובמעקבים.',
  'ad.mgTrackerT': 'רשומות ומגמות',
  'ad.mgTrackerS': 'סקירת הרשומות והמגמות שלכם.',
  'ad.mgDirectoryT': 'רישומים ואישורים',
  'ad.mgDirectoryS': 'אישור הגשות וניהול הרישומים.',
  'ad.mgCommunityT': 'פוסטים וניהול קהילה',
  'ad.mgCommunityS': 'ניהול תוכן הקהילה.',
  'ad.mgAiT': 'פרומפטים ותוצרים',
  'ad.mgAiS': 'סקירת משימות ותוצרים שנוצרו.',
  'ad.notWired': 'עדיין לא מחובר',
  'ad.notWiredHint': 'דורש חיבור לניהול בצד השרת',
  'ad.discAssets': 'ספריית קבצים',
  'ad.discAssetsS': 'תמונות וקבצים שהאפליקציה יכולה להשתמש בהם.',
  'ad.discWaitlist': 'רשימת המתנה',
  'ad.discWaitlistS': 'אנשים שממתינים למקום.',
  'ad.discActivity': 'יומן פעילות',
  'ad.discActivityS': 'מי עשה מה באפליקציה.',
  'ad.discWebhooks': 'Webhooks',
  'ad.discWebhooksS': 'שליחת אירועי האפליקציה ל-Zapier, Make או לנקודת קצה משלכם.',
  'mgmt.wh.intro': 'קבלו בקשת POST חתומה לכתובת שלכם בכל אירוע נבחר (הזמנה חדשה, תור, אישור הגעה…). עובד עם Zapier, Make או כל נקודת קצה מסוג HTTPS.',
  'mgmt.wh.url': 'כתובת נקודת הקצה (HTTPS)',
  'mgmt.wh.enabled': 'שליחה מופעלת',
  'mgmt.wh.events': 'אירועים (ללא בחירה = הכול)',
  'mgmt.wh.secret': 'מפתח חתימה',
  'mgmt.wh.secretHint': 'אמתו את הכותרת X-Pomela-Signature (HMAC-SHA256 של גוף הבקשה) בעזרת מפתח זה. שמרו אותו בסוד.',
  'mgmt.wh.save': 'שמירה',
  'mgmt.wh.saving': 'שומר…',
  'mgmt.wh.saved': 'נשמר',
  'mgmt.wh.test': 'שליחת בדיקה',
  'mgmt.wh.testing': 'שולח…',
  'mgmt.wh.testOk': 'הבדיקה נשלחה',
  'mgmt.wh.testFail': 'הבדיקה נכשלה',
  'mgmt.wh.deliveries': 'שליחות אחרונות',
  'mgmt.wh.none': 'אין שליחות עדיין.',
  'mgmt.wh.err': 'משהו השתבש — נסו שוב.',
  'mgmt.wh.unavailable': 'Webhooks זמינים לאחר שמירת האפליקציה לחשבון שלכם.',
  'ov.attnTitle': 'דורש תשומת לב',
  'ov.attn.booking1': 'בקשת תור אחת ממתינה',
  'ov.attn.bookingN': '{n} בקשות תור ממתינות',
  'ov.attn.order1': 'הזמנה חדשה אחת',
  'ov.attn.orderN': '{n} הזמנות חדשות',
  'ov.attn.wait1': 'נרשם אחד לרשימת ההמתנה',
  'ov.attn.waitN': '{n} נרשמים לרשימת ההמתנה',
  'ov.activityTitle': 'פעילות אחרונה',
  'ov.ev.shared': 'שותפה',
  'ov.ev.installed': 'הותקנה',
  'ov.ev.opened': 'נפתחה מקישור משותף',
  // §S679 — פרטי אפליקציה (בריאות + יכולות + כרום)
  'ad.justNow': 'ממש עכשיו',
  'ad.minAgo': 'לפני {n} דק׳',
  'ad.hourAgo': 'לפני {n} שע׳',
  'ad.dayAgo': 'לפני {n} ימים',
  'ad.healthTitle': 'מצב האפליקציה',
  'ad.hLive': 'חיה ופעילה',
  'ad.hPreview': 'תצוגה מקדימה — פרסמו כדי להפעיל',
  'ad.hUpdated': 'עודכנה',
  'ad.hInstall': 'ניתנת להתקנה בטלפון (הוספה למסך הבית)',
  'ad.hPublic': 'קישור השיתוף פעיל — כל מי שיש לו אותו יכול לפתוח את האפליקציה',
  'ad.hPrivate': 'פרטית — רק מי שיש לו את הקישור יכול להיכנס',
  'ad.hServerData': 'רשומות לקוחות נשמרות בסביבת העבודה המאובטחת שלך',
  'ad.hLocalData': 'עובדת גם בלי אינטרנט — הנתונים נשארים במכשיר של כל משתמש',
  'ad.hRoles': 'תצוגת הבעלים ותצוגת הלקוח מופרדות',
  'ad.hDraft': 'שינויי טיוטה שטרם פורסמו',
  'ad.healthFoot': 'כל שורה כאן משקפת את המצב האמיתי של האפליקציה — שום דבר לא מוערך.',
  'ad.capTitle': 'מה האפליקציה כוללת',
  'ad.cBookings': 'בקשות תור',
  'ad.cAvailability': 'ניהול זמינות',
  'ad.cCustomers': 'רשימת לקוחות',
  'ad.cPaymentLink': 'קישור תשלום (אישור ידני)',
  'ad.cWaitlist': 'רשימת המתנה',
  'ad.cCsv': 'ייצוא CSV',
  'ad.cCatalog': 'קטלוג מוצרים',
  'ad.cCart': 'עגלה ותשלום',
  'ad.cOrders': 'הזמנות',
  'ad.cMenu': 'תפריט',
  'ad.cEvents': 'אירועים',
  'ad.cRsvps': 'אישורי הגעה',
  'ad.cInstallShare': 'התקנה ושיתוף',
  'ad.cEditable': 'ניתנת לעריכה עם Pomy ושרביט הקסם',
  'ad.nCardCapture': 'אין עדיין סליקת אשראי מובנית — אתם מאשרים תשלומים ידנית',
  'ad.open': 'פתיחה',
  'ad.edit': 'עריכה',
  'ad.share': 'שיתוף',
  'ad.live': 'חי',
  'ad.preview': 'תצוגה מקדימה',
  'ad.views': 'צפיות',
  'ad.installs': 'התקנות',
  'ad.interactions': 'אינטראקציות',
  'ad.creditsUsed': 'קרדיטים בשימוש',
  'ad.analyticsNote': 'נתוני שימוש יופיעו כאן ברגע שיהיו ביקורים. המספרים אמיתיים לכל אפליקציה, לעולם לא מוערכים.',
  'ad.mgmt': 'ניהול האפליקציה',
  'ad.notFound': 'האפליקציה לא נמצאה',
  'ad.notFoundSub': 'אין לך גישה לאפליקציה הזו, או שהיא לא קיימת. פתחו את סביבת העבודה כדי לראות את האפליקציות שלכם.',
  'ad.backToWorkspace': 'חזרה לסביבת העבודה',
  // §S679 — PublishControls (קישור שיתוף פעיל/כבוי)
  'pub.title': 'קישור שיתוף',
  'pub.liveTitle': 'קישור השיתוף פעיל',
  'pub.liveSub': 'כל מי שיש לו את הקישור יכול לפתוח את האפליקציה שלכם.',
  'pub.privateTitle': 'פרטי — בקישור בלבד',
  'pub.privateSub': 'רק מי שיש לו את הקישור יכול להשתמש באפליקציה שלכם. הפעילו את קישור השיתוף כדי לקבל כתובת קצרה שאפשר לחלק.',
  'pub.publish': 'הפעלת קישור שיתוף',
  'pub.publishing': 'מפעיל…',
  'pub.unpublish': 'כיבוי',
  'pub.unpublishing': 'מכבה…',
  'pub.liveBadge': 'חי',
  'pub.copy': 'העתקה',
  'pub.copied': 'הועתק',
  'pub.ariaCopy': 'העתקת קישור השיתוף הציבורי',
  'pub.errPublish': 'הפעלת קישור השיתוף לא הצליחה. בדקו את החיבור ונסו שוב.',
  'pub.errUnpublish': 'כיבוי קישור השיתוף לא הצליח. בדקו את החיבור ונסו שוב.',
  'pub.errLoad': 'לא הצלחנו לטעון את מצב קישור השיתוף של האפליקציה.',
  'pub.retry': 'נסו שוב',
  // §S680 — פאנלי ניהול לבעלים (App Detail): מסגרת משותפת
  'mgmt.refresh': 'רענון',
  // §S2 — Owner Order Console (HE)
  'mgmt.ord.title': 'הזמנות לאיסוף',
  // §Owner-console generalization — כותרות קונסולת ניהול לכל אוסף.
  'mgmt.tickets.title': 'פניות תמיכה',
  'mgmt.items.title': 'מלאי',
  'mgmt.adjustments.title': 'תנועות מלאי',
  'mgmt.attempts.title': 'ניסיונות במבחן',
  'mgmt.rsvps.title': 'אישורי הגעה',
  'mgmt.timeoff.title': 'בקשות חופשה',
  'mgmt.employees.title': 'צוות העובדים',
  'mgmt.listings.title': 'רשומות',
  'mgmt.submissions.title': 'הגשות לבדיקה',
  'mgmt.rec.exportCsv': 'ייצוא CSV',
  'mgmt.rec.emptyT': 'אין הזמנות עדיין',
  'mgmt.rec.emptyRecordsT': 'אין כאן עדיין כלום',
  'mgmt.rec.emptyRecordsS': 'רשומות מופיעות כאן כשהן נוספות.',
  'mgmt.rec.errAuthRecords': 'צריך להתחבר כבעלי האפליקציה כדי לנהל את הרשומות האלה.',
  'mgmt.rec.unavailableRecordsS': 'הרשימה תופעל כשלאפליקציה יהיה שרת נתונים.',
  'mgmt.rec.emptyS': 'הזמנות איסוף חדשות יופיעו כאן — לאשר, לקדם את סטטוס ההכנה ולסמן ששולם.',
  'mgmt.rec.errT': 'הטעינה נכשלה',
  'mgmt.rec.errAuth': 'התחברו כבעלים כדי לנהל הזמנות.',
  'mgmt.rec.errGeneric': 'משהו השתבש. נסו שוב.',
  'mgmt.rec.unavailableT': 'עדיין לא זמין',
  'mgmt.rec.unavailableS': 'ניהול ההזמנות נדלק ברגע שלאפליקציה יש שרת נתונים.',
  'mgmt.rec.order.placed': 'הזמנה חדשה',
  'mgmt.rec.order.preparing': 'בהכנה',
  'mgmt.rec.order.ready': 'מוכן לאיסוף',
  'mgmt.rec.order.completed': 'הושלם',
  'mgmt.rec.order.cancelled': 'בוטל',
  'mgmt.pay.awaiting_payment': 'ממתין לתשלום',
  'mgmt.pay.external_pending': 'תשלום בהמתנה',
  'mgmt.pay.pay_at_business_pending': 'תשלום באיסוף',
  'mgmt.pay.marked_paid': 'שולם',
  'mgmt.pay.payment_canceled': 'התשלום בוטל',
  'mgmt.pay.refunded_manual': 'הוחזר',
  'mgmt.saving': 'שומר…',
  'mgmt.saved': 'נשמר',
  'mgmt.errConn': 'החיבור אבד. בדקו את הרשת ונסו שוב.',
  'mgmt.errConnShort': 'החיבור אבד. נסו שוב.',
  'mgmt.time.justNow': 'ממש עכשיו',
  'mgmt.time.m': 'לפני {n} דק׳',
  'mgmt.time.h': 'לפני {n} שע׳',
  'mgmt.time.d': 'לפני {n} ימים',
  // ── SEP-2 / E0 — shared owner-workspace primitive chrome (HE mirror of own.*) ──
  'own.add': 'הוספה',
  'own.addTitle': 'הוספת פריט',
  'own.editTitle': 'עריכה',
  'own.save': 'שמירה',
  'own.saving': 'שומר…',
  'own.cancel': 'ביטול',
  'own.close': 'סגירה',
  'own.edit': 'עריכה',
  'own.archive': 'העברה לארכיון',
  'own.archiveAria': 'העברת {entity} לארכיון — אפשר לשחזר בהמשך',
  'own.restore': 'שחזור',
  'own.restoreAria': 'שחזור {entity}',
  'own.archived': 'בארכיון',
  'own.undo': 'ביטול',
  'own.undoArchiveAria': 'ביטול — שחזור {entity}',
  'own.delete': 'מחיקה',
  'own.deleteAria': 'מחיקת {entity} לצמיתות',
  'own.deleteConfirm': 'כן, למחוק',
  'own.confirmDeleteT': 'למחוק את {entity}?',
  'own.confirmDeleteS': 'הפעולה מוחקת את {entity} לצמיתות ואי אפשר לבטל אותה. העברה לארכיון מסתירה את הפריט אך מאפשרת שחזור.',
  'own.showArchived': 'הצגת ארכיון ({n})',
  'own.hideArchived': 'הסתרת ארכיון',
  'own.required': 'שדה חובה',
  'own.invalidNumber': 'יש להזין מספר',
  'own.invalidRecord': 'חלק מהפרטים חסרים או שגויים. בדקו את השדות ונסו שוב.',
  'own.fixErrors': 'יש לתקן את השדות המסומנים.',
  'own.saveFailed': 'השמירה נכשלה. בדקו את החיבור ונסו שוב.',
  'own.emptyT': 'אין כאן עדיין כלום',
  'own.emptyS': 'פריטים חדשים יופיעו כאן.',
  'own.errT': 'הטעינה נכשלה',
  'own.errAuth': 'התחברו כבעלים כדי לנהל זאת.',
  'own.errGeneric': 'משהו השתבש. נסו שוב.',
  'own.unavailableT': 'לא זמין עדיין',
  'own.unavailableS': 'הניהול נפתח לאחר שהאפליקציה מחוברת לשרת הנתונים.',
  'own.transitionsAria': 'שינוי סטטוס',
  'own.transitionTo': 'העברת {entity} ל{state}',
  'own.thisItem': 'פריט זה',
  'own.mediaUpload': 'העלאת תמונה',
  'own.mediaReplace': 'החלפת תמונה',
  'own.mediaRemove': 'הסרה',
  'own.mediaUploading': 'מעלה…',
  'own.mediaEmpty': 'אין עדיין תמונה',
  'own.mediaUploadFailed': 'ההעלאה נכשלה — נסו שוב.',
  'own.mediaAlt': 'תיאור התמונה (טקסט חלופי)',
  'own.mediaAltHint': 'תארו את התמונה עבור קוראי מסך ומנועי חיפוש.',
  'own.mediaAltRequired': 'הוסיפו תיאור קצר כדי שהתמונה תהיה נגישה.',
  'own.analyticsEmptyT': 'אין עדיין נתונים',
  'own.analyticsEmptyS': 'מספרים אמיתיים יופיעו כאן ברגע שתהיה פעילות.',
  'own.analyticsErrAuth': 'התחברו כבעלים כדי לצפות בנתונים.',
  // ── SEP-2 / E1 — קונסולות ניהול חנות (HE mirror של shop.*) ──
  // מוצרים
  'shop.pr.title': 'מוצרים',
  'shop.pr.add': 'הוספת מוצר',
  'shop.pr.addTitle': 'הוספת מוצר',
  'shop.pr.editTitle': 'עריכת מוצר',
  'shop.pr.emptyT': 'אין עדיין מוצרים',
  'shop.pr.emptyS': 'הוסיפו את המוצר הראשון — שם, מחיר ותמונה זה כל מה שצריך כדי להתחיל למכור.',
  'shop.pr.unavailableT': 'המוצרים אינם זמינים עדיין',
  'shop.pr.errAuth': 'התחברו כבעלים כדי לנהל את המוצרים.',
  'shop.pr.fTitle': 'שם המוצר',
  'shop.pr.fTitlePh': 'למשל: ספל קרמיקה בעבודת יד',
  'shop.pr.fPrice': 'מחיר',
  'shop.pr.fPriceHelp': 'כמה לקוח משלם על יחידה.',
  'shop.pr.fSku': 'מק״ט',
  'shop.pr.fSkuPh': 'למשל: MUG-001',
  'shop.pr.fCategory': 'קטגוריה',
  'shop.pr.fStock': 'במלאי',
  'shop.pr.fStockHelp': 'כמה יש לכם למכירה כרגע.',
  'shop.pr.fVariants': 'וריאציות',
  'shop.pr.fVariantsPh': 'קטן, בינוני, גדול',
  'shop.pr.fVariantsHelp': 'הפרידו אפשרויות בפסיקים.',
  'shop.pr.fDesc': 'תיאור',
  'shop.pr.fDescPh': 'מה מיוחד במוצר הזה.',
  'shop.pr.fMedia': 'תמונת מוצר',
  'shop.pr.outOfStock': 'אזל מהמלאי',
  'shop.pr.lowStock': 'נמוך · נותרו {n}',
  'shop.pr.inStock': '{n} במלאי',
  'shop.pr.hidden': 'מוסתר',
  'shop.pr.visible': 'גלוי',
  'shop.pr.untitled': 'מוצר ללא שם',
  'shop.pr.thisProduct': 'מוצר זה',
  'shop.pr.showToCustomersAria': 'הצגת {title} ללקוחות',
  'shop.pr.hideFromCustomersAria': 'הסתרת {title} מהלקוחות',
  'shop.pr.editAria': 'עריכת {title}',
  // מלאי
  'shop.inv.title': 'מלאי',
  'shop.inv.add': 'הוספת פריט',
  'shop.inv.addTitle': 'הוספת פריט מלאי',
  'shop.inv.editTitle': 'עריכת פריט',
  'shop.inv.emptyT': 'אין עדיין מעקב',
  'shop.inv.emptyS': 'הוסיפו את הפריטים שאתם מחזיקים במלאי — תראו כמויות ותקבלו התראות על מלאי נמוך.',
  'shop.inv.unavailableT': 'המלאי אינו זמין עדיין',
  'shop.inv.errAuth': 'התחברו כבעלים כדי לנהל מלאי.',
  'shop.inv.fName': 'שם הפריט',
  'shop.inv.fNamePh': 'למשל: ספל קרמיקה — כחול',
  'shop.inv.fSku': 'מק״ט',
  'shop.inv.fCategory': 'קטגוריה',
  'shop.inv.fCount': 'במלאי',
  'shop.inv.fCountHelp': 'כמה יש לכם פיזית כרגע.',
  'shop.inv.fReorder': 'נקודת הזמנה מחדש',
  'shop.inv.fReorderHelp': 'התריעו לי כשהמלאי יורד לזה.',
  'shop.inv.fUnit': 'יחידה',
  'shop.inv.fUnitPh': 'למשל: יח׳, ק״ג',
  'shop.inv.fLocation': 'מיקום',
  'shop.inv.adjType': 'סוג עדכון',
  'shop.inv.receive': 'קליטה (הוספה)',
  'shop.inv.recount': 'ספירה מחדש (קביעה)',
  'shop.inv.writeOff': 'מחיקה (הפחתה)',
  'shop.inv.quantity': 'כמות',
  'shop.inv.quantityHelp': 'בספירה מחדש — זה הסך הכולל החדש.',
  'shop.inv.note': 'הערה',
  'shop.inv.notePh': 'למשל: משלוח מהספק',
  'shop.inv.adjust': 'עדכון מלאי',
  'shop.inv.adjustAria': 'עדכון מלאי עבור {name}',
  'shop.inv.adjustTitle': 'עדכון מלאי',
  'shop.inv.applyAdjust': 'החלת העדכון',
  'shop.inv.adjustFailed': 'לא הצלחנו לרשום את העדכון. שום דבר לא השתנה — נסו שוב.',
  'shop.inv.adjustPartial': 'העדכון נרשם אך כמות המלאי לא התעדכנה. רעננו ובדקו.',
  'shop.inv.discontinued': 'הופסק',
  'shop.inv.out': 'אזל',
  'shop.inv.low': 'מלאי נמוך',
  'shop.inv.inStock': 'במלאי',
  'shop.inv.untitled': 'פריט ללא שם',
  'shop.inv.thisItem': 'פריט זה',
  'shop.inv.onHand': '{n} {unit} במלאי',
  'shop.inv.reorderAlert': 'בנקודת ההזמנה מחדש או מתחתיה ({rp}) — הזמן לחדש מלאי',
  'shop.inv.resulting': '← {n} במלאי',
  'shop.inv.historyTitle': 'תנועות מלאי',
  'shop.inv.historyEmptyT': 'אין עדיין תנועות',
  'shop.inv.historyEmptyS': 'כל שינוי מלאי שתעשו נרשם כאן — קליטות, ספירות ומחיקות.',
  'shop.inv.st.in_stock': 'במלאי',
  'shop.inv.st.low_stock': 'מלאי נמוך',
  'shop.inv.st.out_of_stock': 'אזל',
  'shop.inv.st.discontinued': 'הפסקה',
  // CRM
  'shop.crm.contactsTitle': 'לקוחות',
  'shop.crm.contactsEmptyT': 'אין עדיין לקוחות',
  'shop.crm.contactsEmptyS': 'הוסיפו את האנשים שאתם עובדים איתם — לידים, מתעניינים ולקוחות.',
  'shop.crm.addContact': 'הוספת לקוח',
  'shop.crm.addContactTitle': 'הוספת לקוח',
  'shop.crm.editContactTitle': 'עריכת לקוח',
  'shop.crm.dealsTitle': 'עסקאות',
  'shop.crm.dealsEmptyT': 'אין עדיין עסקאות',
  'shop.crm.dealsEmptyS': 'עקבו אחר הזדמנויות מליד ועד סגירה — הוסיפו עסקה ראשונה.',
  'shop.crm.addDeal': 'הוספת עסקה',
  'shop.crm.addDealTitle': 'הוספת עסקה',
  'shop.crm.editDealTitle': 'עריכת עסקה',
  'shop.crm.errAuth': 'התחברו כבעלים כדי לנהל לקוחות.',
  'shop.crm.dStageNote': 'בחרו את שלב העסקה בעת ההוספה. הזזת עסקאות בין שלבים לאחר מכן תתאפשר בקרוב.',
  'shop.crm.cName': 'שם',
  'shop.crm.cNamePh': 'למשל: דנה לוי',
  'shop.crm.cCompany': 'חברה',
  'shop.crm.cEmail': 'אימייל',
  'shop.crm.cPhone': 'טלפון',
  'shop.crm.cRelationship': 'סוג קשר',
  'shop.crm.cTags': 'תגיות',
  'shop.crm.cTagsPh': 'VIP, סיטונאי',
  'shop.crm.cNotes': 'הערות',
  'shop.crm.dTitle': 'שם העסקה',
  'shop.crm.dTitlePh': 'למשל: הזמנה סיטונאית — קפה נײן',
  'shop.crm.dStage': 'שלב',
  'shop.crm.dStageHelp': 'היכן העסקה נמצאת בצינור המכירות.',
  'shop.crm.dValue': 'ערך',
  'shop.crm.dContact': 'איש קשר',
  'shop.crm.dContactHelp': 'קשרו את העסקה ללקוח.',
  'shop.crm.dCloseDate': 'סגירה צפויה',
  'shop.crm.dNotes': 'הערות',
  'shop.crm.rel.Lead': 'ליד',
  'shop.crm.rel.Prospect': 'מתעניין',
  'shop.crm.rel.Customer': 'לקוח',
  'shop.crm.rel.Churned': 'נטש',
  'shop.crm.stage.Lead': 'ליד',
  'shop.crm.stage.Qualified': 'מוסמך',
  'shop.crm.stage.Proposal': 'הצעה',
  'shop.crm.stage.Won': 'נסגרה',
  'shop.crm.stage.Lost': 'אבודה',
  'shop.crm.unnamed': 'איש קשר ללא שם',
  'shop.crm.thisContact': 'לקוח זה',
  'shop.crm.untitledDeal': 'עסקה ללא שם',
  'shop.crm.thisDeal': 'עסקה זו',
  // ציר פעילות CRM (TRACK C)
  'shop.crm.activityTitle': 'פעילות',
  'shop.crm.activityEmptyT': 'אין פעילות עדיין',
  'shop.crm.activityEmptyS': 'שינויי עסקאות, לקוחות חדשים, הזמנות ותורים יופיעו כאן כשהם קורים.',
  'shop.crm.actUnknown': 'פעילות לא מזוהה',
  'shop.crm.actor.owner': 'אתם',
  'shop.crm.actor.customer': 'לקוח',
  'shop.crm.actor.system': 'מערכת',
  'shop.crm.act.contact_created': 'לקוח נוסף',
  'shop.crm.act.contact_updated': 'לקוח עודכן',
  'shop.crm.act.contact_archived': 'לקוח הועבר לארכיון',
  'shop.crm.act.contact_restored': 'לקוח שוחזר',
  'shop.crm.act.deal_created': 'עסקה נוצרה',
  'shop.crm.act.deal_stage_changed': 'שלב העסקה שונה',
  'shop.crm.act.deal_won': 'עסקה נסגרה בהצלחה',
  'shop.crm.act.deal_lost': 'עסקה אבודה',
  'shop.crm.act.deal_archived': 'עסקה הועברה לארכיון',
  'shop.crm.act.order_placed': 'הזמנה בוצעה',
  'shop.crm.act.order_status_changed': 'סטטוס הזמנה שונה',
  'shop.crm.act.booking_requested': 'בקשת תור',
  'shop.crm.act.booking_status_changed': 'סטטוס תור שונה',
  'shop.crm.act.note_added': 'הערה נוספה',
  // אנליטיקת חנות
  'shop.an.title': 'נתוני חנות',
  'shop.an.revenue': 'הכנסות',
  'shop.an.revenueHint': 'לא כולל מבוטלות',
  'shop.an.orders': 'הזמנות',
  'shop.an.paid': 'שולמו',
  'shop.an.lowStock': 'מלאי נמוך',
  'shop.an.topProducts': 'מוצרים מובילים לפי הכנסה',
  'shop.an.source': 'מתוך {orders} הזמנות אמיתיות · {items} פריטים במעקב',
  'shop.an.emptyT': 'אין עדיין נתוני מכירות',
  'shop.an.emptyS': 'ברגע שלקוחות יבצעו הזמנות, ההכנסות, המוצרים המובילים והתראות המלאי יופיעו כאן.',
  // ── SEP-2 / F — סביבת ניהול ההזמנות של הבעלים (שירותים / ניתוח הזמנות / צוות ומשאבים)
  'svc.title': 'שירותים',
  'svc.add': 'הוספת שירות',
  'svc.addTitle': 'הוספת שירות',
  'svc.editTitle': 'עריכת שירות',
  'svc.untitled': 'שירות ללא שם',
  'svc.thisService': 'שירות זה',
  'svc.free': 'חינם',
  'svc.hidden': 'מוסתר',
  'svc.bookable': 'ניתן להזמנה',
  'svc.emptyT': 'עדיין אין שירותים',
  'svc.emptyS': 'הוסיפו את השירותים שלקוחות יכולים להזמין — שם, משך והמחיר. הם יופיעו כאן ובאפליקציה החיה שלכם.',
  'svc.unavailableT': 'השירותים עדיין לא מוגדרים',
  'svc.unavailableS': 'הניהול נפתח לאחר שהאפליקציה מחוברת לשרת הנתונים.',
  'svc.errAuth': 'התחברו כבעלי האפליקציה כדי לנהל שירותים.',
  'svc.fName': 'שם השירות',
  'svc.fNamePh': 'לדוגמה: תספורת, ייעוץ 30 דק׳',
  'svc.fPrice': 'מחיר',
  'svc.fPriceHelp': 'השאירו ריק אם השירות ניתן בחינם.',
  'svc.fDuration': 'משך (דקות)',
  'svc.fDurationHelp': 'כמה זמן נמשך כל תור.',
  'svc.fBuffer': 'מרווח אחרי (דקות)',
  'svc.fBufferHelp': 'מרווח נוסף אחרי כל תור לפני שהבא יכול להתחיל.',
  'svc.fBooking': 'כשלקוח מזמין',
  'svc.fBookingPh': 'בחרו…',
  'svc.fBookingHelp': 'אישור אוטומטי מקבל בקשות מיד. אישור ידני מאפשר לכם לאשר כל בקשה קודם.',
  'svc.bookingAuto': 'אישור אוטומטי',
  'svc.bookingManual': 'אישור כל בקשה',
  'svc.fDesc': 'תיאור',
  'svc.fDescPh': 'מה כלול, למה לצפות…',
  'svc.fMedia': 'תמונה',
  'svc.minShort': '{n} דק׳',
  'svc.hrShort': '{n} ש׳',
  'svc.hrMinShort': '{h} ש׳ {m} דק׳',
  'svc.bufferRow': '+{n} דק׳ מרווח',
  'svc.showToCustomersAria': 'הפיכת {name} לזמין להזמנה על ידי לקוחות',
  'svc.hideFromCustomersAria': 'הסתרת {name} מלקוחות',
  'svc.editAria': 'עריכת {name}',
  'bkan.title': 'תובנות הזמנות',
  'bkan.source': 'מבוסס על {n} הזמנות (מאז ומתמיד)',
  'bkan.emptyT': 'עדיין אין הזמנות',
  'bkan.emptyS': 'ברגע שלקוחות יתחילו להזמין, המספרים שלכם יופיעו כאן — כמות, ביטולים ועוד, מתוך הזמנות אמיתיות בלבד.',
  'bkan.mTotal': 'סך ההזמנות',
  'bkan.mConfirmed': 'מאושרות',
  'bkan.mConfirmedHint': 'קרובות',
  'bkan.mCompleted': 'הושלמו',
  'bkan.mCancelRate': 'שיעור ביטולים',
  'bkan.mNoShowRate': 'שיעור אי-הגעה',
  'bkan.mPaid': 'סומנו כשולמו',
  'bkan.mPaidHint': 'אתם סימנתם אותן כשולמו',
  'bkan.mRevenue': 'הכנסה',
  'bkan.mRevenueHint': 'מהזמנות שסימנתם כשולמו',
  'bkan.byService': 'הזמנות לפי שירות',
  'bkan.unnamedService': 'תור',
  'res.title': 'צוות ומשאבים',
  'res.add': 'הוספה',
  'res.addTitle': 'הוספת איש צוות או משאב',
  'res.editTitle': 'עריכת איש צוות או משאב',
  'res.untitled': 'ללא שם',
  'res.thisResource': 'משאב זה',
  'res.hidden': 'לא ניתן להזמנה',
  'res.bookable': 'ניתן להזמנה',
  'res.emptyT': 'עדיין אין צוות או משאבים',
  'res.emptyS': 'הוסיפו את האנשים או הדברים שההזמנה נעשית מולם — מעצב שיער, חדר, כיסא. אופציונלי, אך נוח כשיש יותר מאחד.',
  'res.unavailableT': 'עדיין לא מוגדר',
  'res.unavailableS': 'הניהול נפתח לאחר שהאפליקציה מחוברת לשרת הנתונים.',
  'res.errAuth': 'התחברו כבעלי האפליקציה כדי לנהל צוות ומשאבים.',
  'res.fName': 'שם',
  'res.fNamePh': 'לדוגמה: אלכס, חדר 2',
  'res.fRole': 'תפקיד',
  'res.fRolePh': 'לדוגמה: מעצב שיער בכיר',
  'res.fRoleHelp': 'מה האדם או המשאב הזה, במילים שלכם.',
  'res.fServices': 'שירותים מוצעים',
  'res.fServicesPh': 'תספורת, צבע, ייעוץ',
  'res.fServicesHelp': 'הקלידו שמות שירותים מופרדים בפסיקים.',
  'res.fNote': 'הערות זמינות',
  'res.fNotePh': 'לדוגמה: שלישי–שבת, בקרים בלבד',
  'res.fNoteHelp': 'טקסט חופשי — תזכורת עבורכם, מוצגת כאן.',
  'res.enableAria': 'הפיכת {name} לזמין להזמנה',
  'res.disableAria': 'הפיכת {name} ללא זמין להזמנה',
  'res.editAria': 'עריכת {name}',
  'mgmt.st.requested': 'התקבלה בקשה',
  'mgmt.st.confirmed': 'מאושר',
  'mgmt.st.completed': 'הושלם',
  'mgmt.st.cancelled': 'בוטל',
  'mgmt.pay.notRequired': 'ללא תשלום',
  'mgmt.pay.awaiting': 'ממתין לתשלום',
  'mgmt.pay.awaitingLink': 'ממתין לתשלום (קישור)',
  'mgmt.pay.payAtBusiness': 'תשלום בעסק',
  'mgmt.pay.paid': 'שולם (ידני)',
  'mgmt.pay.canceled': 'התשלום בוטל',
  'mgmt.pay.refunded': 'הוחזר (ידני)',
  'mgmt.pm.externalLink': 'אונליין (קישור)',
  'mgmt.pm.payAtBusiness': 'תשלום בעסק',
  'mgmt.act.confirm': 'אישור',
  'mgmt.act.decline': 'דחייה',
  'mgmt.act.markDone': 'סימון כהושלם',
  'mgmt.act.cancel': 'ביטול',
  'mgmt.act.markPaid': 'סימון כשולם',
  'mgmt.act.cancelPayment': 'ביטול תשלום',
  'mgmt.act.refunded': 'הוחזר',
  'mgmt.act.markUnpaid': 'סימון כלא שולם',
  'mgmt.act.reopen': 'פתיחה מחדש',
  // ניהול הזמנות
  'mgmt.bk.title': 'ניהול הזמנות',
  'mgmt.bk.appointment': 'תור',
  'mgmt.bk.paymentRow': 'תשלום:',
  'mgmt.bk.err409': 'עדכוני סטטוס של בעלים עוד לא מופעלים בשרת (בהמתנה למדיניות עדכון הבעלים).',
  'mgmt.bk.err404': 'ההזמנה הזו כבר לא קיימת. רעננו כדי לראות את המצב העדכני.',
  'mgmt.bk.errUpdate': 'לא הצלחנו לעדכן את ההזמנה. נסו שוב.',
  'mgmt.bk.unavailableT': 'שרת ההזמנות לא מוגדר',
  'mgmt.bk.unavailableS': 'לסביבה הזו אין חיבור Supabase. הזמנות יופיעו כאן ברגע ששרת הנתונים יהיה פעיל.',
  'mgmt.bk.errT': 'לא הצלחנו לטעון את ההזמנות',
  'mgmt.bk.errAuth': 'התחברו כבעלי האפליקציה כדי לנהל הזמנות.',
  'mgmt.bk.errGeneric': 'משהו השתבש בשליפת ההזמנות של האפליקציה.',
  'mgmt.bk.emptyT': 'עדיין אין הזמנות',
  'mgmt.bk.emptyS': 'כשלקוח מזמין באפליקציה החיה שלכם, זה מופיע כאן בזמן אמת — אשרו, השלימו או בטלו מהפאנל הזה.',
  // לקוחות
  'mgmt.cu.title': 'לקוחות',
  'mgmt.cu.fallback': 'לקוח',
  'mgmt.cu.active': 'פעיל',
  'mgmt.cu.lastSeen': 'נראו לאחרונה {t}',
  'mgmt.cu.unavailableT': 'מאגר הלקוחות לא מוגדר',
  'mgmt.cu.unavailableS': 'לסביבה הזו אין חיבור Supabase. לקוחות יופיעו כאן ברגע ששרת הנתונים יהיה פעיל.',
  'mgmt.cu.errT': 'לא הצלחנו לטעון את הלקוחות',
  'mgmt.cu.errAuth': 'התחברו כבעלי האפליקציה כדי לראות את הלקוחות שלכם.',
  'mgmt.cu.errGeneric': 'משהו השתבש בשליפת הלקוחות של האפליקציה.',
  'mgmt.cu.emptyT': 'עדיין אין לקוחות',
  'mgmt.cu.emptyS': 'כשמישהו מתחבר לאפליקציה החיה שלכם, הוא מופיע כאן — האימייל המאומת שלו ומתי היה פעיל לאחרונה. אנשים אמיתיים, אף פעם לא הערכות.',
  // עורך הזמינות
  'mgmt.av.aria': 'זמינות הזמנות',
  'mgmt.av.title': 'זמינות',
  'mgmt.av.errLoad': 'לא הצלחנו לטעון את הזמינות.',
  'mgmt.av.unavailableT': 'מאגר הזמינות לא מוגדר',
  'mgmt.av.unavailableS': 'לסביבה הזו אין חיבור לשרת האפליקציות.',
  'mgmt.av.err403': 'רק בעלי האפליקציה יכולים לשנות זמינות.',
  'mgmt.av.err401': 'התחברו כבעלי האפליקציה כדי לערוך זמינות.',
  'mgmt.av.errSave': 'לא הצלחנו לשמור את הזמינות.',
  'mgmt.av.window': 'לקוחות יכולים להזמין עד',
  'mgmt.av.year': 'שנה',
  'mgmt.av.days': '{n} ימים',
  'mgmt.av.weekly': 'שעות שבועיות',
  'mgmt.av.closed': 'סגור',
  'mgmt.av.timezone': 'אזור זמן של העסק',
  'mgmt.av.timezoneHint': 'ההזמנות, השעות ו"היום" משתמשים באזור הזמן הזה. הגדירו אותו לפי מיקום העסק.',
  'mgmt.av.duration': 'משך שירות (דקות)',
  'mgmt.av.blockedDates': 'תאריכים חסומים (חגים, חופשות)',
  'mgmt.av.blockDate': 'חסימת תאריך',
  'mgmt.av.unblock': 'ביטול חסימה של {d}',
  'mgmt.av.blockedTimes': 'שעות חסומות (למשל הפסקת צהריים)',
  'mgmt.av.block': 'חסימה',
  'mgmt.av.removeBlocked': 'הסרת שעה חסומה',
  'mgmt.av.payment': 'תשלום',
  'mgmt.av.payNote': 'התשלום מתבצע מחוץ ל‑Pomela. בעלי העסק מאשרים תשלום ידנית.',
  'mgmt.av.extEnable': 'קבלת תשלום אונליין (קישור חיצוני)',
  'mgmt.av.extNote': 'תשלום אונליין דרך קישור התשלום של העסק.',
  'mgmt.av.extUrl': 'קישור תשלום (https://…)',
  'mgmt.av.extLabel': 'תווית (למשל „מקדמה 80 ₪”)',
  'mgmt.av.extLabelPh': 'מקדמה 80 ₪',
  'mgmt.av.instructions': 'הנחיות (לא חובה)',
  'mgmt.av.extInstrPh': 'מה שהלקוח צריך לדעת על תשלום אונליין.',
  'mgmt.av.pabEnable': 'אפשרו תשלום בעסק (פרונטלי)',
  'mgmt.av.pabNote': 'תשלום בעסק.',
  'mgmt.av.pabInstrPh': 'משלמים בעסק אחרי התור.',
  'mgmt.av.footNote': 'לקוחות רואים רק שעות שהזמינות הזו מאפשרת, פחות תורים שכבר נקבעו.',
  'mgmt.av.save': 'שמירת זמינות',
  'mgmt.day.sun': 'א׳',
  'mgmt.day.mon': 'ב׳',
  'mgmt.day.tue': 'ג׳',
  'mgmt.day.wed': 'ד׳',
  'mgmt.day.thu': 'ה׳',
  'mgmt.day.fri': 'ו׳',
  'mgmt.day.sat': 'שבת',
  // רשימת המתנה
  'mgmt.wl.title': 'רשימת המתנה',
  'mgmt.wl.fallback': 'רישום להמתנה',
  'mgmt.wl.stNew': 'חדש',
  'mgmt.wl.stContacted': 'יצרנו קשר',
  'mgmt.wl.stCandidate': 'מועמד',
  'mgmt.wl.stDismissed': 'נדחה',
  'mgmt.wl.markContacted': 'סימון שיצרנו קשר',
  'mgmt.wl.promote': 'קידום',
  'mgmt.wl.dismiss': 'דחייה',
  'mgmt.wl.backToContacted': 'חזרה ל„יצרנו קשר”',
  'mgmt.wl.unavailableT': 'רשימת ההמתנה לא זמינה',
  'mgmt.wl.unavailableS': 'לסביבה הזו אין עדיין חיבור למאגר נתונים.',
  'mgmt.wl.errT': 'לא הצלחנו לטעון את רשימת ההמתנה',
  'mgmt.wl.errAuth': 'התחברו כבעלי האפליקציה כדי לצפות ברשימת ההמתנה.',
  'mgmt.wl.errGeneric': 'משהו השתבש בשליפת רשימת ההמתנה של האפליקציה.',
  'mgmt.wl.emptyT': 'עדיין אין אף אחד ברשימת ההמתנה',
  'mgmt.wl.emptyS': 'כשתור, אירוע או חלון איסוף מתמלא, לקוחות יכולים להצטרף לרשימת ההמתנה מהאפליקציה. הבקשות שלהם מופיעות כאן כדי שתוכלו ליצור קשר ולנהל את התור.',
  // יומן פעילות
  'mgmt.al.title': 'יומן פעילות',
  'mgmt.al.fallback': 'פעילות',
  'mgmt.al.system': 'מערכת',
  'mgmt.ev.memberJoined': 'חבר הצטרף',
  'mgmt.ev.inviteCreated': 'הזמנה נוצרה',
  'mgmt.ev.inviteRedeemed': 'הזמנה מומשה',
  'mgmt.ev.multiOn': 'פיצ׳רים קבוצתיים הופעלו',
  'mgmt.ev.multiOff': 'פיצ׳רים קבוצתיים כובו',
  'mgmt.ev.msgEdited': 'הודעה נערכה',
  'mgmt.ev.msgDeleted': 'הודעה נמחקה',
  'mgmt.ev.bkRequested': 'התקבלה בקשת הזמנה',
  'mgmt.ev.bkConfirmed': 'הזמנה אושרה',
  'mgmt.ev.bkCancelled': 'הזמנה בוטלה',
  'mgmt.ev.bkRescheduled': 'הזמנה הועברה למועד אחר',
  'mgmt.al.unavailableT': 'יומן הפעילות לא זמין',
  'mgmt.al.unavailableS': 'לסביבה הזו אין עדיין חיבור למאגר אירועים. פעילות תופיע כאן ברגע שהוא פעיל.',
  'mgmt.al.errT': 'לא הצלחנו לטעון את הפעילות',
  'mgmt.al.errAuth': 'התחברו כבעלי האפליקציה כדי לצפות בפעילות.',
  'mgmt.al.errGeneric': 'משהו השתבש בשליפת הפעילות של האפליקציה.',
  'mgmt.al.emptyT': 'עדיין אין פעילות',
  'mgmt.al.emptyS': 'הצטרפות חברים, הזמנות, הפעלת פיצ׳רים קבוצתיים ועריכות או מחיקות של הודעות נרשמות כאן, כדי שתראו בדיוק מי עשה מה ומתי.',
  // ספריית נכסים
  'mgmt.as.title': 'ספריית נכסים',
  'mgmt.as.upload': 'העלאה',
  'mgmt.as.uploading': 'מעלה…',
  'mgmt.as.err415': 'סוג הקובץ הזה לא נתמך.',
  'mgmt.as.err413': 'הקובץ גדול מדי.',
  'mgmt.as.err429': 'הגעתם למכסת ההעלאות היומית — נסו שוב מחר.',
  'mgmt.as.err503': 'העלאות עוד לא מופעלות בסביבה הזו.',
  'mgmt.as.errUpload': 'ההעלאה נכשלה. נסו שוב.',
  'mgmt.as.errNoRef': 'ההעלאה הצליחה אבל לא חזרה הפניה לקובץ.',
  'mgmt.as.errCatalog': 'הקובץ הועלה אבל לא הצלחנו להוסיף אותו לספרייה.',
  'mgmt.as.err404': 'הנכס הזה כבר לא קיים. רעננו כדי לראות את המצב העדכני.',
  'mgmt.as.errRemove': 'לא הצלחנו להסיר את הנכס. נסו שוב.',
  'mgmt.as.errConnUp': 'החיבור אבד במהלך ההעלאה. בדקו את הרשת ונסו שוב.',
  'mgmt.as.unavailableT': 'אחסון הנכסים לא מוגדר',
  'mgmt.as.unavailableS': 'לסביבה הזו אין חיבור ל‑Supabase Storage. נכסים יופיעו כאן ברגע שההעלאות פעילות.',
  'mgmt.as.errT': 'לא הצלחנו לטעון את הנכסים',
  'mgmt.as.errAuth': 'התחברו כבעלי האפליקציה כדי לנהל נכסים.',
  'mgmt.as.errGeneric': 'משהו השתבש בשליפת הנכסים של האפליקציה.',
  'mgmt.as.emptyT': 'עדיין אין נכסים',
  'mgmt.as.emptyS': 'העלו תמונה או מסמך והם ייכנסו לקטלוג של האפליקציה — העתיקו את ההפניה כדי להשתמש, או הסירו בכל רגע.',
  'mgmt.as.copyRef': 'העתקת הפניה',
  'mgmt.as.copied': 'הועתק',
  'mgmt.as.remove': 'הסרה',
  // קישור תשלום
  'mgmt.pl.title': 'קישור תשלום',
  'mgmt.pl.errLoad': 'לא הצלחנו לטעון את הגדרות התשלום.',
  'mgmt.pl.unavailableT': 'מאגר התשלומים לא מוגדר',
  'mgmt.pl.unavailableS': 'לסביבה הזו אין חיבור Supabase.',
  'mgmt.pl.err403': 'רק בעלי האפליקציה יכולים לשנות הגדרות תשלום.',
  'mgmt.pl.err400': 'בדקו את קישור התשלום.',
  'mgmt.pl.errSave': 'לא הצלחנו לשמור את הגדרות התשלום.',
  'mgmt.pl.scope': 'לקוחות משלמים דרך הקישור החיצוני שלכם. Pomela לא מעבדת ולא מאמתת תשלומים — אתם מאשרים כל אחד ידנית בניהול ההזמנות.',
  'mgmt.pl.require': 'דרשו תשלום עבור הזמנות',
  'mgmt.pl.linkLabel': 'קישור תשלום (https)',
  'mgmt.pl.httpsErr': 'חייב להתחיל ב‑https://',
  'mgmt.pl.amountLabel': 'תווית סכום (מוצגת ללקוחות)',
  'mgmt.pl.amountPh': 'למשל מקדמה 150 ₪',
  'mgmt.pl.instrPh': 'למשל: שלמו את המקדמה כדי לשמור את התור; היתרה במקום.',
  'mgmt.pl.taxShipT': 'מס ומשלוח',
  'mgmt.pl.taxShipOpt': '(לא חובה · מסחר)',
  'mgmt.pl.taxShipS': 'ערכים ידניים שאתם קובעים — מוצגים כשורות בסכום העגלה. אין מנוע מס אוטומטי; שום דבר לא מחויב.',
  'mgmt.pl.taxRate': 'שיעור מס (%)',
  'mgmt.pl.taxRange': 'חייב להיות 0–100',
  'mgmt.pl.taxLabel': 'תווית מס',
  'mgmt.pl.taxPh': 'מע״מ',
  'mgmt.pl.shipFee': 'דמי משלוח',
  'mgmt.pl.shipMin': '0 ומעלה',
  'mgmt.pl.shipLabel': 'תווית משלוח',
  'mgmt.pl.shipPh': 'משלוח',
  'mgmt.pl.save': 'שמירת הגדרות תשלום',
  // §S680 — משטח השגיאות המאוחד (ErrorState)
  'state.goHome': 'לדף הבית',
  'state.guide': 'מדריך',
  'state.notFound.title': 'הדף הזה הלך לאיבוד',
  'state.notFound.body': 'הדף שחיפשתם לא קיים או שהועבר. בואו נחזיר אתכם.',
  'state.appNotFound.code': 'האפליקציה לא נמצאה',
  'state.appNotFound.title': 'לא מצאנו את האפליקציה הזו',
  'state.appNotFound.body': 'ייתכן שהיא הוסרה, או שהיא לא בסביבת העבודה שלכם במכשיר הזה.',
  'state.genFailed.code': 'הבנייה נכשלה',
  'state.genFailed.title': 'הבנייה הזו לא עברה',
  'state.genFailed.body': 'Pomela לא הצליחה לסיים לבנות את האפליקציה. העבודה שלכם שמורה — נסו שוב בעוד רגע.',
  'state.denied.code': 'גישה',
  'state.denied.title': 'אין לכם גישה לכאן',
  'state.denied.body': 'האזור הזה מוגבל. אם נראה לכם שזו טעות, פנו לתמיכה.',
  'state.offline.code': 'לא מקוון',
  'state.offline.title': 'אתם במצב לא מקוון',
  'state.offline.body': 'בדקו את החיבור. העבודה שלכם שמורה ותסתנכרן כשתחזרו.',
  'state.noResults.code': 'אין תוצאות',
  'state.noResults.title': 'שום דבר לא תאם',
  'state.noResults.body': 'נסו חיפוש אחר או הרחיבו את הסינון.',
  'state.crash.code': 'משהו נשבר',
  'state.crash.title': 'Pomela נתקלה בתקלה',
  'state.crash.body': 'אירעה שגיאה לא צפויה. טעינה מחדש בדרך כלל פותרת — האפליקציות שלכם בטוחות.',
  'state.generic.code': 'שגיאה',
  'state.generic.title': 'משהו השתבש',
  'state.generic.body': 'אירעה שגיאה לא צפויה. בואו נחזיר אתכם למסלול.',
  // §S680 — ShareCard
  'share.openLive': 'פתיחת האפליקציה החיה',
  'share.builtPrefix': 'בניתי את',
  'share.builtSuffix': 'במשפט אחד:',
  'share.qrHelp': 'כוונו את מצלמת הטלפון. הוסיפו למסך הבית לאפליקציה במסך מלא.',
  'share.ariaX': 'שיתוף ב‑X',
  'share.ariaWhatsApp': 'שיתוף ב‑WhatsApp',
  'share.progressAria': '{filled} מתוך {total} שיתופים ראשונים נשלחו',
  'share.ogAlt': 'תצוגה מקדימה חברתית של {name}',
  // §S680 — Sidebar
  'sb.menuAria': 'תפריט',
  'sb.closeMenu': 'סגירת התפריט',
  'sb.brandTag': 'סטודיו לאפליקציות AI',
  'sb.noRecentText': 'האפליקציות שתייצרו נאספות כאן, הקישו על כל אחת כדי לפתוח אותה מחדש.',
  'sb.signedIn': 'מחוברים',
  'sb.notSignedIn': 'לא מחוברים',
  // §S680 — הגדרות
  'set.sectionsAria': 'קטעי ההגדרות',
  'set.billing': 'חיוב וקרדיטים',
  'set.language': 'שפה ו‑RTL',
  'set.privacyNav': 'פרטיות',
  'set.danger': 'אזור מסוכן',
  'set.planPro': 'מנוי Pro',
  'set.planFree': 'מנוי חינמי',
  'set.planManage': 'ניהול המנוי והקרדיטים שלכם',
  'set.planUpgrade': 'שדרגו ל‑Pro ליותר קרדיטים חודשיים',
  'set.rtlRow': 'פריסה מימין לשמאל',
  'set.rtlOn': 'פעיל — ממשק בעברית',
  'set.rtlOff': 'נדלק עם שפה הנכתבת מימין לשמאל',
  'set.deleteConfirm': 'למחוק את כל נתוני Pomela שלכם במכשיר הזה? אי אפשר לבטל את זה.',
  // §S680 — חשבון (מחיקה כנה ברמת המכשיר + כרטיסי מידע)
  'acct.dangerZone': 'אזור מסוכן',
  'acct.delOpen': 'מחיקת הנתונים שלי במכשיר הזה',
  'acct.delAria': 'אישור מחיקת נתונים',
  'acct.wsT': 'סביבת העבודה שלכם',
  'acct.wsB': 'פתחו ונהלו כל אפליקציה שאתם בונים, הכול במקום אחד.',
  'acct.wsCta': 'לסביבת העבודה',
  'acct.helpT': 'צריכים עזרה?',
  'acct.helpB': 'עיינו במאגר הידע או פנו לתמיכה.',
  'acct.helpCta': 'פתיחת המדריך',
  // §S680 — App.tsx ErrorPanel + CommandPalette
  'err.genFailed': 'הבנייה נכשלה',
  'common.tryAgain': 'נסו שוב',
  'common.startOver': 'להתחיל מחדש',
  'cmdk.noMatches': 'אין התאמות.',
  // §refusal-honesty — סירובים מכוונים של /v1/generate (sse.ts)
  'err.maintenance': 'פומלה בתצוגה מקדימה פרטית — נשלח לכם מייל ברגע שנפתח. 🍊',
  'err.credits': 'הקרדיטים שלכם נגמרו — טענו עוד או שדרגו ל‑Pro כדי להמשיך לבנות.',
};

const CATALOG: Record<Locale, Record<string, string>> = { en: EN, he: HE };

let current: Locale = 'en';

/** Resolve the locale + whether the user EXPLICITLY chose it (?lang= or a saved
 *  preference). §launch-defaults — Pomela DEFAULTS TO ENGLISH. An unset visitor
 *  gets English regardless of their OS language; Hebrew is reached only by an
 *  explicit ?lang=he or a saved choice (which then also drives dir=rtl). */
export function detectLocaleSource(): { locale: Locale; explicit: boolean } {
  try {
    const q = new URL(location.href).searchParams.get('lang');
    if (q && (SUPPORTED as readonly string[]).includes(q)) return { locale: q as Locale, explicit: true };
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (SUPPORTED as readonly string[]).includes(saved)) return { locale: saved as Locale, explicit: true };
  } catch {
    /* SSR / no storage */
  }
  return { locale: 'en', explicit: false };
}

/** Resolve the locale from (?lang= → saved → en default). Pure-ish. */
export function detectLocale(): Locale {
  return detectLocaleSource().locale;
}

/** Set <html lang> always; set <html dir="rtl"> for ANY RTL locale — including
 *  one INFERRED from the OS (navigator.language), which is the founder's
 *  "Hebrew driven by system settings" requirement. This is now safe because the
 *  first-run + core creator surfaces (onboarding, nav, home, workspace, the
 *  secondary-screen chrome, settings, explore, auth, paywall) are translated,
 *  and the per-letter brand hero is pinned `dir="ltr"` with bidi isolation so it
 *  never reverses. Any remaining untranslated deep content renders English with
 *  normal bidi inside the RTL shell — readable, not mirrored-broken. The
 *  `explicit` arg is retained for callers but no longer gates `dir`. */
export function applyHtmlLangDir(loc: Locale, _explicit = true): void {
  try {
    const el = document.documentElement;
    el.lang = loc === 'he' ? 'he-IL' : loc;
    el.dir = RTL.has(loc) ? 'rtl' : 'ltr';
  } catch {
    /* no document */
  }
}

/** Detect + apply once at boot. Returns the active locale. */
export function initLocale(): Locale {
  const src = detectLocaleSource();
  current = src.locale;
  applyHtmlLangDir(src.locale, src.explicit);
  return current;
}

export function getLocale(): Locale {
  return current;
}
export function isRtl(): boolean {
  return RTL.has(current);
}

/** Resolve a key for the active locale; {var} interpolation; en + key fallback. */
export function t(key: string, vars?: Record<string, string | number>): string {
  let s = CATALOG[current]?.[key] ?? CATALOG.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
  }
  return s;
}

/** Switch locale at runtime (persists + re-applies dir). A reload re-renders
 *  any already-painted strings; new renders pick it up immediately. */
export function setLocale(loc: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, loc);
  } catch {
    /* ignore */
  }
  current = loc;
  applyHtmlLangDir(loc);
}

// Test-only hooks (not used by the app).
export const __test = { CATALOG, SUPPORTED };
